import {t, useLanguage} from './i18n';
import OnlinePaymentsSection from "./sections/OnlinePaymentsSection";
import {useEditorNavigation} from "./hooks/useEditorNavigation";
import "./BusinessEditorPolish.css";
import BusinessReviewChecklist from './components/BusinessReviewChecklist';
// LOOP AGENDA MADRE WORKER ACCESS MIRROR V86 — worker access mirrors include profile/availability.
// src/BusinessEditPage.js
// LOOP WORKER PERMISSIONS BUSINESS EDIT HOTFIX — BusinessEditPage preserves and mirrors saved worker permissions.
// Fixed build errors: added missing getOwnerUidForUpload and handleToggleSingleService handlers.
// Also kept the earlier adjustments: price in addService, services/images toggles handled elsewhere.
//
// ✅ Update (Android/iOS native):
// - Replace left sidebar with a horizontal sticky section tabs bar (General/Servicios/Trabajadores/Proyectos)
// - Keep sidebar for web/desktop
// - Give more vertical space to content and ensure proper scrolling
// LOOP D2.9.4D.1 — settings layout left action panel cleanup
// LOOP D2.9.4E.2 — CRM uses full available width in settings
// LOOP D2.9.4I.2 — Business settings sidebar premium revamp
// LOOP D2.9.4I.4 — sidebar subtitles one-line compact polish
// LOOP D2.9.4J.2 — settings sidebar exact compact match
// LOOP D2.9.4J.1 — grouped settings sidebar + General section visual direction
// LOOP D2.9.4J.3 — true exact sidebar scale override
// LOOP D2.9.4J.4 — sidebar harmony scale override
// LOOP BUSINESS EDIT V59 — native tablet picker TDZ fix.

import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  arrayUnion,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db as defaultDb, storage as defaultStorage, auth as defaultAuth } from "./firebase";
import planLoopEarlyAccessCard from "./assets/plan-cards/plan-loop-early-access.png";
import planLoopPremiumCard from "./assets/plan-cards/plan-loop-premium.png";
import planLoopBlackCard from "./assets/plan-cards/plan-loop-black.png";
import planLoopBasicoCard from "./assets/plan-cards/plan-loop-basico.png";

import ImageCropper from "./components/ImageCropper";
import GeneralSection from "./sections/GeneralSection";
import ServicesSection from "./sections/ServicesSection";
import WorkersSection from "./sections/WorkersSection";
import {manageWorkerAccess} from './services/workerAccess';
import GallerySection from "./sections/GallerySection";
import AvailabilitySection from "./sections/AvailabilitySection";
import NotificationSection from "./sections/NotificationSection";
import LoyaltySection from "./sections/LoyaltySection";
import MembershipSection from "./sections/MembershipSection";
import BillingSection from "./sections/BillingSection";
import BusinessOsSection from "./sections/BusinessOsSection";
import CustomersPage from "./pages/CustomersPage";
import fsClient from "./data/firestoreClient";
import { isIosCapacitor } from "./utils/nativeHttp";
import { loopBusinessAddDoc, loopBusinessBulkUpdateCollection, loopBusinessDeleteDoc, loopBusinessEffectiveUser, loopBusinessGetDoc, loopBusinessIsPlatformOwner, loopBusinessListCollection, loopBusinessUserId, loopBusinessNow, loopBusinessSetDoc, loopBusinessUpdateDoc } from "./utils/businessNativeSafeWrites";
import "./styles/loopLayers.css";

/* ---------------------------
   uploadToStorageWithMetadata
--------------------------- */
const uploadToStorageWithMetadata = (storageClient, path, file, ownerUid, onProgress = () => {}) =>
  new Promise((resolve, reject) => {
    const client = storageClient || defaultStorage;
    if (!client) return reject(new Error("storage_not_initialized"));
    try {
      const ref = storageRef(client, path);
      const metadata = {
        contentType: file.type || "application/octet-stream",
        customMetadata: { ownerUid: ownerUid || "" },
      };
      const task = uploadBytesResumable(ref, file, metadata);
      task.on(
        "state_changed",
        (snap) => {
          try {
            const pct = snap.totalBytes ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0;
            onProgress(pct);
          } catch (e) {}
        },
        (err) => {
          reject(err);
        },
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve(url);
          } catch (err) {
            reject(err);
          }
        }
      );
    } catch (err) {
      reject(err);
    }
  });

function normalizeWorkerAccessEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function buildWorkerAccessFields(emailValue, uidValue = null) {
  const email = normalizeWorkerAccessEmail(emailValue);
  const uid = uidValue ? String(uidValue) : null;
  const payload = {
    email,
    emailLower: email,
    emailNormalized: email,
    workerEmail: email,
    workerEmailLower: email,
    workerEmailNormalized: email,
    employeeEmail: email,
    employeeEmailLower: email,
    employeeEmailNormalized: email,
    inviteEmail: email,
    inviteEmailLower: email,
    inviteEmailNormalized: email,
    contactEmail: email,
    contactEmailLower: email,
    contactEmailNormalized: email,
    accessEmails: email ? [email] : [],
    allowedEmails: email ? [email] : [],
    accessStatus: uid ? "linked" : (email ? "invited" : "not_configured"),
  };
  if (uid) {
    payload.userUid = uid;
    payload.authUid = uid;
    payload.linkedUserId = uid;
    payload.linkedUserUid = uid;
  } else {
    payload.userUid = null;
  }
  return payload;
}

// LOOP WORKER PERMISSIONS BUSINESS EDIT HOTFIX — keep permission writes native-safe and mirrored.
function businessEditPersistNow() {
  return isIosCapacitor() ? new Date().toISOString() : serverTimestamp();
}

async function businessEditSetDocByPath(dbRef, path, payload, options = { merge: true }) {
  return loopBusinessSetDoc(dbRef, path, payload, options);
}

async function businessEditAddDocToCollectionPath(dbRef, collectionPath, payload) {
  return loopBusinessAddDoc(dbRef, collectionPath, payload);
}

function businessEditExtractWorkerPermissions(source = {}) {
  const raw = source?.permissions && typeof source.permissions === "object"
    ? source.permissions
    : source?.workerPermissions && typeof source.workerPermissions === "object"
      ? source.workerPermissions
      : null;
  return raw && typeof raw === "object" ? { ...raw } : null;
}

function businessEditAccessRole(source = {}) {
  return String(source?.accessRole || source?.loopRole || source?.securityRole || "worker").trim().toLowerCase() || "worker";
}

function businessEditWorkerPhotoUrl(source = {}) {
  const candidates = [
    source.photoUrl, source.photoURL, source.photo, source.imageUrl, source.image,
    source.avatarUrl, source.workerPhotoUrl, source.employeePhotoUrl,
    source.profileImageUrl, source.profilePhotoUrl, source.pictureUrl,
  ];
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (value && typeof value === "object") {
      const nested = value.url || value.downloadURL || value.src || value.href || "";
      if (typeof nested === "string" && nested.trim()) return nested.trim();
    }
  }
  return "";
}

function businessEditWorkerAvailability(source = {}) {
  return source.availability || source.employeeAvailability || source.workerAvailability || source.schedule || source.workingHours || source.hours || null;
}

async function writeWorkerAccessMirrorDocs(db, businessId, employeeId, employeePayload = {}) {
  if (!db || !businessId || !employeeId) return;
  const email = normalizeWorkerAccessEmail(employeePayload.email || employeePayload.employeeEmail || employeePayload.workerEmail);
  const uid = employeePayload.userUid || employeePayload.authUid || employeePayload.linkedUserId || null;
  const permissions = businessEditExtractWorkerPermissions(employeePayload);
  const accessRole = businessEditAccessRole(employeePayload);
  const now = businessEditPersistNow();
  const base = {
    businessId,
    employeeId,
    employeeEmail: email || "",
    email: email || "",
    userUid: uid || null,
    uid: uid || null,
    active: employeePayload.disabled === true ? false : true,
    role: employeePayload.role || "worker",
    accessRole,
    loopRole: accessRole,
    securityRole: accessRole,
    name: employeePayload.name || employeePayload.displayName || employeePayload.employeeName || employeePayload.workerName || "",
    displayName: employeePayload.displayName || employeePayload.name || employeePayload.employeeName || employeePayload.workerName || "",
    employeeName: employeePayload.employeeName || employeePayload.name || employeePayload.displayName || employeePayload.workerName || "",
    workerName: employeePayload.workerName || employeePayload.name || employeePayload.displayName || employeePayload.employeeName || "",
    photoUrl: businessEditWorkerPhotoUrl(employeePayload) || null,
    workerPhotoUrl: businessEditWorkerPhotoUrl(employeePayload) || null,
    employeePhotoUrl: businessEditWorkerPhotoUrl(employeePayload) || null,
    imageUrl: businessEditWorkerPhotoUrl(employeePayload) || null,
    avatarUrl: businessEditWorkerPhotoUrl(employeePayload) || null,
    availability: businessEditWorkerAvailability(employeePayload),
    employeeAvailability: businessEditWorkerAvailability(employeePayload),
    workerAvailability: businessEditWorkerAvailability(employeePayload),
    schedule: employeePayload.schedule || businessEditWorkerAvailability(employeePayload),
    workingHours: employeePayload.workingHours || businessEditWorkerAvailability(employeePayload),
    hours: employeePayload.hours || null,
    services: Array.isArray(employeePayload.services) ? employeePayload.services : [],
    serviceIds: Array.isArray(employeePayload.serviceIds) ? employeePayload.serviceIds : (Array.isArray(employeePayload.services) ? employeePayload.services : []),
    blockedDates: Array.isArray(employeePayload.blockedDates) ? employeePayload.blockedDates : [],
    blockedRanges: Array.isArray(employeePayload.blockedRanges) ? employeePayload.blockedRanges : [],
    visibleToCustomers: employeePayload.visibleToCustomers !== false,
    showOnBusinessPage: employeePayload.showOnBusinessPage !== false,
    canBeBookedOnline: employeePayload.canBeBookedOnline !== false,
    updatedAt: now,
  };

  if (permissions) {
    base.permissions = permissions;
    base.workerPermissions = permissions;
    base.permissionsManualOverride = employeePayload.permissionsManualOverride === true;
    base.permissionsUpdatedAt = now;
  }

  if (email) {
    await businessEditSetDocByPath(db, `businesses/${businessId}/workerAccessEmails/${email}`, base, { merge: true });
  }

  if (uid) {
    await businessEditSetDocByPath(db, `businesses/${businessId}/workerAccess/${String(uid)}`, base, { merge: true });
  }

  // Bulletproof fast-path for Header/userBusinesses:
  // Store worker access on the business document too, so the header can query
  // businesses by array-contains without relying on collectionGroup employees
  // permissions/indexes. This is especially useful on iOS/Apple private relay.
  const businessPatch = {
    workerAccessUpdatedAt: now,
  };

  if (isIosCapacitor()) {
    let latestBusiness = {};
    try {
      latestBusiness = await fsClient.getDocByPath(`businesses/${businessId}`, { db }) || {};
    } catch (_) {}

    const mergeUnique = (existing, value) => {
      const list = Array.isArray(existing) ? existing.map(String).filter(Boolean) : [];
      if (value) list.push(String(value));
      return Array.from(new Set(list));
    };

    if (email) {
      businessPatch.workerAccessEmails = mergeUnique(latestBusiness.workerAccessEmails, email);
      businessPatch.workerEmails = mergeUnique(latestBusiness.workerEmails, email);
    }
    if (uid) {
      businessPatch.workerAccessUids = mergeUnique(latestBusiness.workerAccessUids, uid);
      businessPatch.workerUids = mergeUnique(latestBusiness.workerUids, uid);
    }
  } else {
    if (email) {
      businessPatch.workerAccessEmails = arrayUnion(email);
      businessPatch.workerEmails = arrayUnion(email);
    }
    if (uid) {
      businessPatch.workerAccessUids = arrayUnion(String(uid));
      businessPatch.workerUids = arrayUnion(String(uid));
    }
  }

  if (email || uid) {
    await businessEditSetDocByPath(db, `businesses/${businessId}`, businessPatch, { merge: true });
  }
}


function isNativeRuntimeNow() {
  try {
    if (typeof window === "undefined") return false;
    const cap = window.Capacitor;
    if (!cap) return false;
    const platform = typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform || null;
    return !!(platform && platform !== "web");
  } catch {
    return false;
  }
}

function TabChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-4 py-2 rounded-full border font-extrabold whitespace-nowrap",
        active ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700",
      ].join(" ")}
      style={{ flex: "0 0 auto" }}
    >
      {children}
    </button>
  );
}


const LOOP_PLAN_CARD_META = {
  early_access: {
    key: "early-access",
    image: planLoopEarlyAccessCard,
    title: "Plan gratis",
    subtitle: "Herramientas para tu negocio",
    cta: "Ver beneficios",
    icon: "diamond",
  },
  basico: {
    key: "basico",
    image: planLoopBasicoCard,
    title: "Plan Loop Básico",
    subtitle: "Herramientas esenciales",
    cta: "Ver beneficios",
    icon: "heart",
  },
  premium: {
    key: "premium",
    image: planLoopPremiumCard,
    title: "Plan Loop Premium",
    subtitle: "Automatizaciones y beneficios",
    cta: "Ver beneficios",
    icon: "crown",
  },
  black: {
    key: "black",
    image: planLoopBlackCard,
    title: "Plan Loop Black",
    subtitle: "Prioridad, soporte y control avanzado",
    cta: "Ver beneficios",
    icon: "diamond",
  },
};

function normalizeLoopPlanCardKey(value) {
  const raw = String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

  if (!raw) return "";
  if (raw.includes("early") || raw.includes("access") || raw.includes("trial") || raw.includes("beta") || raw === "free") return "early_access";
  if (raw.includes("black") || raw.includes("elite") || raw.includes("enterprise")) return "black";
  if (raw.includes("premium")) return "premium";
  if (raw.includes("basic") || raw.includes("basico") || raw.includes("pro") || raw.includes("starter")) return "basico";
  return "";
}

function getLoopPlanCardMeta(businessDoc = {}) {
  const candidates = [
    businessDoc?.billing?.plan,
    businessDoc?.billing?.planKey,
    businessDoc?.billing?.tier,
    businessDoc?.billing?.status,
    businessDoc?.billingPlan,
    businessDoc?.billingPlanKey,
    businessDoc?.loopPlan,
    businessDoc?.loopPlanKey,
    businessDoc?.planLoop,
    businessDoc?.plan,
    businessDoc?.planName,
    businessDoc?.planKey,
    businessDoc?.planId,
    businessDoc?.subscriptionPlan,
    businessDoc?.subscriptionTier,
    businessDoc?.tier,
    businessDoc?.accessPlan,
    businessDoc?.businessOsPlan,
  ];

  for (const candidate of candidates) {
    const key = normalizeLoopPlanCardKey(candidate);
    if (key && LOOP_PLAN_CARD_META[key]) return LOOP_PLAN_CARD_META[key];
  }

  if (businessDoc?.billing?.earlyAccess === true || businessDoc?.earlyAccess === true) {
    return LOOP_PLAN_CARD_META.early_access;
  }

  return LOOP_PLAN_CARD_META.early_access;
}


function SettingsIcon({ name, size = 22 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2.05,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };
  const icons = {
    gear: <><circle cx="12" cy="12" r="3.1" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.04.04a2.05 2.05 0 0 1-2.9 2.9l-.04-.04A1.7 1.7 0 0 0 15 19.43a1.7 1.7 0 0 0-1 .3 1.7 1.7 0 0 0-.7 1.37V21a2.05 2.05 0 0 1-4.1 0v-.06a1.7 1.7 0 0 0-.7-1.37 1.7 1.7 0 0 0-1-.3 1.7 1.7 0 0 0-1.87.34l-.04.04a2.05 2.05 0 0 1-2.9-2.9l.04-.04A1.7 1.7 0 0 0 4.57 15a1.7 1.7 0 0 0-.3-1 1.7 1.7 0 0 0-1.37-.7H2.8a2.05 2.05 0 0 1 0-4.1h.06a1.7 1.7 0 0 0 1.37-.7 1.7 1.7 0 0 0 .3-1 1.7 1.7 0 0 0-.34-1.87l-.04-.04a2.05 2.05 0 0 1 2.9-2.9l.04.04A1.7 1.7 0 0 0 9 4.57c.33-.13.64-.24 1-.3A1.7 1.7 0 0 0 10.7 2.9V2.8a2.05 2.05 0 0 1 4.1 0v.06a1.7 1.7 0 0 0 .7 1.37c.36.06.67.17 1 .3a1.7 1.7 0 0 0 1.87-.34l.04-.04a2.05 2.05 0 0 1 2.9 2.9l-.04.04A1.7 1.7 0 0 0 19.43 9c.13.33.24.64.3 1a1.7 1.7 0 0 0 1.37.7h.1a2.05 2.05 0 0 1 0 4.1h-.06a1.7 1.7 0 0 0-1.37.7c-.06.36-.17.67-.37 1Z" /></>,
    home: <><path d="M3.5 11.4 12 4l8.5 7.4" /><path d="M5.7 10.2v8.4a1.4 1.4 0 0 0 1.4 1.4h9.8a1.4 1.4 0 0 0 1.4-1.4v-8.4" /><path d="M9.6 20v-5.2h4.8V20" /></>,
    grid: <><rect x="4" y="4" width="6" height="6" rx="1.4" /><rect x="14" y="4" width="6" height="6" rx="1.4" /><rect x="4" y="14" width="6" height="6" rx="1.4" /><rect x="14" y="14" width="6" height="6" rx="1.4" /></>,
    clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.8v4.6l3.2 1.9" /></>,
    bell: <><path d="M18 9.8a6 6 0 0 0-12 0c0 7-2.2 7.3-2.2 7.3h16.4S18 16.8 18 9.8" /><path d="M9.8 20a2.4 2.4 0 0 0 4.4 0" /></>,
    star: <path d="m12 3.8 2.45 5.1 5.6.8-4.05 3.95.95 5.55L12 16.55 7.05 19.2 8 13.65 3.95 9.7l5.6-.8L12 3.8Z" />,
    crown: <><path d="m4.2 8.4 4.4 3.5L12 5.3l3.4 6.6 4.4-3.5-1.7 9.3H5.9L4.2 8.4Z" /><path d="M6.6 20h10.8" /></>,
    diamond: <><path d="M6.2 4.8h11.6l3 5.2L12 20 3.2 10l3-5.2Z" /><path d="M3.2 10h17.6" /><path d="m8.2 4.8 3.8 15.2 3.8-15.2" /></>,
    users: <><path d="M16.5 19v-1.2a3.5 3.5 0 0 0-3.5-3.5H7.4a3.5 3.5 0 0 0-3.5 3.5V19" /><circle cx="10.2" cy="7.8" r="3.4" /><path d="M20.2 19v-1.1a3.1 3.1 0 0 0-2.4-3" /><path d="M16.5 4.6a3.2 3.2 0 0 1 0 6.2" /></>,
    user: <><circle cx="12" cy="8" r="3.7" /><path d="M5.4 20a6.6 6.6 0 0 1 13.2 0" /></>,
    save: <><path d="M5 4h11.2L19 6.8V20H5V4Z" /><path d="M8 4v6h7V4" /><path d="M8 20v-6h8v6" /></>,
    shield: <><path d="M12 3.5 19 6v5.4c0 4.3-2.8 7.3-7 9.1-4.2-1.8-7-4.8-7-9.1V6l7-2.5Z" /><path d="m9.2 12 1.8 1.8 3.8-4" /></>,
    link: <><path d="M10.2 13.8a4 4 0 0 0 5.6 0l2.1-2.1a4 4 0 0 0-5.6-5.6l-1.2 1.2" /><path d="M13.8 10.2a4 4 0 0 0-5.6 0l-2.1 2.1a4 4 0 0 0 5.6 5.6l1.2-1.2" /></>,
    map: <><path d="M12 21s7-4.7 7-11a7 7 0 0 0-14 0c0 6.3 7 11 7 11Z" /><circle cx="12" cy="10" r="2.4" /></>,
    edit: <><path d="M4 20h4.8L19.2 9.6a2.2 2.2 0 0 0-3.1-3.1L5.7 16.9 4 20Z" /><path d="m14.7 7.9 3.4 3.4" /></>,
    image: <><rect x="4" y="5" width="16" height="14" rx="2.2" /><circle cx="9" cy="10" r="1.5" /><path d="m6.5 17 4.1-4.1 2.7 2.7 2-2 2.2 3.4" /></>,
    mail: <><rect x="4" y="6" width="16" height="12" rx="2" /><path d="m5.5 8 6.5 5 6.5-5" /></>,
    cpu: <><rect x="7" y="7" width="10" height="10" rx="2" /><path d="M9.5 2.8v3M14.5 2.8v3M9.5 18.2v3M14.5 18.2v3M2.8 9.5h3M2.8 14.5h3M18.2 9.5h3M18.2 14.5h3" /></>,
    heart: <path d="M20.2 7.8c0 5.4-8.2 10-8.2 10s-8.2-4.6-8.2-10a4.3 4.3 0 0 1 7.4-3.1L12 5.5l.8-.8a4.3 4.3 0 0 1 7.4 3.1Z" />,
  };
  return <svg {...common}>{icons[name] || icons.home}</svg>;
}

export default function BusinessEditPage({
  db: dbProp = defaultDb,
  auth = defaultAuth,
  storage = defaultStorage,
  businessId,
  user: userProp = null,
  businessTypes = [],
  onBack = () => {},
  onSaved = () => {},
}) {
  useLanguage();
  const db = dbProp;
  const editorNavigation = useEditorNavigation();
  const availabilitySaveRef = useRef(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  // data
  const [businessDoc, setBusinessDoc] = useState(null);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]); // used and passed to ProjectsSection

  // flags
  const [noWorkers, setNoWorkers] = useState(false);
  const [singleService, setSingleService] = useState(false);
  const [singleWorker, setSingleWorker] = useState(false);
  const [singleServiceDuration, setSingleServiceDuration] = useState(60);

  // images + cropper
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [cropperFile, setCropperFile] = useState(null);
  const [cropperMode, setCropperMode] = useState(null);
  const [cropperTargetId, setCropperTargetId] = useState(null);
  const [cropperAspect, setCropperAspect] = useState(1);
  const [cropperOutputWidth, setCropperOutputWidth] = useState(800);
  const [cropperBackgroundColor, setCropperBackgroundColor] = useState("#f7efe6");
  const [cropperFitMode, setCropperFitMode] = useState("contain");
  const [cropperZIndex, setCropperZIndex] = useState(5000);

  // projects upload
  const [projectUploading, setProjectUploading] = useState(false);

  // uncontrolled refs used by GeneralSection
  const nameRef = useRef(null);
  const addressRef = useRef(null);
  const mapsRef = useRef(null);
  const phoneRef = useRef(null);
  const whatsappRef = useRef(null);
  const instagramRef = useRef(null);
  const websiteRef = useRef(null);
  const hoursRef = useRef(null);
  const refundPolicyRef = useRef(null);
  const eventCategoryRef = useRef(null);
  const serviceModeRef = useRef(null);
  const serviceAreasRef = useRef(null);
  const quienesRef = useRef(null);

  // LOOP SIDEBAR LOYALTY + MEMBERSHIPS V60.2 - direct editor destination.
  // /business/:id/edit already preserves location.search, so consume the two
  // allowed direct tabs here without adding routing props.
  const resolveDirectSettingsTab = () => {
    try {
      if (typeof window === "undefined") return "general";
      const requested = String(new URLSearchParams(window.location.search || "").get("tab") || "").trim().toLowerCase();
      return ["loyalty", "memberships", "general", "services", "availability", "businessos", "onlinepayments"].includes(requested) ? requested : "general";
    } catch (_) {
      return "general";
    }
  };
  const [paymentReturnTab, setPaymentReturnTab] = useState(null);
  const [activeTab, setActiveTab] = useState(() => resolveDirectSettingsTab());
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // ✅ detect native runtime before any hook/effect uses it.
  // V59 fixes TDZ error: Cannot access 'isNativeRuntime' before initialization.
  const isNativeRuntime = useMemo(() => isNativeRuntimeNow(), []);
  const notificationSaveRef = useRef(null);
  const billingSaveRef = useRef(null);
  const toastTimerRef = useRef(null);
  const [sectionSaving, setSectionSaving] = useState(false);
  const [saveToast, setSaveToast] = useState(null);

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
      }
    } catch (_) {}
  }, [activeTab]);

  const showSaveToast = (message, tone = "success") => {
    try {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    } catch (_) {}
    setSaveToast({ message: String(message || "Cambios guardados."), tone, id: Date.now() });
    try {
      toastTimerRef.current = window.setTimeout(() => setSaveToast(null), 3600);
    } catch (_) {}
  };

  useEffect(() => () => {
    try { if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current); } catch (_) {}
  }, []);

  useEffect(() => {
    try {
      if (typeof document === "undefined") return undefined;
      if (!mobileNavOpen) {
        document.body.classList.remove("loop-business-edit-mobile-nav-open");
        return undefined;
      }
      document.body.classList.add("loop-business-edit-mobile-nav-open");
      return () => {
        try { document.body.classList.remove("loop-business-edit-mobile-nav-open"); } catch (_) {}
      };
    } catch (_) {
      return undefined;
    }
  }, [mobileNavOpen]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return undefined;
      window.__LOOP_BUSINESS_EDIT_NAV_DEBUG__ = () => {
        const layer = document.querySelector(".loop-business-edit-mobile-nav-portal");
        const sheet = document.querySelector(".loop-business-edit-mobile-nav-portal .loop-mobile-nav-sheet") || document.querySelector(".loop-mobile-nav-sheet");
        const trigger = document.querySelector(".loop-mobile-section-trigger");
        const rect = (el) => {
          if (!el) return null;
          const r = el.getBoundingClientRect();
          return {
            left: Math.round(r.left),
            top: Math.round(r.top),
            right: Math.round(r.right),
            bottom: Math.round(r.bottom),
            width: Math.round(r.width),
            height: Math.round(r.height),
          };
        };
        const styles = (el) => {
          if (!el) return null;
          const s = getComputedStyle(el);
          return {
            position: s.position,
            display: s.display,
            left: s.left,
            right: s.right,
            bottom: s.bottom,
            width: s.width,
            maxWidth: s.maxWidth,
            transform: s.transform,
            overflow: s.overflow,
            zIndex: s.zIndex,
          };
        };
        const report = {
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          dpr: window.devicePixelRatio,
          platform: window.Capacitor?.getPlatform?.() || window.Capacitor?.platform || "web",
          bodyClasses: document.body.className,
          mobileNavOpen,
          isNativeRuntime,
          matchMax760: window.matchMedia?.("(max-width: 760px)")?.matches || false,
          matchMax1100: window.matchMedia?.("(max-width: 1100px)")?.matches || false,
          matchCoarseTablet: window.matchMedia?.("(pointer: coarse) and (min-width: 761px) and (max-width: 1368px)")?.matches || false,
          layerRect: rect(layer),
          sheetRect: rect(sheet),
          triggerRect: rect(trigger),
          sheetStyle: styles(sheet),
        };
        console.table(report);
        return report;
      };
      return () => {
        try { delete window.__LOOP_BUSINESS_EDIT_NAV_DEBUG__; } catch (_) {}
      };
    } catch (_) {
      return undefined;
    }
  }, [mobileNavOpen, isNativeRuntime]);

  const isEventsBusiness = useMemo(() => {
    const normalize = (v) => String(v || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

    const fields = [
      businessDoc?.type,
      businessDoc?.category,
      businessDoc?.businessType,
      businessDoc?.serviceCategory,
      businessDoc?.vertical,
    ].map(normalize).filter(Boolean);

    return fields.some((x) => x.includes("evento") || x.includes("events"));
  }, [businessDoc]);


  /* ---------------------------
     Helper: ensure current user token is fresh and get ownerUid
  --------------------------- */
  const getOwnerUidForUpload = async () => {
    const user = loopBusinessEffectiveUser(auth, userProp);
    if (!user) throw new Error("no_auth");
    try {
      if (typeof user.getIdToken === "function") {
        await user.getIdToken(true);
        console.log("DEBUG: idToken refreshed for", user.uid);
      }
    } catch (tErr) {
      console.warn("DEBUG: token refresh failed", tErr);
    }

    let businessData = null;
    try {
      businessData = await loopBusinessGetDoc(db, `businesses/${businessId}`);
    } catch (readErr) {
      console.warn("DEBUG: ownerUid business read failed", readErr);
    }

    // Storage rules commonly authorize by the UID represented in the storage path.
    // A platform owner editing another account's company should upload under the
    // platform owner's own UID, without changing the company's real ownerUid.
    const isPlatformOwner = loopBusinessIsPlatformOwner(auth, userProp);
    const ownerUid = isPlatformOwner
      ? (user.uid || user.email || "")
      : (businessData?.ownerUid || user.uid || user.email || "");
    return { user, ownerUid, isPlatformOwner };
  };

  /* ---------------------------
     Debug helper: expose auth/storage/db on window temporarily
  --------------------------- */
  useEffect(() => {
    try {
      window._auth = auth;
      window._storage = storage;
      window._db = db;
    } catch (e) {}
    return () => {
      try {
        delete window._auth;
        delete window._storage;
        delete window._db;
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------------------
     Load business and subcollections
  --------------------------- */
  useEffect(() => {
    let mounted = true;

    async function loadAll() {
      if (!db || !businessId) {setLoadError('No se pudo identificar el negocio.');setLoading(false);return;}
      setLoading(true);
      setLoadError('');
      setBusinessDoc(null);

      const applyBusinessDoc = (data) => {
        if (!mounted || !data) return;
        setBusinessDoc(data);

        const initialGalleryProjects = Array.isArray(data.featuredProjects)
          ? data.featuredProjects
          : (Array.isArray(data?.businessPage?.featuredProjects) ? data.businessPage.featuredProjects : []);

        setProjects(
          initialGalleryProjects
            .map((item, index) => {
              const url = typeof item === "string"
                ? item
                : (item?.url || item?.imageUrl || item?.src || item?.downloadURL || "");
              return url
                ? {
                    id: item?.id || `featured-${index}`,
                    name: item?.name || item?.title || `Foto ${index + 1}`,
                    url,
                    source: "featuredProjects",
                  }
                : null;
            })
            .filter(Boolean)
        );

        setNoWorkers(!!data.noWorkers);
        setSingleService(!!data.singleService);
        setSingleWorker(!!data.singleWorker);
        setSingleServiceDuration(Number(data.singleServiceDuration || 60));
        if (data.logoUrl) setLogoPreview(data.logoUrl);
        if (data.mainPhotoUrl) setTemplatePreview(data.mainPhotoUrl);

        requestAnimationFrame(() => {
          try {
            if (nameRef.current) nameRef.current.value = data.name || "";
          } catch (_) {}
          try {
            if (addressRef.current) addressRef.current.value = data.address || "";
          } catch (_) {}
          try {
            if (mapsRef.current) mapsRef.current.value = data.googleMapsLink || "";
          } catch (_) {}
          try {
            if (phoneRef.current) phoneRef.current.value = data.phone || data.phoneNumber || "";
          } catch (_) {}
          try {
            if (whatsappRef.current) whatsappRef.current.value = data.whatsapp || data.whatsApp || "";
          } catch (_) {}
          try {
            if (instagramRef.current) instagramRef.current.value = data.instagram || "";
          } catch (_) {}
          try {
            if (websiteRef.current) websiteRef.current.value = data.website || "";
          } catch (_) {}
          try {
            if (hoursRef.current) hoursRef.current.value = data.hours || data.horarios || data.businessHours || "";
          } catch (_) {}
          try {
            if (eventCategoryRef.current) eventCategoryRef.current.value = data.eventCategory || "";
          } catch (_) {}
          try {
            if (serviceModeRef.current) serviceModeRef.current.value = data.serviceMode || "hybrid";
          } catch (_) {}
          try {
            if (serviceAreasRef.current) {
              const areas = Array.isArray(data.serviceAreas) ? data.serviceAreas.join(", ") : (data.serviceArea || data.coverageArea || "");
              serviceAreasRef.current.value = areas;
            }
          } catch (_) {}
          try {
            if (quienesRef.current) quienesRef.current.value = data.quienesSomos || data.about || data.description || "";
          } catch (_) {}
        });
      };

      try {
        if (isIosCapacitor()) {
          const [bizRes, svcRes, empRes, projRes] = await Promise.allSettled([
            loopBusinessGetDoc(db, `businesses/${businessId}`),
            loopBusinessListCollection(db, `businesses/${businessId}/services`),
            loopBusinessListCollection(db, `businesses/${businessId}/employees`),
            loopBusinessListCollection(db, `businesses/${businessId}/projects`),
          ]);

          if (!mounted) return;

          if (bizRes.status === "fulfilled" && bizRes.value) {
            applyBusinessDoc(bizRes.value);
          } else {
            console.warn("[BusinessEditPage] iOS business doc load failed", bizRes.status === "rejected" ? bizRes.reason : null);
            throw new Error('business-unavailable');
          }

          if (svcRes.status === "fulfilled") {
            const list = Array.isArray(svcRes.value) ? svcRes.value.map((d) => ({ id: d.id, ...(d || {}) })) : [];
            setServices(list);
          } else {
            console.warn("[BusinessEditPage] iOS services load failed", svcRes.reason);
            setServices([]);
          }

          if (empRes.status === "fulfilled") {
            const list = Array.isArray(empRes.value) ? empRes.value.map((d) => ({ id: d.id, ...(d || {}) })) : [];
            setEmployees(list);
          } else {
            console.warn("[BusinessEditPage] iOS employees load failed", empRes.reason);
            setEmployees([]);
          }

          if (projRes.status === "rejected") {
            console.warn("[BusinessEditPage] legacy iOS projects collection read failed", projRes.reason);
          }

          return;
        }

        const [bSnap, svcSnap, empSnap, projSnap] = await Promise.allSettled([
          getDoc(doc(db, "businesses", businessId)),
          getDocs(collection(db, `businesses/${businessId}/services`)),
          getDocs(collection(db, `businesses/${businessId}/employees`)),
          getDocs(collection(db, `businesses/${businessId}/projects`)),
        ]);

        if (!mounted) return;

        if (bSnap.status === "fulfilled" && bSnap.value && bSnap.value.exists()) {
          applyBusinessDoc(bSnap.value.data());
        } else {
          throw new Error('business-unavailable');
        }

        if (svcSnap.status === "fulfilled") {
          setServices(svcSnap.value.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
        } else {
          console.warn("[BusinessEditPage] web services load failed", svcSnap.reason);
          setServices([]);
        }

        if (empSnap.status === "fulfilled") {
          setEmployees(empSnap.value.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
        } else {
          console.warn("[BusinessEditPage] web employees load failed", empSnap.reason);
          setEmployees([]);
        }

        if (projSnap.status === "rejected") {
          console.warn("[BusinessEditPage] legacy web projects collection read failed", projSnap.reason);
        }
      } catch (err) {
        console.error("loadAll error", err);
        if (mounted) setLoadError('No pudimos cargar los datos de este negocio. Reintenta antes de editar; no se ha cambiado tu información.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAll();
    return () => {
      mounted = false;
    };
  }, [db, businessId, loadAttempt, userProp?.uid]);

  /* ---------------------------
     Cropper helpers
  --------------------------- */
  const openCropper = (file, mode, opts = {}) => {
    setCropperFile(file || null);
    setCropperMode(mode || null);
    setCropperTargetId(opts.targetId || null);
    setCropperAspect(opts.aspect || 1);
    setCropperOutputWidth(opts.outputWidth || 800);
    setCropperBackgroundColor(String(opts.backgroundColor || "#f7efe6").trim() || "#f7efe6");
    setCropperFitMode(String(opts.fitMode || "contain").toLowerCase() === "cover" ? "cover" : "contain");
    const requestedZ = Number(opts.cropperZIndex || opts.zIndex || 0);
    setCropperZIndex(Number.isFinite(requestedZ) && requestedZ > 0 ? Math.min(Math.max(requestedZ, 5000), 5900) : 5000);
  };

  const handleCropperComplete = async ({ blob, url }) => {
    const originalName = cropperFile && cropperFile.name ? cropperFile.name : `${Date.now()}.jpg`;
    const ext = originalName.split(".").pop() || "jpg";
    const file = new File([blob], `img-${Date.now()}.${ext}`, { type: blob.type || "image/jpeg" });

    try {
      if (cropperMode === "logo") {
        setLogoFile(file);
        setLogoPreview(url);
        setBusinessDoc((prev) => ({
          ...(prev || {}),
          logoBackgroundColor: cropperBackgroundColor,
          brandBackgroundColor: cropperBackgroundColor,
          mediaBackgroundColor: cropperBackgroundColor,
        }));
      } else if (cropperMode === "template") {
        setTemplateFile(file);
        setTemplatePreview(url);
      } else if (cropperMode === "service" && cropperTargetId) {
        setUploadPct(0);
        try {
          const { ownerUid } = await getOwnerUidForUpload();
          const path = `businesses/${ownerUid}/${businessId}/services/${cropperTargetId}/image-${Date.now()}.${ext}`;
          const uploaded = await uploadToStorageWithMetadata(storage, path, file, ownerUid, (pct) => setUploadPct(pct));
          await loopBusinessUpdateDoc(db, `businesses/${businessId}/services/${cropperTargetId}`, {
            imageUrl: uploaded,
            updatedAt: loopBusinessNow(),
          });
          setServices((prev) => prev.map((s) => (s.id === cropperTargetId ? { ...s, imageUrl: uploaded } : s)));
        } catch (err) {
          console.error("service image upload failed", err);
          alert("Error subiendo imagen del servicio. Revisa la consola y tus Storage Rules.");
        } finally {
          setUploadPct(0);
        }
      } else if (cropperMode === "employee-edit" && cropperTargetId) {
        setUploadPct(0);
        try {
          const { ownerUid } = await getOwnerUidForUpload();
          const path = `businesses/${ownerUid}/${businessId}/employees/${cropperTargetId}/photo-${Date.now()}.${ext}`;
          const uploaded = await uploadToStorageWithMetadata(storage, path, file, ownerUid, (pct) => setUploadPct(pct));
          await loopBusinessUpdateDoc(db, `businesses/${businessId}/employees/${cropperTargetId}`, {
            photoUrl: uploaded,
            updatedAt: loopBusinessNow(),
          });
          setEmployees((prev) => prev.map((e) => (e.id === cropperTargetId ? { ...e, photoUrl: uploaded } : e)));
        } catch (err) {
          console.error("employee image upload failed", err);
          alert("Error subiendo foto del trabajador. Revisa la consola y tus Storage Rules.");
        } finally {
          setUploadPct(0);
        }
      }
    } catch (e) {
      console.error("cropper complete error", e);
    } finally {
      setCropperFile(null);
      setCropperMode(null);
      setCropperTargetId(null);
      setCropperBackgroundColor("#f7efe6");
      setCropperFitMode("contain");
      setCropperZIndex(5000);
    }
  };

  const handleCropperCancel = () => {
    setCropperFile(null);
    setCropperMode(null);
    setCropperTargetId(null);
    setCropperBackgroundColor("#f7efe6");
    setCropperFitMode("contain");
    setCropperZIndex(5000);
  };

  /* ---------------------------
     Save business (logo/template upload + doc update)
  --------------------------- */
  const handleSaveBusiness = async (profilePatch = {}) => {
    if (!db || !businessId || saving || loading || loadError || !businessDoc) return false;
    setSaving(true);
    try {
      const user = loopBusinessEffectiveUser(auth, userProp);
      if (!user) {
        showSaveToast("Debes iniciar sesión para guardar cambios e imágenes.", "error");
        setSaving(false);
        return false;
      }

      let ownerUid = user.uid;
      try {
        const info = await getOwnerUidForUpload();
        ownerUid = info.ownerUid;
        console.log("DEBUG: ownerUid from getOwnerUidForUpload:", ownerUid, "auth uid:", info.user.uid);
      } catch (err) {
        console.warn("DEBUG: getOwnerUidForUpload failed, falling back to auth uid", err);
      }

      let logoUrl = businessDoc?.logoUrl || null;
      let mainPhotoUrl = businessDoc?.mainPhotoUrl || null;

      if (logoFile) {
        setUploadPct(0);
        try {
          const ext = logoFile.name.split(".").pop() || "jpg";
          const path = `businesses/${ownerUid}/${businessId}/logo/logo-${Date.now()}.${ext}`;
          const uploaded = await uploadToStorageWithMetadata(storage, path, logoFile, ownerUid, (pct) => setUploadPct(pct));
          logoUrl = uploaded;
        } catch (err) {
          console.error("logo upload failed", err);
          alert("Error subiendo logo. Revisa la consola y Storage Rules.");
          throw err;
        } finally {
          setUploadPct(0);
        }
      }

      if (templateFile) {
        setUploadPct(0);
        try {
          const ext = templateFile.name.split(".").pop() || "jpg";
          const path = `businesses/${ownerUid}/${businessId}/template/template-${Date.now()}.${ext}`;
          const uploaded = await uploadToStorageWithMetadata(storage, path, templateFile, ownerUid, (pct) => setUploadPct(pct));
          mainPhotoUrl = uploaded;
        } catch (err) {
          console.error("template upload failed", err);
          alert("Error subiendo imagen principal. Revisa la consola y tus Storage Rules.");
          throw err;
        } finally {
          setUploadPct(0);
        }
      }

      const nameVal = nameRef.current ? String(nameRef.current.value || "").trim() : businessDoc?.name || "";
      const addressVal = addressRef.current ? String(addressRef.current.value || "").trim() : businessDoc?.address || "";
      const mapsVal = mapsRef.current ? String(mapsRef.current.value || "").trim() : businessDoc?.googleMapsLink || "";
      const phoneVal = phoneRef.current ? String(phoneRef.current.value || "").trim() : (businessDoc?.phone || businessDoc?.phoneNumber || "");
      const whatsappVal = whatsappRef.current ? String(whatsappRef.current.value || "").trim() : (businessDoc?.whatsapp || businessDoc?.whatsApp || "");
      const instagramVal = instagramRef.current ? String(instagramRef.current.value || "").trim() : (businessDoc?.instagram || "");
      const websiteVal = websiteRef.current ? String(websiteRef.current.value || "").trim() : (businessDoc?.website || "");
      const hoursVal = hoursRef.current ? String(hoursRef.current.value || "").trim() : (businessDoc?.hours || businessDoc?.horarios || businessDoc?.businessHours || "");
      const eventCategoryVal = eventCategoryRef.current ? String(eventCategoryRef.current.value || "").trim() : (businessDoc?.eventCategory || "");
      const serviceModeVal = serviceModeRef.current ? String(serviceModeRef.current.value || "").trim() : (businessDoc?.serviceMode || "hybrid");
      const serviceAreasVal = serviceAreasRef.current ? String(serviceAreasRef.current.value || "").trim() : (businessDoc?.serviceArea || businessDoc?.coverageArea || "");
      const serviceAreas = serviceAreasVal.split(",").map((x) => x.trim()).filter(Boolean);
      const quienesVal = quienesRef.current ? String(quienesRef.current.value || "").trim() : businessDoc?.quienesSomos || "";

      if(!nameVal) throw new Error("business_name_required");
      const payload = {
        ...(typeof profilePatch.type === "string" ? {type:profilePatch.type} : {}),
        name: nameVal || "",
        address: addressVal || "",
        googleMapsLink: mapsVal || "",
        phone: phoneVal || "",
        phoneNumber: phoneVal || "",
        whatsapp: whatsappVal || "",
        whatsApp: whatsappVal || "",
        instagram: instagramVal || "",
        website: websiteVal || "",
        hours: hoursVal || "",
        horarios: hoursVal || "",
        businessHours: hoursVal || "",
        eventCategory: eventCategoryVal || "",
        eventTags: eventCategoryVal ? [eventCategoryVal] : (Array.isArray(businessDoc?.eventTags) ? businessDoc.eventTags : []),
        serviceMode: serviceModeVal || "hybrid",
        serviceArea: serviceAreasVal || "",
        coverageArea: serviceAreasVal || "",
        serviceAreas,
        ...(serviceAreasRef.current?.dataset?.serviceAreasAdvanced ? {serviceAreasAdvanced: JSON.parse(serviceAreasRef.current.dataset.serviceAreasAdvanced),deliveryAreas:JSON.parse(serviceAreasRef.current.dataset.serviceAreasAdvanced)} : {}),
        hasPhysicalLocation: serviceModeVal === "onsite" || serviceModeVal === "hybrid",
        quienesSomos: quienesVal || "",
        about: quienesVal || "",
        description: quienesVal || "",
        updatedAt: loopBusinessNow(),
        singleService: !!singleService,
        singleServiceDuration: Number(singleServiceDuration || 60),
      };
      if (profilePatch.publicContactPatch && typeof profilePatch.publicContactPatch === "object") Object.assign(payload, profilePatch.publicContactPatch);
      if (profilePatch.logoBackgroundColor) Object.assign(payload, {logoBackgroundColor:profilePatch.logoBackgroundColor,brandBackgroundColor:profilePatch.logoBackgroundColor,mediaBackgroundColor:profilePatch.logoBackgroundColor});
      if (logoUrl) payload.logoUrl = logoUrl;
      if (refundPolicyRef.current && refundPolicyRef.current.value !== "") {
        const choice = refundPolicyRef.current.value;
        payload.bookingRefundPolicy = {version: 1, enabled: choice !== "disabled", noticeHours: choice === "disabled" ? 24 : Number(choice)};
      }
      if (mainPhotoUrl) payload.mainPhotoUrl = mainPhotoUrl;

      console.log("DEBUG business payload contact fields", {
        phone: payload.phone,
        whatsapp: payload.whatsapp,
        instagram: payload.instagram,
        website: payload.website,
        hours: payload.hours,
      });

      await loopBusinessSetDoc(db, `businesses/${businessId}`, payload, { merge: true });

      const latest = await loopBusinessGetDoc(db, `businesses/${businessId}`);
      if (latest) setBusinessDoc(latest);
      onSaved && onSaved();
      showSaveToast("Cambios guardados correctamente.", "success");
      setLogoFile(null);setTemplateFile(null);
      return true;
    } catch (err) {
      console.error("handleSaveBusiness error", err);
      showSaveToast("No pudimos guardar el negocio. Tus cambios siguen aquí; intenta nuevamente.", "error");
      return false;
    } finally {
      setSaving(false);
      setUploadPct(0);
    }
  };

  /* ---------------------------
     Services & Workers & Projects handlers
  --------------------------- */

  const handleAddService = async (name, duration, price = 0, extras = {}) => {
    if (singleService) return alert("La empresa está marcada como 'un solo servicio' — no se pueden agregar más servicios.");
    if (!name) return alert("Nombre requerido");
    try {
      const payload = {
        name: String(name).trim(),
        duration: Number(duration) || 30,
        price: price != null ? Number(price) : null,
        ...(extras && typeof extras === "object" ? extras : {}),
        createdAt: loopBusinessNow(),
        disabled: !!singleService,
      };
      const ref = await loopBusinessAddDoc(db, `businesses/${businessId}/services`, payload);
      const refId = ref?.id || ref?.docId || ref?.name?.split?.("/")?.pop?.() || `tmp-${Date.now()}`;
      setServices((prev) => [...prev, { id: refId, ...payload }]);
      return { id: refId, ...payload };
    } catch (e) {
      console.error("handleAddService failed", e);
      alert("No se pudo crear servicio");
    }
  };

  const handleToggleSingleServiceConfirmed = async (value) => {
    setSaving(true);
    try {
      await loopBusinessUpdateDoc(db, `businesses/${businessId}`, {
        singleService: !!value,
        singleServiceDuration: Number(singleServiceDuration || 60),
        updatedAt: loopBusinessNow(),
      });
      if (value) {
        // disable existing services
        await loopBusinessBulkUpdateCollection(db, `businesses/${businessId}/services`, () => ({ disabled: true, updatedAt: loopBusinessNow() }));
      }
      setSingleService(!!value);
      const svcRows2 = await loopBusinessListCollection(db, `businesses/${businessId}/services`);
      setServices(svcRows2);
    } catch (e) {
      console.error(e);
      alert("Error actualizando estado de servicios");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSingleService = (value) => {
    // If enabling and there are existing services, ask for confirmation.
    if (value && services && services.length > 0) {
      const confirmed = window.confirm(
        "Al activar 'empresa con un solo servicio' se deshabilitarán los servicios actuales. ¿Deseas continuar?"
      );
      if (!confirmed) {
        // keep previous state
        return;
      }
    }
    // proceed (either enabling with confirmation or disabling)
    handleToggleSingleServiceConfirmed(value);
  };

  const handleUploadServiceImage = (file, serviceId) =>
    openCropper(file, "service", { targetId: serviceId, aspect: 282 / 154, outputWidth: 1400 });

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Eliminar servicio?")) return;
    try {
      await loopBusinessDeleteDoc(db, `businesses/${businessId}/services/${serviceId}`);
      setServices((prev) => prev.filter((s) => s.id !== serviceId));
    } catch (e) {
      console.error(e);
      alert("No se pudo eliminar servicio");
    }
  };

  const handleAddEmployee = async (payloadInput) => {
    const name = payloadInput?.name || "";
    if (!name.trim()) return alert("Nombre obligatorio");
    if (singleWorker && employees && employees.length > 0) return alert("Empresa marcada como 'un solo trabajador' — no se pueden agregar más.");
    try {
      const emailNormalized = payloadInput?.email ? String(payloadInput.email).trim().toLowerCase() : "";
      // A contact address is not acceptance of an invitation.
      const userUid = null;
      const permissions = businessEditExtractWorkerPermissions(payloadInput);
      const accessRole = businessEditAccessRole(payloadInput);
      const now = businessEditPersistNow();
      const payload = {
        businessId,
        name: name.trim(),
        role: payloadInput?.role || "",
        accessRole,
        loopRole: accessRole,
        securityRole: accessRole,
        ...buildWorkerAccessFields(emailNormalized, userUid),
        accessVersion: 2,
        accessStatus: 'pending',
        inviteStatus: 'pending',
        phone: payloadInput?.phone || "",
        active: true,
        availability: payloadInput?.availability || {},
        services: Array.isArray(payloadInput?.services) ? payloadInput.services : [],
        createdAt: now,
        updatedAt: now,
        disabled: !!noWorkers,
      };
      if (permissions) {
        payload.permissions = permissions;
        payload.workerPermissions = permissions;
        payload.permissionsManualOverride = true;
        payload.permissionsUpdatedAt = now;
      }
      const created = await businessEditAddDocToCollectionPath(db, `businesses/${businessId}/employees`, payload);
      const id = created?.id || created?.docId || created?.ref?.id || created?.name?.split?.("/")?.pop?.() || null;
      setEmployees((prev) => [...prev, { id, ...payload }]);
      return { id, ...payload };
    } catch (e) {
      console.error("handleAddEmployee failed", e);
      alert("No se pudo agregar trabajador");
      throw e;
    }
  };

  const handleSaveEmployee = async (edited) => {
    if (!edited || !edited.id) return;
    try {
      const emailNormalized = edited.email ? String(edited.email).trim().toLowerCase() : "";
      let userUid = edited.userUid || edited.authUid || edited.linkedUserId || null;
      if (emailNormalized) {
        try {
          const q = query(collection(db, "users"), where("email", "==", emailNormalized));
          const snap = await getDocs(q);
          if (!snap.empty) userUid = snap.docs[0].id;
        } catch (userLookupErr) {
          console.warn("handleSaveEmployee: user lookup failed", userLookupErr);
        }
      }

      let blockedRanges = [];
      if (Array.isArray(edited.blockedRanges)) {
        blockedRanges = edited.blockedRanges.map((r) => ({ from: String(r.from), to: String(r.to) }));
      } else if (Array.isArray(edited.blockedDates)) {
        blockedRanges = edited.blockedDates.map((d) => ({ from: String(d), to: String(d) }));
      }

      const permissions = businessEditExtractWorkerPermissions(edited);
      const accessRole = businessEditAccessRole(edited);
      const now = businessEditPersistNow();
      const payload = {
        businessId,
        name: edited.name || "",
        role: edited.role || "",
        accessRole,
        loopRole: accessRole,
        securityRole: accessRole,
        compensationVisibilityOverride: edited.compensationVisibilityOverride || "business_default",
        availability: edited.availability || {},
        services: Array.isArray(edited.services) ? edited.services : [],
        serviceIds: Array.isArray(edited.serviceIds) ? edited.serviceIds : Array.isArray(edited.services) ? edited.services : [],
        ...buildWorkerAccessFields(emailNormalized, userUid),
        phone: edited.phone || "",
        workerType: edited.workerType || edited.staffType || edited.type || "service",
        staffType: edited.staffType || edited.workerType || edited.type || "service",
        type: edited.type || edited.workerType || edited.staffType || "service",
        isOperationalWorker: edited.isOperationalWorker === true,
        operationalOnly: edited.operationalOnly === true,
        hiddenFromBooking: edited.hiddenFromBooking === true,
        hiddenFromPublicProfile: edited.hiddenFromPublicProfile === true,
        visibleToCustomers: edited.visibleToCustomers !== false,
        showOnBusinessPage: edited.showOnBusinessPage !== false,
        canBeBookedOnline: edited.canBeBookedOnline !== false,
        blockedRanges,
        projects: Array.isArray(edited.projects) ? edited.projects : [],
        defaultCommissionType: edited.defaultCommissionType || edited.commissionType || "none",
        defaultCommissionValue: Number(edited.defaultCommissionValue ?? edited.commissionValue ?? 0) || 0,
        commissionIncludesTips: Boolean(edited.commissionIncludesTips),
        commissionOnlyCompleted: edited.commissionOnlyCompleted !== false,
        updatedAt: now,
      };

      if (permissions) {
        payload.permissions = permissions;
        payload.workerPermissions = permissions;
        payload.permissionsManualOverride = true;
        payload.permissionsUpdatedAt = now;
      }

      await businessEditSetDocByPath(db, `businesses/${businessId}/employees/${edited.id}`, payload, { merge: true });
      await writeWorkerAccessMirrorDocs(db, businessId, edited.id, payload);

      // Critical fix: BusinessEditPage owns the employees prop passed into WorkersSection.
      // If we don't update this local state with permissions, reopening the modal uses stale data
      // even though Firestore already verified the save.
      const optimisticEmployee = { ...edited, ...payload, id: edited.id };
      setEmployees((prev) => prev.map((e) => (String(e.id) === String(edited.id) ? { ...e, ...optimisticEmployee } : e)));

      try {
        if (typeof window !== "undefined") {
          window.__LOOP_LAST_BUSINESS_EDIT_WORKER_SAVE__ = {
            stage: "saved",
            employeeDocPath: `businesses/${businessId}/employees/${edited.id}`,
            employeeId: edited.id,
            expectedPermissions: permissions,
            payload: optimisticEmployee,
            at: new Date().toISOString(),
          };
        }
      } catch (_) {}

      return true;
    } catch (e) {
      console.error("handleSaveEmployee failed", e);
      alert("Error guardando trabajador. Revisa la consola para más detalles.");
      throw e;
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm("Eliminar trabajador?")) return;
    try {
      await manageWorkerAccess(businessId, employeeId, 'unlink');
      await loopBusinessDeleteDoc(db, `businesses/${businessId}/employees/${employeeId}`);
      setEmployees((prev) => prev.filter((e) => e.id !== employeeId));
    } catch (e) {
      console.error(e);
      alert("No se pudo eliminar trabajador");
    }
  };

  const handleToggleNoWorkers = async (value) => {
    setSaving(true);
    try {
      await loopBusinessUpdateDoc(db, `businesses/${businessId}`, { noWorkers: !!value, updatedAt: loopBusinessNow() });
      if (value) {
        await loopBusinessBulkUpdateCollection(db, `businesses/${businessId}/employees`, () => ({ disabled: true, updatedAt: loopBusinessNow() }));
      }
      setNoWorkers(!!value);
      const empRows2 = await loopBusinessListCollection(db, `businesses/${businessId}/employees`);
      setEmployees(empRows2);
    } catch (e) {
      console.error(e);
      alert("Error cambiando estado de trabajadores");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSingleWorker = async (value) => {
    setSaving(true);
    try {
      await loopBusinessUpdateDoc(db, `businesses/${businessId}`, { singleWorker: !!value, updatedAt: loopBusinessNow() });
      if (value) {
        await loopBusinessBulkUpdateCollection(db, `businesses/${businessId}/employees`, () => ({ disabled: true, updatedAt: loopBusinessNow() }));
      }
      setSingleWorker(!!value);
      const empRows2 = await loopBusinessListCollection(db, `businesses/${businessId}/employees`);
      setEmployees(empRows2);
    } catch (e) {
      console.error("handleToggleSingleWorker failed", e);
      alert("Error actualizando estado single worker");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadEmployeeProjects = async (employeeId, files) => {
    if (!employeeId || !files || files.length === 0) return;
    setUploadPct(0);
    try {
      const { ownerUid } = await getOwnerUidForUpload();
      const uploadedUrls = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const ext = f.name.split(".").pop() || "jpg";
        const path = `businesses/${ownerUid}/${businessId}/employees/${employeeId}/projects/project-${Date.now()}-${i}.${ext}`;
        const url = await uploadToStorageWithMetadata(storage, path, f, ownerUid, (pct) => setUploadPct(Math.round(pct)));
        uploadedUrls.push(url);
      }
      const currentEmployeeProjects = Array.isArray(employees?.find?.((e) => String(e.id) === String(employeeId))?.projects)
        ? employees.find((e) => String(e.id) === String(employeeId)).projects
        : [];
      const nextEmployeeProjects = [...currentEmployeeProjects, ...uploadedUrls];
      await loopBusinessUpdateDoc(db, `businesses/${businessId}/employees/${employeeId}`, {
        projects: isIosCapacitor() ? nextEmployeeProjects : arrayUnion(...uploadedUrls),
        updatedAt: loopBusinessNow(),
      });
      setEmployees((prev) =>
        prev.map((e) => (e.id === employeeId ? { ...e, projects: [...(e.projects || []), ...uploadedUrls] } : e))
      );
    } catch (e) {
      console.error("handleUploadEmployeeProjects", e);
      alert("Error subiendo proyectos del trabajador. Revisa consola.");
    } finally {
      setUploadPct(0);
    }
  };

  // LOOP BUSINESSPAGE GALLERY PUBLISHING V4
  // Public gallery source of truth: businesses/{businessId}.featuredProjects
  // This field is already normalized/read by BusinessPage and inherits the
  // public business document read rule. No public subcollection rule is needed.
  const getGalleryUrls = (value) => {
    const source = Array.isArray(value) ? value : [];
    const seen = new Set();

    return source
      .map((item) => {
        const url = typeof item === "string"
          ? item
          : (item?.url || item?.imageUrl || item?.src || item?.downloadURL || "");
        return String(url || "").trim();
      })
      .filter((url) => {
        if (!url || seen.has(url)) return false;
        seen.add(url);
        return true;
      });
  };

  const handleUploadProjects = async (files) => {
    if (!files || files.length === 0) return;
    if (files.length > 12) {
      alert("Selecciona hasta 12 fotos por subida.");
      return;
    }

    setProjectUploading(true);
    try {
      const { ownerUid } = await getOwnerUidForUpload();

      const currentUrls = getGalleryUrls(
        Array.isArray(businessDoc?.featuredProjects)
          ? businessDoc.featuredProjects
          : (Array.isArray(businessDoc?.businessPage?.featuredProjects)
              ? businessDoc.businessPage.featuredProjects
              : projects)
      );

      const uploadedUrls = [];

      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const ext = (f.name.split(".").pop() || "jpg").replace(/[^a-zA-Z0-9]/g, "") || "jpg";
        const path = `businesses/${ownerUid}/${businessId}/gallery/gallery-${Date.now()}-${i}.${ext}`;

        const url = await uploadToStorageWithMetadata(
          storage,
          path,
          f,
          ownerUid,
          (pct) => setUploadPct(pct)
        );

        if (url) uploadedUrls.push(url);
      }

      const updatedUrls = Array.from(new Set([...currentUrls, ...uploadedUrls])).slice(0, 50);

      await loopBusinessUpdateDoc(db, `businesses/${businessId}`, {
        featuredProjects: updatedUrls,
        updatedAt: loopBusinessNow(),
      });

      setBusinessDoc((prev) => ({
        ...(prev || {}),
        featuredProjects: updatedUrls,
      }));

      setProjects(
        updatedUrls.map((url, index) => ({
          id: `featured-${index}`,
          name: `Foto ${index + 1}`,
          url,
          source: "featuredProjects",
        }))
      );

      alert(`Fotos publicadas: ${uploadedUrls.length}`);
    } catch (e) {
      console.error("handleUploadProjects failed", e);
      if (e && e.message === "no_auth") alert("Debes iniciar sesión para subir archivos.");
      else alert("Error subiendo fotos. Revisa la consola y tus Storage Rules.");
      throw e;
    } finally {
      setProjectUploading(false);
      setUploadPct(0);
    }
  };

  const handleRemoveGalleryProject = async (projectUrl) => {
    const urlToRemove = String(projectUrl || "").trim();
    if (!urlToRemove) return;

    try {
      const currentUrls = getGalleryUrls(
        Array.isArray(businessDoc?.featuredProjects)
          ? businessDoc.featuredProjects
          : (Array.isArray(businessDoc?.businessPage?.featuredProjects)
              ? businessDoc.businessPage.featuredProjects
              : projects)
      );

      const updatedUrls = currentUrls.filter((url) => url !== urlToRemove);

      await loopBusinessUpdateDoc(db, `businesses/${businessId}`, {
        featuredProjects: updatedUrls,
        updatedAt: loopBusinessNow(),
      });

      setBusinessDoc((prev) => ({
        ...(prev || {}),
        featuredProjects: updatedUrls,
      }));

      setProjects(
        updatedUrls.map((url, index) => ({
          id: `featured-${index}`,
          name: `Foto ${index + 1}`,
          url,
          source: "featuredProjects",
        }))
      );
    } catch (e) {
      console.error("handleRemoveGalleryProject failed", e);
      alert("No se pudo quitar la foto de la galería.");
      throw e;
    }
  };

  const handleGalleryVisibilityChange = async (isVisible) => {
    const visible = Boolean(isVisible);
    try {
      await loopBusinessUpdateDoc(db, `businesses/${businessId}`, {
        showProjectsOnBusinessPage: visible,
        hideProjectsOnBusinessPage: !visible,
        updatedAt: loopBusinessNow(),
      });

      setBusinessDoc((prev) => ({
        ...(prev || {}),
        showProjectsOnBusinessPage: visible,
        hideProjectsOnBusinessPage: !visible,
      }));
    } catch (e) {
      console.error("handleGalleryVisibilityChange failed", e);
      alert("No se pudo actualizar la visibilidad de la galería.");
      throw e;
    }
  };

  if (!loading && (loadError || !businessDoc)) return <section role="alert" style={{padding:24}}><h1>{t("No pudimos abrir el negocio")}</h1><p>{loadError || 'Los datos no están disponibles. Intenta nuevamente.'}</p><button type="button" onClick={()=>setLoadAttempt(n=>n+1)}>{t("Reintentar")}</button><button type="button" onClick={onBack}>{t("Volver")}</button></section>;
  if (loading) return (
    <div className="business-settings-shell loop-settings-minimal loop-settings-loading-shell">
      <style>{`
        .loop-settings-loading-shell{
          min-height:calc(100dvh - 66px);
          width:100%;
          background:linear-gradient(180deg,#fbf7ef 0%,#fffdf8 46%,#f8fafc 100%);
          padding:24px 18px 54px;
          color:#0f172a;
          overflow:hidden;
        }
        .loop-loading-layout{
          display:grid;
          grid-template-columns:300px minmax(0,1fr);
          gap:24px;
          align-items:start;
          width:100%;
        }
        .loop-loading-card,
        .loop-loading-main-card{
          border:1px solid rgba(226,232,240,.9);
          background:rgba(255,255,255,.94);
          box-shadow:0 22px 58px rgba(15,23,42,.06);
        }
        .loop-loading-card{
          width:272px;
          border-radius:28px;
          padding:16px;
          display:grid;
          gap:14px;
        }
        .loop-loading-profile{display:grid;grid-template-columns:54px minmax(0,1fr);gap:12px;align-items:center;padding-bottom:12px;border-bottom:1px solid rgba(226,232,240,.82);}
        .loop-loading-avatar{width:54px;height:54px;border-radius:18px;background:#e8f3f1;}
        .loop-loading-lines{display:grid;gap:8px;}
        .loop-loading-line{height:11px;border-radius:999px;background:#edf2f7;overflow:hidden;position:relative;}
        .loop-loading-line.short{width:54%;}.loop-loading-line.med{width:76%;}.loop-loading-line.long{width:100%;}
        .loop-loading-menu{display:grid;gap:9px;}
        .loop-loading-pill{height:38px;border-radius:14px;background:#f4f7f6;}
        .loop-loading-main{display:grid;gap:14px;min-width:0;}
        .loop-loading-main-head{display:flex;justify-content:space-between;gap:14px;align-items:center;}
        .loop-loading-title{width:220px;height:34px;border-radius:999px;background:#edf2f7;}
        .loop-loading-save{width:160px;height:42px;border-radius:14px;background:#00675f;opacity:.92;}
        .loop-loading-main-card{border-radius:28px;min-height:360px;padding:26px;display:grid;gap:16px;align-content:start;}
        .loop-loading-block{height:68px;border-radius:18px;background:#f8fafc;}
        .loop-loading-shimmer{position:relative;overflow:hidden;}
        .loop-loading-shimmer::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.72),transparent);animation:loopLoadingShimmer 1.15s infinite;}
        @keyframes loopLoadingShimmer{100%{transform:translateX(100%);}}
        @media(max-width:760px){
          .loop-settings-loading-shell{padding:10px 10px calc(96px + max(env(safe-area-inset-bottom,0px),18px));}
          .loop-loading-layout{display:grid;grid-template-columns:1fr;gap:12px;}
          .loop-loading-card{width:100%;border-radius:18px;padding:10px;}
          .loop-loading-profile{display:none;}
          .loop-loading-menu{display:flex;overflow:hidden;gap:8px;}
          .loop-loading-pill{min-width:132px;height:42px;border-radius:14px;}
          .loop-loading-main-head{display:none;}
          .loop-loading-main-card{border-radius:22px;min-height:300px;padding:18px;}
        }
      `}</style>

      <style>{`
        /* LOOP BUSINESS EDIT — CONTENT GAP TIGHTEN FIX */
        @media (min-width: 1025px){
          .loop-settings-minimal{
            padding-left: clamp(18px, 2.2vw, 34px) !important;
            padding-right: clamp(18px, 2.2vw, 34px) !important;
          }

          .loop-settings-layout{
            grid-template-columns: 286px minmax(0, 1fr) !important;
            gap: clamp(28px, 3.2vw, 56px) !important;
            align-items: start !important;
          }

          .loop-settings-nav{
            width: 286px !important;
            max-width: 286px !important;
          }

          .loop-settings-main{
            max-width: min(1240px, calc(100vw - 390px)) !important;
            justify-self: start !important;
          }
        }

        @media (min-width: 1320px){
          .loop-settings-layout{
            grid-template-columns: 286px minmax(0, 1fr) !important;
            gap: 56px !important;
          }
        }

        @media (min-width: 1600px){
          .loop-settings-layout{
            grid-template-columns: 286px minmax(0, 1fr) !important;
            gap: 64px !important;
          }

          .loop-settings-main{
            max-width: 1260px !important;
          }
        }

        @media (max-width: 1024px){
          .loop-settings-main{
            max-width: 100% !important;
          }
        }
      `}</style>

      <div className="loop-loading-layout" aria-label={t("Cargando configuración del negocio")}>
        <aside className="loop-loading-card">
          <div className="loop-loading-profile">
            <div className="loop-loading-avatar loop-loading-shimmer" />
            <div className="loop-loading-lines">
              <div className="loop-loading-line med loop-loading-shimmer" />
              <div className="loop-loading-line short loop-loading-shimmer" />
            </div>
          </div>
          <div className="loop-loading-menu">
            <div className="loop-loading-pill loop-loading-shimmer" />
            <div className="loop-loading-pill loop-loading-shimmer" />
            <div className="loop-loading-pill loop-loading-shimmer" />
            <div className="loop-loading-pill loop-loading-shimmer" />
          </div>
        </aside>
        <main className="loop-loading-main">
          <div className="loop-loading-main-head">
            <div className="loop-loading-title loop-loading-shimmer" />
            <div className="loop-loading-save loop-loading-shimmer" />
          </div>
          <section className="loop-loading-main-card">
            <div className="loop-loading-line long loop-loading-shimmer" />
            <div className="loop-loading-block loop-loading-shimmer" />
            <div className="loop-loading-block loop-loading-shimmer" />
            <div className="loop-loading-block loop-loading-shimmer" />
          </section>
        </main>
      </div>
    </div>
  );


  const handleVisibilityChange = async (nextVisible) => {
    if (!db || !businessId) throw new Error("No hay negocio disponible.");
    await loopBusinessUpdateDoc(db, `businesses/${businessId}`, {
      visible: !!nextVisible,
      hidden: !nextVisible,
      isActive: !!nextVisible,
      published: !!nextVisible,
      updatedAt: loopBusinessNow(),
    });
    setBusinessDoc((prev) => ({
      ...(prev || {}),
      visible: !!nextVisible,
      hidden: !nextVisible,
      isActive: !!nextVisible,
      published: !!nextVisible,
    }));
  };

  const profileVisible = businessDoc?.visible !== false && businessDoc?.hidden !== true;
  const currentBusinessName = businessDoc?.name || "Tu negocio";
  const publicLogo = logoPreview || businessDoc?.logoUrl || businessDoc?.logo || businessDoc?.logoImage || businessDoc?.mainPhotoUrl || templatePreview || "";
  const publicType = businessDoc?.type || businessDoc?.businessType || businessDoc?.category || (isEventsBusiness ? "Eventos" : "Negocio");
  const loopPlanCard = getLoopPlanCardMeta(businessDoc || {});
  const servicesCount = Array.isArray(services) ? services.length : 0;
  const employeesCount = Array.isArray(employees) ? employees.length : 0;
  const projectsCount = Array.isArray(projects) ? projects.length : 0;

  const readinessItems = [
    { key: "main", label: "Datos principales", done: Boolean(currentBusinessName && currentBusinessName !== "Tu negocio" && publicType) },
    { key: "contact", label: "Contacto público", done: Boolean(businessDoc?.phone || businessDoc?.phoneNumber || businessDoc?.whatsapp || businessDoc?.whatsApp || businessDoc?.instagram) },
    { key: "location", label: "Ubicación y cobertura", done: Boolean(businessDoc?.address || businessDoc?.customerLocation || businessDoc?.serviceArea || businessDoc?.coverageArea || (Array.isArray(businessDoc?.serviceAreas) && businessDoc.serviceAreas.length)) },
    { key: "presentation", label: "Presentación", done: Boolean(businessDoc?.quienesSomos || businessDoc?.description || businessDoc?.about || businessDoc?.bio) },
    { key: "services", label: "Servicios", done: servicesCount > 0 },
  ];
  const completedReadiness = readinessItems.filter((x) => x.done).length;
  const readinessPct = Math.round((completedReadiness / readinessItems.length) * 100);
  const missingReadiness = readinessItems.filter((item) => !item.done).slice(0, 2);

  const settingsGroups = [
    {label:"TU NEGOCIO",items:[
      {id:"general",navKey:"general",label:"Perfil público",icon:"gear",primary:true},
      {id:"gallery",navKey:"gallery",label:"Galería",icon:"image",primary:true}]},
    {label:"RESERVAS Y VENTAS",items:[
      {id:"services",navKey:"services",label:"Servicios",icon:"grid",primary:true},
      {id:"availability",navKey:"availability",label:"Horarios y reservas",icon:"clock",primary:true},
      {id:"workers",navKey:"workers",label:"Equipo",icon:"users",primary:true},
      {id:"projects",navKey:"customers",label:"Clientes",icon:"user",primary:true},
      {id:"loyalty",navKey:"loyalty",label:"Fidelidad",icon:"heart",primary:true},
      {id:"memberships",navKey:"memberships",label:"Membresías",icon:"crown",primary:true}]},
    {label:"CONFIGURACIÓN",items:[
      {id:"notifications",navKey:"messages",label:"Mensajes",icon:"mail",primary:true},
      {id:"onlinepayments",navKey:"onlinepayments",label:"Cobros online",icon:"credit-card",primary:true},
      {id:"businessos",navKey:"businessos",label:"Funciones",icon:"cpu",primary:true},
      {id:"billing",navKey:"billing",label:"Plan Loop",icon:loopPlanCard.icon||"diamond",primary:true,mobileOnly:true}]}
  ];

  const flatSettingsItems = settingsGroups.flatMap((group) =>
    group.items.map((item) => ({ ...item, groupLabel: group.label }))
  );

  const activeNavItem = flatSettingsItems.find((item) => item.id === activeTab) || flatSettingsItems[0];

  const selectSettingsTab = (tabId) => {
    if(tabId === activeTab){setMobileNavOpen(false);return;}
    editorNavigation.navigate(()=>{setActiveTab(tabId);setMobileNavOpen(false);});
  };

  const openPublicPreview = () => {
    try {
      if (businessId) window.open(`/business/${businessId}`, "_blank", "noopener,noreferrer");
    } catch (_) {}
  };

  const hasSectionSave = (["availability","notifications","billing"].includes(activeTab) || (activeTab === "general" && Boolean(logoFile || templateFile)));
  const getContextualSaveLabel = () => {
    if (activeTab === "notifications") return t("Guardar esta pestaña");
    if (activeTab === "billing") return t("Guardar Plan Loop");
    if (activeTab === "businessos") return t("Guardar Business OS");
    if (activeTab === "loyalty") return t("Guardar fidelidad");
    if (activeTab === "memberships") return t("Guardar membresías");
    if (activeTab === "general") return t("Guardar perfil");
    if (activeTab === "gallery") return t("Guardar galería");
    return t("Guardar cambios");
  };

  const getContextualSuccessMessage = () => {
    if (activeTab === "notifications") return t("Preferencias de notificaciones guardadas.");
    if (activeTab === "billing") return t("Plan Loop y beneficios actualizados.");
    if (activeTab === "businessos") return t("Business OS actualizado.");
    if (activeTab === "loyalty") return t("Programas de fidelidad actualizados.");
    if (activeTab === "memberships") return t("Membresías actualizadas.");
    if (activeTab === "general") return t("Perfil del negocio guardado correctamente.");
    if (activeTab === "gallery") return t("Galería actualizada correctamente.");
    return t("Cambios guardados correctamente.");
  };


  const getActiveSectionTitle = () => {
    if (activeTab === "notifications") return t("Mensajes");
    if (activeTab === "billing") return t("Plan Loop");
    if (activeTab === "onlinepayments") return t("Cobros online");
    if (activeTab === "businessos") return t("Funciones y configuración");
    if (activeTab === "loyalty") return t("Fidelidad");
    if (activeTab === "memberships") return t("Membresías");
    if (activeTab === "services") return t("Servicios");
    if (activeTab === "availability") return t("Horarios y reservas");
    if (activeTab === "gallery") return t("Galería");
    if (activeTab === "workers") return t("Equipo");
    if (activeTab === "projects") return t("Clientes");
    return t("Perfil público");
  };

  const getActiveSectionSubtitle = () => {
    if (activeTab === "general") return t("Gestiona la información principal de tu negocio y cómo te ven tus clientes en Loop.");
    if (activeTab === "gallery") return t("Administra las fotos que aparecen en el perfil público de tu negocio.");
    if (activeTab === "availability") return t("Define horarios, días de atención y reglas de agenda.");
    if (activeTab === "projects") return t("Gestiona la base de clientes y el historial del negocio.");
    if (activeTab === "notifications") return t("Configura mensajes, canales y automatizaciones.");
    if (activeTab === "onlinepayments") return t("Solicita la activación y prepara tus datos para recibir pagos con Wompi.");
    if (activeTab === "businessos") return t("Activa módulos y permisos operativos del negocio.");
    return t("Guarda los cambios de esta sección.");
  };

  const handleContextualSave = async () => {
    const externalSave = activeTab === "notifications"
      ? notificationSaveRef.current
      : activeTab === "availability"
        ? availabilitySaveRef.current
        : activeTab === "billing"
        ? billingSaveRef.current
        : null;

    if (typeof externalSave === "function") {
      setSectionSaving(true);
      try {
        const result = await externalSave();
        showSaveToast(result?.message || getContextualSuccessMessage(), result === false || result?.ok === false ? "error" : "success");
      } catch (err) {
        console.error("[BusinessEditPage] contextual save failed", err);
        showSaveToast(err?.message || "No se pudo guardar esta sección.", "error");
      } finally {
        setSectionSaving(false);
      }
      return;
    }

    if (activeTab !== "general") {
      showSaveToast("Espera a que termine de cargar esta sección.", "error");
      return;
    }
    await handleSaveBusiness();
  };

  return (
    <div className={isNativeRuntime ? "business-settings-shell loop-settings-minimal is-native" : "business-settings-shell loop-settings-minimal"}>
      <style>{`
        .loop-settings-minimal {
          --loop-brand:#00675f;
          --loop-brand-dark:#003f3a;
          --loop-soft:#f2faf7;
          --loop-card:#ffffff;
          --loop-line:#e5edf3;
          --loop-text:#0f172a;
          --loop-muted:#64748b;
          width:100%;
        }

        .loop-save-toast{
          position:fixed;
          right:24px;
          top:92px;
          z-index:var(--loop-z-toast,6000);
          width:min(420px,calc(100vw - 32px));
          border:1px solid rgba(0,103,95,.18);
          background:rgba(255,255,255,.96);
          box-shadow:0 22px 55px rgba(15,23,42,.18);
          border-radius:22px;
          padding:15px 16px;
          display:flex;
          align-items:flex-start;
          gap:12px;
          backdrop-filter:blur(18px);
          -webkit-backdrop-filter:blur(18px);
          animation:loopToastIn .18s ease-out;
        }
        .loop-save-toast.error{border-color:#fecaca;background:#fff7f7;}
        .loop-save-toast-icon{width:34px;height:34px;border-radius:999px;display:grid;place-items:center;background:#dcfce7;color:#047857;font-weight:950;flex:0 0 auto;}
        .loop-save-toast.error .loop-save-toast-icon{background:#fee2e2;color:#b91c1c;}
        .loop-save-toast strong{display:block;color:#0f172a;font-size:14px;font-weight:950;line-height:1.2;}
        .loop-save-toast span{display:block;margin-top:3px;color:#64748b;font-size:12px;font-weight:750;line-height:1.35;}
        .loop-save-toast button{margin-left:auto;border:0;background:transparent;color:#94a3b8;font-size:18px;font-weight:950;cursor:pointer;line-height:1;}
        @keyframes loopToastIn{from{opacity:0;transform:translateY(-8px) scale(.98)}to{opacity:1;transform:translateY(0) scale(1)}}
        @media(max-width:720px){.loop-save-toast{top:auto;left:12px;right:12px;bottom:calc(92px + env(safe-area-inset-bottom,0px));width:auto;}}

        .loop-settings-minimal {
          min-height:calc(100dvh - 66px);
          background:linear-gradient(180deg,#fbf7ef 0%,#ffffff 42%,#f8fafc 100%);
          padding:22px clamp(10px,1.4vw,22px) 48px;
          color:var(--loop-text);
        }
        .loop-settings-minimal *{box-sizing:border-box;}
        .loop-settings-layout{
          width:100%;
          max-width:none;
          margin:0;
          display:grid;
          grid-template-columns:220px minmax(0,1fr);
          gap:16px;
          align-items:start;
          justify-content:start;
        }
        .loop-settings-nav{
          position:sticky;
          top:88px;
          border:1px solid var(--loop-line);
          border-radius:22px;
          background:rgba(255,255,255,.82);
          box-shadow:0 12px 32px rgba(15,23,42,.05);
          padding:8px;
          backdrop-filter:blur(14px);
          display:grid;
          gap:10px;
        }
        .loop-settings-nav-title{
          padding:10px 10px 9px;
          border-bottom:1px solid rgba(226,232,240,.72);
          margin-bottom:8px;
        }
        .loop-settings-nav-title strong{display:block;font-size:14px;font-weight:950;letter-spacing:-.02em;color:#0f172a;}
        .loop-settings-nav-title span{display:block;margin-top:3px;font-size:11px;font-weight:750;color:#64748b;line-height:1.35;}
        .loop-settings-tabs{display:grid;gap:4px;}
        .loop-settings-tab{
          width:100%;
          border:0;
          background:transparent;
          border-radius:14px;
          padding:9px 9px;
          display:flex;
          align-items:center;
          gap:10px;
          text-align:left;
          cursor:pointer;
          color:#475569;
          transition:.15s ease;
        }
        .loop-settings-tab:hover{background:#f8fafc;color:#0f172a;}
        .loop-settings-tab.is-active{background:rgba(0,103,95,.09);color:var(--loop-brand-dark);}
        .loop-settings-tab-icon{
          width:28px;
          height:28px;
          border-radius:11px;
          display:grid;
          place-items:center;
          background:rgba(15,23,42,.045);
          font-size:13px;
          font-weight:950;
          flex:0 0 auto;
        }
        .loop-settings-tab.is-active .loop-settings-tab-icon{background:#fff;color:var(--loop-brand);box-shadow:0 8px 18px rgba(0,103,95,.10);}
        .loop-settings-tab-copy{min-width:0;}
        .loop-settings-tab-copy strong{display:block;font-size:13px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .loop-settings-tab-copy span{display:none;}
        .loop-settings-main{min-width:0;display:grid;gap:14px;width:100%;}
        .loop-settings-topbar{display:flex;justify-content:flex-end;align-items:center;gap:12px;min-height:44px;}
        .loop-settings-topbar-note{font-size:12px;color:#64748b;font-weight:760;}
        .loop-settings-topbar .loop-save-btn{width:auto!important;min-width:158px!important;height:44px!important;border-radius:14px!important;padding:0 18px!important;}
        .loop-settings-kicker{font-size:11px;font-weight:950;letter-spacing:.14em;text-transform:uppercase;color:var(--loop-brand);margin-bottom:7px;}
        .loop-settings-title{margin:0;font-size:clamp(26px,2.4vw,36px);line-height:.95;font-weight:950;letter-spacing:-.06em;color:#123f39;}
        .loop-settings-subtitle{margin:6px 0 0;max-width:680px;color:var(--loop-muted);font-size:14px;line-height:1.45;font-weight:720;}
        .loop-settings-actions{display:flex;align-items:center;gap:10px;flex-wrap:wrap;justify-content:flex-end;}
        .loop-status-pill{
          height:34px;
          border-radius:999px;
          padding:0 13px;
          border:1px solid #bbf7d0;
          background:#ecfdf5;
          color:#047857;
          display:inline-flex;
          align-items:center;
          font-size:12px;
          font-weight:950;
          white-space:nowrap;
        }
        .loop-status-pill.paused{border-color:#fed7aa;background:#fff7ed;color:#c2410c;}
        .loop-save-btn{
          height:40px;
          border:0;
          border-radius:12px;
          padding:0 18px;
          background:linear-gradient(135deg,#004f47,#007467);
          color:#fff;
          font-weight:950;
          box-shadow:0 14px 28px rgba(0,103,95,.16);
          cursor:pointer;
          white-space:nowrap;
        }
        .loop-save-btn:disabled{opacity:.65;cursor:not-allowed;}
        .loop-quick-strip{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          border:1px solid var(--loop-line);
          background:rgba(255,255,255,.84);
          border-radius:20px;
          padding:12px 14px;
          box-shadow:0 14px 36px rgba(15,23,42,.045);
        }
        .loop-quick-left{display:flex;align-items:center;gap:10px;min-width:0;}
        .loop-quick-logo{
          width:38px;
          height:38px;
          border-radius:14px;
          display:grid;
          place-items:center;
          overflow:hidden;
          background:linear-gradient(135deg,#003f3a,#0f766e);
          color:#f7e8c9;
          font-size:12px;
          font-weight:950;
          flex:0 0 auto;
        }
        .loop-quick-logo img{width:100%;height:100%;object-fit:cover;display:block;}
        .loop-quick-text{min-width:0;}
        .loop-quick-text strong{display:block;color:#0f172a;font-size:13px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .loop-quick-text span{display:block;color:#64748b;font-size:12px;font-weight:720;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .loop-preview-btn{height:36px;border-radius:12px;border:1px solid rgba(0,103,95,.28);background:#fff;color:var(--loop-brand);padding:0 13px;font-size:12px;font-weight:950;cursor:pointer;white-space:nowrap;}
        .loop-settings-content{
          border:0;
          border-radius:0;
          background:transparent;
          box-shadow:none;
          padding:0;
          overflow:visible;
        }
        .loop-settings-content .gen-shell,
        .loop-settings-content .services-pro-shell,
        .loop-settings-content .availability-shell,
        .loop-settings-content .workers-pro-shell,
        .loop-crm-only-shell{display:grid;gap:18px;min-width:0}.loop-crm-only-shell .cp-page.is-embedded{padding:0!important}.loop-crm-only-shell .cp-shell{gap:16px!important}
        .loop-settings-content .projects-pro-shell{margin:0!important;}

        .loop-action-rail{
          position:sticky;
          top:90px;
          display:grid;
          gap:12px;
        }
        .loop-action-card{
          border:1px solid var(--loop-line);
          background:rgba(255,255,255,.86);
          border-radius:20px;
          padding:12px;
          box-shadow:0 12px 32px rgba(15,23,42,.048);
          backdrop-filter:blur(14px);
        }
        .loop-action-profile{display:grid;gap:10px;}
        .loop-action-profile-top{display:flex;align-items:center;gap:10px;min-width:0;}
        .loop-action-logo{width:36px;height:36px;border-radius:15px;display:grid;place-items:center;overflow:hidden;background:linear-gradient(135deg,#003f3a,#0f766e);color:#f7e8c9;font-size:12px;font-weight:950;flex:0 0 auto;}
        .loop-action-logo img{width:100%;height:100%;object-fit:cover;display:block;}
        .loop-action-business{min-width:0;}
        .loop-action-business strong{display:block;color:#0f172a;font-size:13px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .loop-action-business span{display:block;margin-top:3px;color:#64748b;font-size:11px;font-weight:760;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .loop-action-progress{display:grid;gap:8px;padding-top:4px;}
        .loop-action-progress-row{display:flex;justify-content:space-between;gap:10px;align-items:center;color:#0f172a;font-size:12px;font-weight:950;}
        .loop-action-progress-row span{color:#64748b;font-size:11px;font-weight:780;}
        .loop-action-track{height:7px;border-radius:999px;background:#e5edf3;overflow:hidden;}
        .loop-action-track i{display:block;height:100%;border-radius:999px;background:linear-gradient(135deg,#00675f,#0f766e);}
        .loop-action-missing{display:grid;gap:5px;margin-top:2px;}
        .loop-action-missing small{color:#64748b;font-size:11px;font-weight:800;line-height:1.35;}
        .loop-action-buttons{display:grid;gap:8px;margin-top:2px;}
        .loop-action-buttons .loop-preview-btn,.loop-action-buttons .loop-save-btn{width:100%;height:36px;}
        .loop-nav-actions{display:grid;gap:10px;}
        .loop-nav-action-card{border-radius:18px;padding:10px;background:linear-gradient(180deg,rgba(255,255,255,.92),rgba(248,250,252,.86));}
        .loop-nav-action-card .loop-action-profile-top{gap:9px;}
        .loop-nav-action-card .loop-action-logo{width:34px;height:34px;border-radius:14px;}
        .loop-nav-action-card .loop-status-pill{height:32px;width:100%;justify-content:center;}
        .loop-nav-action-card .loop-action-buttons{grid-template-columns:1fr;gap:7px;}
        .loop-nav-action-card .loop-preview-btn,.loop-nav-action-card .loop-save-btn{height:38px;}
        .loop-settings-layout > .loop-action-rail{display:none!important;}

        /* PHASE 31D.4 — Centered Minimal Settings Layout */
        .loop-settings-minimal .loop-settings-layout{
          transform:none;
        }
        .loop-settings-minimal .loop-settings-nav,
        .loop-settings-minimal .loop-action-card,
        .loop-settings-minimal .loop-settings-content{
          backdrop-filter:blur(12px);
        }
        .loop-settings-minimal .loop-action-business span{
          max-width:150px;
        }
        .loop-settings-minimal .loop-action-progress-row{
          letter-spacing:-.01em;
        }
        .loop-settings-minimal .loop-action-missing small{
          display:block;
          padding:0;
        }
        .loop-settings-minimal .loop-preview-btn{
          box-shadow:none;
        }
        .loop-settings-minimal .loop-save-btn{
          box-shadow:0 10px 22px rgba(0,103,95,.14);
        }
        @media(min-width:1280px){
          .loop-settings-minimal{
            padding-left:clamp(18px,2.2vw,34px);
            padding-right:clamp(18px,2.2vw,34px);
          }
        }




        /* =========================================================
           LOOP D2.9.4F.1 — Business settings side panel polish
           Makes the business card the first thing in the rail, then settings.
           ========================================================= */
        .loop-settings-minimal:not(.is-native) .loop-settings-layout{
          grid-template-columns:248px minmax(0,1fr)!important;
          gap:18px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav{
          padding:10px!important;
          border-radius:26px!important;
          background:linear-gradient(180deg,rgba(255,255,255,.94),rgba(248,252,250,.88))!important;
          box-shadow:0 18px 50px rgba(15,23,42,.075)!important;
          border:1px solid rgba(214,226,234,.96)!important;
          gap:12px!important;
          overflow:hidden!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-nav-actions{
          order:-3!important;
          display:grid!important;
          gap:0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-nav-action-card{
          border:1px solid rgba(212,226,232,.92)!important;
          border-radius:24px!important;
          padding:12px!important;
          background:
            radial-gradient(circle at 0% 0%,rgba(0,103,95,.095),transparent 38%),
            linear-gradient(180deg,#ffffff,#f7fffc)!important;
          box-shadow:0 16px 36px rgba(15,23,42,.07)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-nav-action-card .loop-action-profile-top{
          padding-bottom:10px!important;
          border-bottom:1px solid rgba(226,232,240,.82)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-nav-action-card .loop-action-logo{
          width:42px!important;
          height:42px!important;
          border-radius:16px!important;
          box-shadow:0 10px 22px rgba(0,103,95,.12)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-action-business strong{
          font-size:14px!important;
          letter-spacing:-.025em!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-action-business span{
          font-size:11.5px!important;
          color:#5c6d81!important;
          max-width:155px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-nav-action-card .loop-status-pill{
          margin-top:10px!important;
          height:34px!important;
          border-radius:999px!important;
          font-size:12px!important;
          letter-spacing:-.01em!important;
          background:#ecfdf5!important;
          border-color:#bbf7d0!important;
          color:#047857!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-nav-action-card .loop-status-pill.paused{
          background:#fff7ed!important;
          border-color:#fed7aa!important;
          color:#c2410c!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-nav-action-card .loop-action-progress{
          margin-top:10px!important;
          padding-top:0!important;
          gap:7px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-action-progress-row strong{
          font-size:12.5px!important;
          color:#0f172a!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-action-track{
          height:8px!important;
          background:#e7eef3!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-action-missing small{
          color:#68788d!important;
          font-size:11.5px!important;
          font-weight:820!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-nav-action-card .loop-action-buttons{
          margin-top:11px!important;
          gap:8px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-nav-action-card .loop-preview-btn{
          height:40px!important;
          border-radius:14px!important;
          background:rgba(255,255,255,.86)!important;
          border-color:rgba(0,103,95,.24)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-nav-action-card .loop-save-btn{
          height:44px!important;
          border-radius:15px!important;
          font-size:14px!important;
          background:linear-gradient(135deg,#005f55,#008474)!important;
          box-shadow:0 14px 26px rgba(0,103,95,.18)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-title{
          order:-2!important;
          margin:0!important;
          padding:12px 12px 8px!important;
          border-bottom:0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-title strong{
          font-size:13.5px!important;
          color:#0f172a!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-title span{
          max-width:190px!important;
          color:#64748b!important;
          font-size:11.5px!important;
          line-height:1.32!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tabs{
          gap:5px!important;
          padding:0 2px 2px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab{
          min-height:45px!important;
          border-radius:17px!important;
          padding:8px 10px!important;
          gap:10px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon{
          width:31px!important;
          height:31px!important;
          border-radius:13px!important;
          background:#f1f5f9!important;
          color:#64748b!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab.is-active{
          background:linear-gradient(90deg,rgba(0,103,95,.12),rgba(0,103,95,.06))!important;
          box-shadow:inset 0 0 0 1px rgba(0,103,95,.055)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab.is-active .loop-settings-tab-icon{
          background:#ffffff!important;
          color:#00675f!important;
          box-shadow:0 10px 20px rgba(0,103,95,.12)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-copy strong{
          font-size:13.2px!important;
          color:inherit!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-copy span{
          display:block!important;
          margin-top:2px!important;
          font-size:10.5px!important;
          color:#7b8aa0!important;
          font-weight:800!important;
          white-space:nowrap!important;
          overflow:hidden!important;
          text-overflow:ellipsis!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab.is-active .loop-settings-tab-copy span{
          color:#00675f!important;
        }

        .loop-settings-updated{font-size:12px;color:#94a3b8;font-weight:720;padding:0 2px;}
        .loop-cropper-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.58);z-index:var(--loop-z-nested-modal-backdrop,5000);padding:6vh 16px;overflow:auto;}
        .loop-cropper-panel{max-width:920px;margin:0 auto;background:#fff;padding:16px;border-radius:22px;box-shadow:0 28px 90px rgba(15,23,42,.30);}
        .loop-cropper-panel h3{font-size:18px;font-weight:950;margin:0 0 12px;color:#0f172a;}
        .loop-settings-minimal.is-native{padding:12px 12px 40px;background:#f8fafc;}
        .loop-settings-minimal.is-native .loop-settings-layout{display:flex;flex-direction:column;gap:12px;}
        .loop-settings-minimal.is-native .loop-settings-nav{position:static;width:100%;border-radius:20px;padding:9px;}
        .loop-settings-minimal.is-native .loop-settings-nav-title{display:none;}
        .loop-settings-minimal.is-native .loop-settings-tabs{display:flex;gap:8px;overflow-x:auto;padding-bottom:2px;}
        .loop-settings-minimal.is-native .loop-settings-tab{min-width:max-content;border-radius:999px;padding:9px 12px;}
        .loop-settings-minimal.is-native .loop-settings-tab-icon{width:26px;height:26px;border-radius:999px;}
        .loop-settings-minimal.is-native .loop-settings-topbar{display:grid;gap:12px;}
        .loop-settings-minimal.is-native .loop-action-rail{position:static;width:100%;order:2;}
        .loop-settings-minimal.is-native .loop-settings-actions{justify-content:flex-start;}
        .loop-settings-minimal.is-native .loop-settings-content{padding:0;border:0;background:transparent;box-shadow:none;border-radius:0;}
        @media(max-width:900px){
          .loop-settings-minimal{padding:14px 12px 44px;}
          .loop-settings-layout{display:flex;flex-direction:column;gap:12px;}
          .loop-settings-nav{position:static;width:100%;border-radius:20px;padding:9px;}
          .loop-settings-nav-title{display:none;}
          .loop-settings-tabs{display:flex;gap:8px;overflow-x:auto;padding-bottom:2px;}
          .loop-settings-tab{min-width:max-content;border-radius:999px;padding:9px 12px;}
          .loop-settings-tab-icon{width:26px;height:26px;border-radius:999px;}
          .loop-settings-topbar{display:grid;gap:12px;}
          .loop-action-rail{position:static;width:100%;order:2;}
          .loop-settings-actions{justify-content:flex-start;}
          .loop-settings-content{padding:0;border:0;background:transparent;box-shadow:none;border-radius:0;}
        }



        @media(max-width:900px){
          .loop-settings-layout{grid-template-columns:1fr!important;width:100%!important;}
          .loop-settings-nav{position:static;width:100%;}
          .loop-nav-actions{display:none!important;}
          .loop-settings-layout > .loop-action-rail{display:grid!important;position:fixed!important;}
        }

        /* =========================================================
           LOOP MOBILE SAFE ACTION BAR — BUSINESS EDIT PHASE 1
           Keeps Guardar/Vista previa above Android/iOS navigation.
           ========================================================= */
        @media(max-width:900px){
          .loop-settings-minimal{
            --loop-safe-bottom:max(env(safe-area-inset-bottom,0px),34px);
            padding-bottom:calc(128px + var(--loop-safe-bottom))!important;
          }

          .loop-settings-minimal .loop-action-rail{
            position:fixed!important;
            left:12px!important;
            right:12px!important;
            bottom:calc(var(--loop-safe-bottom) + 10px)!important;
            z-index:var(--loop-z-sticky,500)!important;
            width:auto!important;
            order:initial!important;
            display:block!important;
            pointer-events:none!important;
          }

          .loop-settings-minimal .loop-action-card{
            pointer-events:auto!important;
            border-radius:24px!important;
            padding:8px!important;
            border:1px solid rgba(226,232,240,.94)!important;
            background:rgba(255,255,255,.95)!important;
            box-shadow:0 18px 48px rgba(15,23,42,.18)!important;
            backdrop-filter:blur(18px)!important;
            -webkit-backdrop-filter:blur(18px)!important;
          }

          .loop-settings-minimal .loop-action-profile-top,
          .loop-settings-minimal .loop-status-pill,
          .loop-settings-minimal .loop-action-progress{
            display:none!important;
          }

          .loop-settings-minimal .loop-action-buttons{
            display:grid!important;
            grid-template-columns:minmax(0,.72fr) minmax(0,1fr)!important;
            gap:8px!important;
            margin:0!important;
          }

          .loop-settings-minimal .loop-action-buttons .loop-preview-btn,
          .loop-settings-minimal .loop-action-buttons .loop-save-btn{
            width:100%!important;
            height:46px!important;
            min-height:46px!important;
            border-radius:16px!important;
            font-size:13.5px!important;
            margin:0!important;
          }

          .loop-settings-minimal .loop-settings-content{
            padding-bottom:18px!important;
          }
        }

        /* =========================================================
           LOOP MOBILE SAFE ACTIONS — PHASE 2
           Hardens business editor for Android/iOS.
           ========================================================= */
        @media(max-width:900px){
          .loop-settings-minimal{
            --loop-safe-top:max(env(safe-area-inset-top,0px),0px);
            --loop-safe-bottom:max(env(safe-area-inset-bottom,0px),34px);
            --loop-mobile-savebar-height:72px;
            padding-left:12px!important;
            padding-right:12px!important;
            padding-bottom:calc(176px + var(--loop-safe-bottom))!important;
            box-sizing:border-box!important;
            overflow-x:hidden!important;
          }

          .loop-settings-minimal .loop-settings-layout{
            padding-bottom:calc(118px + var(--loop-safe-bottom))!important;
          }

          .loop-settings-minimal .loop-settings-nav{
            position:sticky!important;
            top:calc(var(--loop-mobile-header-height,64px) + var(--loop-safe-top) + 8px)!important;
            z-index:120!important;
            border-radius:20px!important;
            background:rgba(255,255,255,.96)!important;
            backdrop-filter:blur(16px)!important;
            -webkit-backdrop-filter:blur(16px)!important;
            box-shadow:0 14px 34px rgba(15,23,42,.10)!important;
            border:1px solid rgba(226,232,240,.96)!important;
          }

          .loop-settings-minimal .loop-settings-tabs{
            scrollbar-width:none!important;
            -webkit-overflow-scrolling:touch!important;
            padding:1px 2px 3px!important;
          }

          .loop-settings-minimal .loop-settings-tabs::-webkit-scrollbar{display:none!important;}

          .loop-settings-minimal .loop-settings-tab{
            min-height:42px!important;
            box-shadow:none!important;
          }

          .loop-settings-minimal .loop-settings-content{
            padding-bottom:calc(128px + var(--loop-safe-bottom))!important;
            scroll-margin-bottom:calc(150px + var(--loop-safe-bottom))!important;
          }

          .loop-settings-minimal .loop-action-rail{
            position:fixed!important;
            left:10px!important;
            right:10px!important;
            bottom:calc(var(--loop-safe-bottom) + 10px)!important;
            z-index:var(--loop-z-sticky,500)!important;
            pointer-events:none!important;
            width:auto!important;
            max-width:560px!important;
            margin:0 auto!important;
          }

          .loop-settings-minimal .loop-action-card{
            pointer-events:auto!important;
            width:100%!important;
            border-radius:24px!important;
            padding:7px!important;
            background:rgba(255,255,255,.96)!important;
            border:1px solid rgba(226,232,240,.96)!important;
            box-shadow:0 18px 48px rgba(15,23,42,.20)!important;
            backdrop-filter:blur(18px)!important;
            -webkit-backdrop-filter:blur(18px)!important;
            box-sizing:border-box!important;
          }

          .loop-settings-minimal .loop-action-buttons{
            display:grid!important;
            grid-template-columns:minmax(0,.72fr) minmax(0,1fr)!important;
            gap:8px!important;
            width:100%!important;
            margin:0!important;
          }

          .loop-settings-minimal .loop-action-buttons .loop-preview-btn,
          .loop-settings-minimal .loop-action-buttons .loop-save-btn{
            width:100%!important;
            min-width:0!important;
            height:48px!important;
            min-height:48px!important;
            max-height:48px!important;
            border-radius:17px!important;
            font-size:13.5px!important;
            font-weight:950!important;
            margin:0!important;
            white-space:nowrap!important;
          }

          .loop-settings-minimal .loop-action-buttons .loop-save-btn{
            box-shadow:0 12px 26px rgba(0,103,95,.20)!important;
          }

          .loop-cropper-backdrop{
            z-index:var(--loop-z-nested-modal-backdrop,5000)!important;
            padding:calc(14px + env(safe-area-inset-top,0px)) 12px calc(18px + var(--loop-safe-bottom))!important;
            box-sizing:border-box!important;
          }

          .loop-cropper-panel{
            max-height:calc(100dvh - var(--loop-safe-bottom) - var(--loop-safe-top) - 32px)!important;
            overflow:auto!important;
            -webkit-overflow-scrolling:touch!important;
          }
        }

        @media(max-width:380px){
          .loop-settings-minimal .loop-action-rail{left:8px!important;right:8px!important;}
          .loop-settings-minimal .loop-action-buttons{grid-template-columns:1fr 1.18fr!important;gap:7px!important;}
          .loop-settings-minimal .loop-action-buttons .loop-preview-btn,
          .loop-settings-minimal .loop-action-buttons .loop-save-btn{
            height:46px!important;
            min-height:46px!important;
            font-size:13px!important;
          }
        }



        /* =========================================================
           LOOP D2.9.4I.2 — Premium minimal business settings sidebar
           ========================================================= */
        .loop-settings-minimal:not(.is-native) .loop-settings-layout{
          grid-template-columns:320px minmax(0,1fr)!important;
          gap:22px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav{
          padding:18px!important;
          border-radius:34px!important;
          background:linear-gradient(180deg,rgba(255,255,255,.97),rgba(255,253,249,.92))!important;
          box-shadow:0 24px 70px rgba(15,23,42,.08)!important;
          border:1px solid rgba(214,226,234,.95)!important;
          gap:20px!important;
          overflow:hidden!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-nav-actions{
          order:-5!important;
          display:grid!important;
          gap:0!important;
        }
        .loop-nav-business-card{
          border:0!important;
          border-radius:28px!important;
          padding:0 0 20px!important;
          background:transparent!important;
          box-shadow:none!important;
          display:grid!important;
          gap:14px!important;
          border-bottom:1px solid rgba(226,232,240,.92)!important;
        }
        .loop-nav-business-row{
          display:grid;
          grid-template-columns:76px minmax(0,1fr);
          align-items:center;
          gap:14px;
        }
        .loop-nav-business-logo{
          width:72px;
          height:72px;
          border-radius:22px;
          display:grid;
          place-items:center;
          overflow:hidden;
          background:linear-gradient(135deg,#00463f,#008474);
          box-shadow:0 18px 34px rgba(0,103,95,.14);
          color:#fff;
          font-size:20px;
          font-weight:950;
          position:relative;
        }
        .loop-nav-business-logo::after{
          content:"";
          position:absolute;
          right:-2px;
          bottom:-2px;
          width:18px;
          height:18px;
          border-radius:999px;
          background:#f59e0b;
          border:3px solid #fff;
          box-shadow:0 6px 14px rgba(245,158,11,.18);
        }
        .loop-nav-business-logo img{width:100%;height:100%;object-fit:cover;display:block;}
        .loop-nav-business-copy{min-width:0;}
        .loop-nav-business-copy strong{
          display:block;
          font-size:19px;
          line-height:1.1;
          letter-spacing:-.045em;
          font-weight:950;
          color:#0f172a;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .loop-nav-business-copy span{
          display:block;
          margin-top:7px;
          color:#64748b;
          font-size:13px;
          line-height:1.25;
          font-weight:760;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .loop-nav-status{
          justify-self:start;
          min-height:42px;
          border-radius:999px;
          padding:0 18px;
          display:inline-flex;
          align-items:center;
          gap:9px;
          font-size:14px;
          font-weight:950;
          color:#047857;
          border:1px solid #bbf7d0;
          background:#ecfdf5;
        }
        .loop-nav-status.paused{color:#c2410c;border-color:#fed7aa;background:#fff7ed;}
        .loop-nav-status i{
          width:18px;height:18px;border-radius:999px;display:inline-grid;place-items:center;background:rgba(255,255,255,.82);
          box-shadow:inset 0 0 0 1px rgba(15,23,42,.04);
        }
        .loop-nav-status i::before{content:"Ⅱ";font-size:10px;line-height:1;}
        .loop-nav-status:not(.paused) i::before{content:"✓";font-size:11px;}
        .loop-nav-save-main{
          width:100%;
          min-height:62px;
          border:0;
          border-radius:20px;
          background:linear-gradient(135deg,#005447,#008b78);
          color:#fff;
          display:flex;
          align-items:center;
          justify-content:center;
          gap:14px;
          font-size:20px;
          line-height:1;
          font-weight:950;
          letter-spacing:-.03em;
          cursor:pointer;
          box-shadow:0 20px 38px rgba(0,103,95,.20);
        }
        .loop-nav-save-main:disabled{opacity:.68;cursor:not-allowed;}
        .loop-nav-save-icon{font-size:21px;line-height:1;opacity:.95;}
        .loop-nav-save-note{
          display:flex;
          align-items:center;
          gap:10px;
          min-height:26px;
          color:#708197;
          font-size:13px;
          font-weight:790;
        }
        .loop-nav-save-note span{
          width:22px;height:22px;border-radius:999px;display:grid;place-items:center;
          color:#00977f;background:#e6fbf5;border:1px solid rgba(0,103,95,.12);
          font-size:13px;font-weight:950;flex:0 0 auto;
        }
        .loop-nav-save-note em{font-style:normal;}
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-title{
          order:-4!important;
          margin:0!important;
          padding:0!important;
          border:0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-title strong{
          font-size:22px!important;
          line-height:1.05!important;
          letter-spacing:-.045em!important;
          color:#0f172a!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-title span{
          margin-top:9px!important;
          max-width:250px!important;
          color:#64748b!important;
          font-size:14px!important;
          line-height:1.42!important;
          font-weight:760!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tabs{
          display:grid!important;
          gap:12px!important;
          padding:0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab{
          position:relative!important;
          min-height:78px!important;
          border-radius:22px!important;
          padding:14px 44px 14px 18px!important;
          gap:16px!important;
          border:1px solid rgba(226,232,240,.95)!important;
          background:rgba(255,255,255,.94)!important;
          box-shadow:0 10px 24px rgba(15,23,42,.035)!important;
          color:#0f172a!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab:hover{
          transform:translateY(-1px);
          border-color:rgba(0,103,95,.18)!important;
          box-shadow:0 16px 34px rgba(15,23,42,.06)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab::before{
          content:"";
          position:absolute;left:0;top:16px;bottom:16px;width:4px;border-radius:999px;background:transparent;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab::after{
          content:"›";
          position:absolute;right:18px;top:50%;transform:translateY(-50%);
          color:#0f172a;font-size:30px;line-height:1;font-weight:500;opacity:.82;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab.is-active{
          background:linear-gradient(90deg,rgba(0,103,95,.105),rgba(240,253,250,.78))!important;
          border-color:rgba(0,103,95,.16)!important;
          box-shadow:inset 4px 0 0 #00675f, 0 16px 36px rgba(0,103,95,.075)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab.is-active::before{background:#00675f;}
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon{
          width:52px!important;
          height:52px!important;
          border-radius:18px!important;
          background:rgba(0,103,95,.065)!important;
          color:transparent!important;
          display:grid!important;
          place-items:center!important;
          flex:0 0 auto!important;
          box-shadow:none!important;
          position:relative!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon::after{
          color:#00675f;
          font-size:24px;
          line-height:1;
          font-weight:900;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon.icon-home::after{content:"⌂";}
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon.icon-grid::after{content:"▦";}
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon.icon-clock::after{content:"◷";}
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon.icon-bell::after{content:"◉";}
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon.icon-star::after{content:"☆";}
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon.icon-crown::after{content:"♕";}
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon.icon-diamond::after{content:"◇";}
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon.icon-users::after{content:"♟";}
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon.icon-user::after{content:"⊙";}
        .loop-settings-minimal:not(.is-native) .loop-settings-tab.is-active .loop-settings-tab-icon{
          background:#ffffff!important;
          box-shadow:0 12px 24px rgba(0,103,95,.10)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-copy strong{
          font-size:20px!important;
          line-height:1.12!important;
          letter-spacing:-.045em!important;
          color:#0f172a!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-copy span{
          display:block!important;
          margin-top:6px!important;
          font-size:14px!important;
          color:#64748b!important;
          font-weight:720!important;
          line-height:1.28!important;
          white-space:normal!important;
          overflow:visible!important;
          text-overflow:clip!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab.is-active .loop-settings-tab-copy strong,
        .loop-settings-minimal:not(.is-native) .loop-settings-tab.is-active .loop-settings-tab-copy span{color:#00675f!important;}
        .loop-settings-privacy-card{
          order:20!important;
          margin-top:8px;
          border-radius:24px;
          padding:18px;
          display:grid;
          grid-template-columns:52px minmax(0,1fr);
          gap:14px;
          align-items:center;
          background:linear-gradient(135deg,rgba(240,253,250,.96),rgba(236,248,244,.86));
          border:1px solid rgba(0,103,95,.10);
        }
        .loop-settings-privacy-icon{
          width:52px;height:52px;border-radius:18px;background:#fff;display:grid;place-items:center;color:#00675f;font-size:22px;font-weight:950;
          box-shadow:0 10px 24px rgba(0,103,95,.07);
        }
        .loop-settings-privacy-icon::before{content:"◈";}
        .loop-settings-privacy-card strong{display:block;color:#00675f;font-size:18px;font-weight:950;letter-spacing:-.035em;}
        .loop-settings-privacy-card p{margin:6px 0 0;color:#0f766e;font-size:14px;font-weight:720;line-height:1.35;}
        .loop-settings-minimal:not(.is-native) .loop-preview-btn{display:none!important;}
        .loop-settings-minimal:not(.is-native) .loop-action-buttons{grid-template-columns:1fr!important;}
        @media(max-width:900px){
          .loop-settings-minimal .loop-preview-btn{display:none!important;}
          .loop-settings-minimal .loop-action-buttons{grid-template-columns:1fr!important;}
        }


        /* LOOP D2.9.4I.3 — compact exact sidebar sizing + real line icons */
        .loop-settings-minimal:not(.is-native) .loop-settings-layout{
          grid-template-columns:278px minmax(0,1fr)!important;
          gap:18px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav{
          padding:14px!important;
          border-radius:28px!important;
          gap:14px!important;
          background:linear-gradient(180deg,rgba(255,255,255,.96),rgba(255,253,248,.90))!important;
          box-shadow:0 20px 54px rgba(15,23,42,.07)!important;
        }
        .loop-nav-business-card{
          padding:0 0 14px!important;
          gap:10px!important;
          border-bottom:1px solid rgba(226,232,240,.82)!important;
        }
        .loop-nav-business-row{
          grid-template-columns:58px minmax(0,1fr)!important;
          gap:12px!important;
        }
        .loop-nav-business-logo{
          width:56px!important;
          height:56px!important;
          border-radius:18px!important;
          box-shadow:0 12px 24px rgba(0,103,95,.12)!important;
        }
        .loop-nav-business-logo::after{
          width:14px!important;
          height:14px!important;
          border-width:2px!important;
          right:-1px!important;
          bottom:-1px!important;
        }
        .loop-nav-business-copy strong{
          font-size:16px!important;
          line-height:1.08!important;
          letter-spacing:-.04em!important;
        }
        .loop-nav-business-copy span{
          margin-top:5px!important;
          font-size:12px!important;
          font-weight:730!important;
        }
        .loop-nav-status{
          min-height:32px!important;
          padding:0 13px!important;
          gap:7px!important;
          font-size:12px!important;
          background:#fff7ed!important;
          border-color:#fed7aa!important;
          color:#c2410c!important;
        }
        .loop-nav-status:not(.paused){
          background:#ecfdf5!important;
          border-color:#bbf7d0!important;
          color:#047857!important;
        }
        .loop-nav-status i{
          width:16px!important;
          height:16px!important;
        }
        .loop-nav-save-main{
          min-height:48px!important;
          border-radius:18px!important;
          font-size:17px!important;
          gap:10px!important;
          box-shadow:0 15px 28px rgba(0,103,95,.18)!important;
        }
        .loop-nav-save-icon{
          display:inline-grid!important;
          place-items:center!important;
          font-size:0!important;
          line-height:0!important;
        }
        .loop-nav-save-note{
          gap:8px!important;
          min-height:22px!important;
          font-size:12px!important;
        }
        .loop-nav-save-note span{
          width:19px!important;
          height:19px!important;
          font-size:11px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-title{
          padding:2px 0 0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-title strong{
          font-size:19px!important;
          line-height:1.08!important;
          letter-spacing:-.04em!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-title span{
          margin-top:7px!important;
          max-width:224px!important;
          font-size:12.5px!important;
          line-height:1.42!important;
          font-weight:700!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tabs{
          gap:9px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab{
          min-height:58px!important;
          border-radius:19px!important;
          padding:10px 36px 10px 12px!important;
          gap:12px!important;
          box-shadow:0 10px 26px rgba(15,23,42,.035)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab::after{
          right:16px!important;
          font-size:22px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon{
          width:40px!important;
          height:40px!important;
          border-radius:15px!important;
          background:rgba(0,103,95,.065)!important;
          color:#007467!important;
          box-shadow:none!important;
          font-size:0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon::after{content:none!important;}
        .loop-settings-minimal:not(.is-native) .loop-settings-tab.is-active .loop-settings-tab-icon{
          background:#ffffff!important;
          color:#00675f!important;
          box-shadow:0 10px 20px rgba(0,103,95,.10)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-copy{
          min-width:0!important;
          flex:1 1 auto!important;
          overflow:visible!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-copy strong{
          font-size:16px!important;
          line-height:1.12!important;
          letter-spacing:-.035em!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-tab-copy span{
          display:block!important;
          margin-top:4px!important;
          font-size:9.6px!important;
          line-height:1.05!important;
          font-weight:760!important;
          letter-spacing:-.045em!important;
          white-space:nowrap!important;
          overflow:hidden!important;
          text-overflow:ellipsis!important;
          max-width:100%!important;
        }
        .loop-settings-privacy-card{
          margin-top:2px!important;
          border-radius:20px!important;
          padding:14px!important;
          grid-template-columns:42px minmax(0,1fr)!important;
          gap:12px!important;
        }
        .loop-settings-privacy-icon{
          width:42px!important;
          height:42px!important;
          border-radius:15px!important;
          color:#00675f!important;
          font-size:0!important;
        }
        .loop-settings-privacy-icon::before{content:none!important;}
        .loop-settings-privacy-card strong{
          font-size:15px!important;
          letter-spacing:-.025em!important;
        }
        .loop-settings-privacy-card p{
          margin-top:4px!important;
          font-size:12.5px!important;
          line-height:1.34!important;
        }


        /* =========================================================
           LOOP D2.9.4J.1 — grouped left sidebar, closer to new reference
           ========================================================= */
        .loop-settings-minimal:not(.is-native){
          background:#fbfaf7!important;
          padding:22px 18px 48px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-layout{
          grid-template-columns:236px minmax(0,1fr)!important;
          gap:22px!important;
          align-items:start!important;
        }
        .loop-settings-nav-v2{
          width:236px!important;
          padding:14px!important;
          border-radius:22px!important;
          background:rgba(255,255,255,.96)!important;
          box-shadow:0 18px 55px rgba(15,23,42,.07)!important;
          border:1px solid rgba(226,232,240,.92)!important;
          gap:14px!important;
        }
        .loop-sidebar-brand{padding:0 4px 4px!important;}
        .loop-sidebar-brand strong{display:block;color:#00675f;font-size:24px;font-weight:850;letter-spacing:-.08em;line-height:1;}
        .loop-settings-nav-v2 .loop-settings-nav-title{display:none!important;}
        .loop-settings-nav-v2 .loop-nav-actions{order:0!important;display:block!important;}
        .loop-settings-nav-v2 .loop-nav-business-card{
          border:1px solid rgba(226,232,240,.95)!important;
          border-radius:18px!important;
          padding:10px!important;
          background:#fff!important;
          box-shadow:0 12px 30px rgba(15,23,42,.045)!important;
        }
        .loop-settings-nav-v2 .loop-nav-business-row{align-items:center!important;gap:10px!important;}
        .loop-settings-nav-v2 .loop-nav-business-logo{width:48px!important;height:48px!important;border-radius:14px!important;}
        .loop-settings-nav-v2 .loop-nav-business-copy strong{font-size:12.5px!important;font-weight:950!important;letter-spacing:-.025em!important;}
        .loop-settings-nav-v2 .loop-nav-business-copy span{font-size:10.5px!important;font-weight:760!important;color:#64748b!important;}
        .loop-settings-nav-v2 .loop-nav-status{height:24px!important;margin-top:8px!important;font-size:10px!important;justify-content:center!important;width:100%!important;}
        .loop-settings-nav-v2 .loop-nav-save-main{display:none!important;}
        .loop-settings-nav-v2 .loop-nav-last-saved{display:none!important;}
        .loop-settings-grouped-tabs{display:grid!important;gap:13px!important;}
        .loop-settings-group{display:grid!important;gap:6px!important;}
        .loop-settings-group-label{font-size:9px!important;letter-spacing:.14em!important;color:#94a3b8!important;font-weight:950!important;text-transform:uppercase!important;padding:0 8px!important;}
        .loop-settings-nav-v2 .loop-settings-tabs{display:grid!important;gap:2px!important;padding:0!important;}
        .loop-settings-nav-v2 .loop-settings-tab{
          min-height:31px!important;
          height:31px!important;
          border-radius:9px!important;
          padding:5px 8px!important;
          gap:8px!important;
          background:transparent!important;
          box-shadow:none!important;
        }
        .loop-settings-nav-v2 .loop-settings-tab:hover{background:#f8fafc!important;}
        .loop-settings-nav-v2 .loop-settings-tab.is-active{
          background:linear-gradient(90deg,rgba(0,103,95,.13),rgba(0,103,95,.06))!important;
          color:#00675f!important;
        }
        .loop-settings-nav-v2 .loop-settings-tab-icon{
          width:21px!important;height:21px!important;border-radius:7px!important;background:transparent!important;color:#334155!important;box-shadow:none!important;
        }
        .loop-settings-nav-v2 .loop-settings-tab.is-active .loop-settings-tab-icon{background:transparent!important;color:#00675f!important;box-shadow:none!important;}
        .loop-settings-nav-v2 .loop-settings-tab-copy strong{font-size:10.5px!important;font-weight:900!important;letter-spacing:-.015em!important;line-height:1!important;}
        .loop-settings-nav-v2 .loop-settings-tab-copy span{display:none!important;}
        .loop-settings-tab-arrow{margin-left:auto;color:#334155;font-size:17px;font-weight:600;line-height:1;opacity:.9;}
        .loop-settings-nav-v2 .loop-settings-tab.is-active .loop-settings-tab-arrow{color:#00675f;}
        .loop-premium-card{
          border:0!important;width:100%;border-radius:13px!important;padding:10px!important;display:flex!important;align-items:center!important;gap:9px!important;text-align:left!important;cursor:pointer!important;
          background:linear-gradient(135deg,#fff7ed,#fffaf2)!important;color:#9a5a14!important;box-shadow:inset 0 0 0 1px rgba(251,191,36,.22)!important;
        }
        .loop-premium-card span{width:22px;height:22px;border-radius:8px;display:grid;place-items:center;background:#fff;color:#b45309;font-size:12px;}
        .loop-premium-card div{min-width:0;display:grid;gap:1px;}
        .loop-premium-card strong{font-size:10.5px;font-weight:950;color:#9a5a14;}
        .loop-premium-card em{font-style:normal;font-size:9.5px;font-weight:800;color:#b45309;}
        .loop-premium-card b{margin-left:auto;font-size:16px;}
        .loop-settings-privacy-card{display:none!important;}



        /* =========================================================
           LOOP D2.9.4J.2 — exact compact business OS sidebar match
           ========================================================= */
        .loop-settings-minimal:not(.is-native){
          background:#f7f8f7!important;
          padding:20px 16px 48px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-layout{
          grid-template-columns:184px minmax(0,1fr)!important;
          gap:24px!important;
        }
        .loop-settings-nav-v2{
          width:184px!important;
          min-height:auto!important;
          padding:0!important;
          border:0!important;
          border-radius:0!important;
          background:transparent!important;
          box-shadow:none!important;
          gap:12px!important;
          align-content:start!important;
          overflow:visible!important;
        }
        .loop-sidebar-brand{
          padding:0 0 9px 10px!important;
        }
        .loop-sidebar-brand strong{
          color:#00675f!important;
          font-size:21px!important;
          font-weight:820!important;
          letter-spacing:-.075em!important;
          line-height:1!important;
        }
        .loop-settings-nav-v2 .loop-nav-actions,
        .loop-settings-nav-v2 .loop-settings-grouped-tabs,
        .loop-settings-nav-v2 .loop-premium-card{
          width:100%!important;
        }
        .loop-settings-nav-v2 .loop-nav-actions{
          display:block!important;
          margin:0!important;
          padding:0!important;
        }
        .loop-settings-nav-v2 .loop-nav-business-card{
          width:100%!important;
          border:0!important;
          border-radius:12px!important;
          padding:9px!important;
          background:#ffffff!important;
          box-shadow:0 13px 35px rgba(15,23,42,.055)!important;
          text-align:left!important;
          display:grid!important;
          gap:9px!important;
          cursor:pointer!important;
        }
        .loop-settings-nav-v2 .loop-nav-business-row{
          display:grid!important;
          grid-template-columns:42px minmax(0,1fr) 10px!important;
          align-items:center!important;
          gap:8px!important;
          padding:0!important;
          border:0!important;
        }
        .loop-settings-nav-v2 .loop-nav-business-logo{
          width:42px!important;
          height:42px!important;
          border-radius:11px!important;
          box-shadow:none!important;
          background:#f1f5f9!important;
          overflow:hidden!important;
          position:relative!important;
        }
        .loop-settings-nav-v2 .loop-nav-business-logo::after{
          content:"";
          position:absolute;
          width:8px;
          height:8px;
          right:-1px;
          bottom:-1px;
          border-radius:999px;
          border:2px solid #fff;
          background:#f59e0b;
        }
        .loop-settings-nav-v2 .loop-nav-business-logo img{width:100%!important;height:100%!important;object-fit:cover!important;display:block!important;}
        .loop-settings-nav-v2 .loop-nav-business-copy{min-width:0!important;display:grid!important;gap:1px!important;}
        .loop-settings-nav-v2 .loop-nav-business-copy strong{
          color:#0f172a!important;
          font-size:9.6px!important;
          font-weight:950!important;
          letter-spacing:-.02em!important;
          line-height:1.1!important;
          white-space:nowrap!important;
          overflow:hidden!important;
          text-overflow:ellipsis!important;
        }
        .loop-settings-nav-v2 .loop-nav-business-copy span{
          color:#64748b!important;
          font-size:8.2px!important;
          font-weight:760!important;
          line-height:1.1!important;
          white-space:nowrap!important;
          overflow:hidden!important;
          text-overflow:ellipsis!important;
        }
        .loop-nav-business-arrow{color:#0f172a!important;font-size:14px!important;line-height:1!important;font-weight:650!important;opacity:.8;}
        .loop-nav-readiness{display:grid!important;gap:5px!important;padding:0 1px!important;}
        .loop-nav-readiness-row{display:flex!important;align-items:center!important;justify-content:space-between!important;}
        .loop-nav-readiness-row span{color:#334155!important;font-size:7.8px!important;font-weight:780!important;line-height:1!important;}
        .loop-nav-readiness-track{height:3px!important;border-radius:999px!important;background:#e2e8f0!important;overflow:hidden!important;}
        .loop-nav-readiness-track i{display:block!important;height:100%!important;max-width:100%!important;border-radius:999px!important;background:#007467!important;}
        .loop-settings-grouped-tabs{
          display:grid!important;
          gap:9px!important;
          padding:0 6px!important;
          border-radius:14px!important;
          background:#fff!important;
          box-shadow:0 13px 35px rgba(15,23,42,.055)!important;
          padding-top:11px!important;
          padding-bottom:11px!important;
        }
        .loop-settings-group{display:grid!important;gap:2px!important;}
        .loop-settings-group-label{
          padding:0 6px 3px!important;
          color:#94a3b8!important;
          font-size:7.4px!important;
          font-weight:880!important;
          letter-spacing:.06em!important;
          text-transform:uppercase!important;
          line-height:1!important;
        }
        .loop-settings-nav-v2 .loop-settings-tabs{display:grid!important;gap:1px!important;padding:0!important;}
        .loop-settings-nav-v2 .loop-settings-tab{
          min-height:24px!important;
          height:24px!important;
          border-radius:6px!important;
          padding:4px 6px!important;
          gap:6px!important;
          background:transparent!important;
          color:#0f172a!important;
          box-shadow:none!important;
          border:0!important;
        }
        .loop-settings-nav-v2 .loop-settings-tab:hover{background:#f8fafc!important;}
        .loop-settings-nav-v2 .loop-settings-tab.is-active{
          background:linear-gradient(90deg,rgba(0,103,95,.12),rgba(0,103,95,.04))!important;
          color:#00675f!important;
        }
        .loop-settings-nav-v2 .loop-settings-tab-icon{
          width:13px!important;
          height:13px!important;
          min-width:13px!important;
          border-radius:0!important;
          background:transparent!important;
          color:#334155!important;
          box-shadow:none!important;
        }
        .loop-settings-nav-v2 .loop-settings-tab-icon svg{width:12px!important;height:12px!important;stroke-width:1.85!important;}
        .loop-settings-nav-v2 .loop-settings-tab.is-active .loop-settings-tab-icon{color:#007467!important;background:transparent!important;box-shadow:none!important;}
        .loop-settings-nav-v2 .loop-settings-tab-copy{min-width:0!important;}
        .loop-settings-nav-v2 .loop-settings-tab-copy strong{
          color:inherit!important;
          font-size:8.6px!important;
          font-weight:820!important;
          letter-spacing:-.01em!important;
          line-height:1!important;
          white-space:nowrap!important;
          overflow:hidden!important;
          text-overflow:ellipsis!important;
        }
        .loop-settings-tab-arrow{display:none!important;}
        .loop-premium-card{
          width:100%!important;
          min-height:42px!important;
          border-radius:8px!important;
          padding:8px!important;
          gap:7px!important;
          background:linear-gradient(135deg,#fff7ed,#fff3df)!important;
          box-shadow:inset 0 0 0 1px rgba(251,191,36,.17),0 12px 26px rgba(146,64,14,.05)!important;
        }
        .loop-premium-card span{
          width:17px!important;height:17px!important;border-radius:6px!important;background:#fff8eb!important;color:#b45309!important;font-size:9px!important;
        }
        .loop-premium-card strong{font-size:8.8px!important;line-height:1.05!important;color:#9a5a14!important;font-weight:900!important;}
        .loop-premium-card em{font-size:7.8px!important;line-height:1!important;color:#b45309!important;font-weight:760!important;}
        .loop-premium-card b{font-size:13px!important;color:#9a5a14!important;}
        .loop-nav-status,.loop-nav-save-main,.loop-nav-last-saved,.loop-nav-save-note,.loop-settings-privacy-card{display:none!important;}
        @media(max-width:980px){
          .loop-settings-minimal:not(.is-native) .loop-settings-layout{grid-template-columns:1fr!important;}
          .loop-settings-nav-v2{position:relative!important;top:auto!important;width:100%!important;}
        }

      

        /* =========================================================
           LOOP D2.9.4J.3 — sidebar true exact scale override
           Important: these selectors intentionally beat older F/I rules.
           ========================================================= */
        .loop-settings-minimal:not(.is-native){
          background:#f7f8f7!important;
          padding:18px 14px 48px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-layout{
          grid-template-columns:176px minmax(0,1fr)!important;
          gap:22px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2{
          width:176px!important;
          min-width:176px!important;
          padding:0!important;
          border:0!important;
          border-radius:0!important;
          background:transparent!important;
          box-shadow:none!important;
          display:grid!important;
          gap:10px!important;
          align-content:start!important;
          overflow:visible!important;
          position:sticky!important;
          top:84px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-sidebar-brand{
          padding:0 0 8px 10px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-sidebar-brand strong{
          font-size:21px!important;
          font-weight:820!important;
          letter-spacing:-.075em!important;
          line-height:.95!important;
          color:#00675f!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-card{
          width:100%!important;
          border:0!important;
          border-radius:12px!important;
          padding:9px!important;
          background:#fff!important;
          box-shadow:0 12px 32px rgba(15,23,42,.055)!important;
          display:grid!important;
          gap:8px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-row{
          display:grid!important;
          grid-template-columns:40px minmax(0,1fr) 9px!important;
          gap:8px!important;
          align-items:center!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-logo{
          width:40px!important;
          height:40px!important;
          border-radius:11px!important;
          box-shadow:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-copy strong{
          font-size:9.5px!important;
          font-weight:900!important;
          line-height:1.05!important;
          color:#0f172a!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-copy span{
          font-size:7.9px!important;
          font-weight:720!important;
          line-height:1.05!important;
          color:#64748b!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-arrow{
          font-size:12px!important;
          font-weight:600!important;
          color:#0f172a!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-readiness{
          gap:5px!important;
          padding:0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-readiness-row span{
          font-size:7.5px!important;
          font-weight:760!important;
          color:#334155!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-readiness-track{
          height:3px!important;
          border-radius:999px!important;
          background:#e4e8ea!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-grouped-tabs{
          width:100%!important;
          display:grid!important;
          gap:9px!important;
          padding:11px 6px!important;
          border-radius:13px!important;
          background:#fff!important;
          box-shadow:0 12px 32px rgba(15,23,42,.055)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-group{
          display:grid!important;
          gap:2px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-group-label{
          padding:0 6px 3px!important;
          color:#95a1ad!important;
          font-size:7.2px!important;
          font-weight:850!important;
          letter-spacing:.065em!important;
          text-transform:uppercase!important;
          line-height:1!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tabs{
          display:grid!important;
          gap:1px!important;
          padding:0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab{
          min-height:23px!important;
          height:23px!important;
          border-radius:6px!important;
          padding:3px 6px!important;
          gap:6px!important;
          border:0!important;
          background:transparent!important;
          box-shadow:none!important;
          color:#172033!important;
          display:flex!important;
          align-items:center!important;
          text-align:left!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab:hover{
          background:#f6f8f8!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab.is-active{
          color:#00675f!important;
          background:linear-gradient(90deg,rgba(0,103,95,.12),rgba(0,103,95,.045))!important;
          box-shadow:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-icon{
          width:13px!important;
          height:13px!important;
          min-width:13px!important;
          border-radius:0!important;
          background:transparent!important;
          color:#334155!important;
          box-shadow:none!important;
          display:grid!important;
          place-items:center!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-icon svg{
          width:11px!important;
          height:11px!important;
          stroke-width:1.72!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab.is-active .loop-settings-tab-icon{
          color:#00675f!important;
          background:transparent!important;
          box-shadow:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-copy{
          min-width:0!important;
          display:block!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-copy strong{
          display:block!important;
          color:inherit!important;
          font-size:8.35px!important;
          font-weight:760!important;
          letter-spacing:-.006em!important;
          line-height:1!important;
          white-space:nowrap!important;
          overflow:hidden!important;
          text-overflow:ellipsis!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-copy span,
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-arrow{
          display:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card{
          width:100%!important;
          min-height:40px!important;
          border-radius:8px!important;
          padding:8px!important;
          gap:7px!important;
          background:linear-gradient(135deg,#fff7ed,#fff2dc)!important;
          box-shadow:inset 0 0 0 1px rgba(251,191,36,.16),0 10px 22px rgba(146,64,14,.045)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card span{
          width:16px!important;
          height:16px!important;
          border-radius:6px!important;
          font-size:8.5px!important;
          background:#fff8eb!important;
          color:#b45309!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card strong{
          font-size:8.4px!important;
          font-weight:850!important;
          line-height:1.05!important;
          color:#9a5a14!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card em{
          font-size:7.3px!important;
          font-weight:720!important;
          line-height:1!important;
          color:#b45309!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card b{
          font-size:12px!important;
          color:#9a5a14!important;
        }


        /* =========================================================
           LOOP D2.9.4J.4 — sidebar harmony scale override
           Bigger than J.3, aligned with General Section proportion.
           ========================================================= */
        .loop-settings-minimal:not(.is-native){
          padding:24px 18px 54px!important;
          background:linear-gradient(180deg,#faf8f3 0%,#ffffff 38%,#f8fafc 100%)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-layout{
          grid-template-columns:318px minmax(0,1fr)!important;
          gap:24px!important;
          align-items:start!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2{
          width:292px!important;
          min-width:292px!important;
          max-width:292px!important;
          padding:0!important;
          gap:14px!important;
          top:84px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-sidebar-brand{
          padding:2px 0 4px 18px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-sidebar-brand strong{
          font-size:32px!important;
          font-weight:780!important;
          letter-spacing:-.08em!important;
          line-height:.9!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-card{
          border-radius:22px!important;
          padding:18px!important;
          gap:14px!important;
          background:#fff!important;
          box-shadow:0 18px 46px rgba(15,23,42,.07)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-row{
          grid-template-columns:64px minmax(0,1fr) 16px!important;
          gap:14px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-logo{
          width:64px!important;
          height:64px!important;
          border-radius:18px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-logo::after{
          width:12px!important;
          height:12px!important;
          right:-1px!important;
          bottom:-1px!important;
          border-width:3px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-copy strong{
          font-size:15px!important;
          font-weight:900!important;
          line-height:1.05!important;
          letter-spacing:-.03em!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-copy span{
          font-size:12px!important;
          font-weight:720!important;
          line-height:1.15!important;
          margin-top:4px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-arrow{
          font-size:22px!important;
          opacity:.76!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-readiness{
          gap:9px!important;
          padding-top:2px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-readiness-row span{
          font-size:12px!important;
          font-weight:780!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-readiness-track{
          height:6px!important;
          background:#e6eceb!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-grouped-tabs{
          border-radius:22px!important;
          padding:18px!important;
          gap:20px!important;
          box-shadow:0 18px 46px rgba(15,23,42,.06)!important;
          background:rgba(255,255,255,.96)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-group{
          gap:6px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-group-label{
          padding:0 0 6px!important;
          font-size:10.5px!important;
          font-weight:820!important;
          letter-spacing:.08em!important;
          color:#8795a5!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tabs{
          gap:2px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab{
          min-height:38px!important;
          height:38px!important;
          border-radius:10px!important;
          padding:0 9px!important;
          gap:12px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab.is-active{
          background:linear-gradient(90deg,rgba(0,103,95,.13),rgba(0,103,95,.055))!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-icon{
          width:18px!important;
          height:18px!important;
          min-width:18px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-icon svg{
          width:16px!important;
          height:16px!important;
          stroke-width:1.75!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-copy strong{
          font-size:13px!important;
          font-weight:760!important;
          line-height:1!important;
          letter-spacing:-.018em!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card{
          min-height:72px!important;
          border-radius:18px!important;
          padding:15px 16px!important;
          gap:12px!important;
          background:linear-gradient(135deg,#fff7ed,#fff2dc)!important;
          box-shadow:inset 0 0 0 1px rgba(251,191,36,.18),0 18px 38px rgba(146,64,14,.07)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card span{
          width:25px!important;
          height:25px!important;
          border-radius:9px!important;
          font-size:13px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card strong{
          font-size:13px!important;
          line-height:1.05!important;
          font-weight:850!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card em{
          font-size:11px!important;
          line-height:1.15!important;
          font-weight:700!important;
          margin-top:3px!important;
          display:block!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card b{
          font-size:21px!important;
        }
        @media(max-width:1180px){
          .loop-settings-minimal:not(.is-native) .loop-settings-layout{
            grid-template-columns:286px minmax(0,1fr)!important;
            gap:18px!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2{
            width:264px!important;min-width:264px!important;max-width:264px!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-copy strong{font-size:12px!important;}
        }

        /* =========================================================
           LOOP D2.9.4J.5 — premium unified sidebar exact polish
           - Business card, menu and premium card live inside one panel
           - Removes active dot, chevrons/arrows and orange avatar marker
           - Icons stay as clean line icons like the visual reference
           ========================================================= */
        .loop-settings-minimal:not(.is-native) .loop-settings-layout{
          grid-template-columns:300px minmax(0,1fr)!important;
          gap:24px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2{
          width:272px!important;
          min-width:272px!important;
          max-width:272px!important;
          padding:16px!important;
          border-radius:28px!important;
          background:rgba(255,255,255,.96)!important;
          border:1px solid rgba(226,232,240,.88)!important;
          box-shadow:0 22px 58px rgba(15,23,42,.075)!important;
          gap:16px!important;
          overflow:hidden!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-actions{
          margin:0!important;
          padding:0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-card{
          margin:0!important;
          padding:0 0 14px!important;
          border-radius:0!important;
          background:transparent!important;
          box-shadow:none!important;
          border:0!important;
          border-bottom:1px solid rgba(226,232,240,.72)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-row{
          grid-template-columns:54px minmax(0,1fr)!important;
          gap:12px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-arrow{
          display:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-logo{
          width:54px!important;
          height:54px!important;
          border-radius:16px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-logo::after{
          content:none!important;
          display:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-copy strong{
          font-size:14px!important;
          line-height:1.08!important;
          font-weight:920!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-business-copy span{
          font-size:11px!important;
          line-height:1.15!important;
          margin-top:4px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-readiness{
          padding-top:12px!important;
          gap:8px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-readiness-row span{
          font-size:11.5px!important;
          color:#334155!important;
          font-weight:760!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-nav-readiness-track{
          height:5px!important;
          background:#e5ecea!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-grouped-tabs{
          padding:0!important;
          border-radius:0!important;
          background:transparent!important;
          box-shadow:none!important;
          gap:17px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-group{
          gap:6px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-group-label{
          padding:0!important;
          font-size:10px!important;
          letter-spacing:.075em!important;
          color:#8b98a7!important;
          font-weight:840!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tabs{
          gap:3px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab{
          position:relative!important;
          min-height:34px!important;
          height:34px!important;
          border-radius:11px!important;
          padding:0 11px!important;
          gap:11px!important;
          color:#0f172a!important;
          background:transparent!important;
          box-shadow:none!important;
          border:0!important;
          transform:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab:hover{
          background:rgba(15,23,42,.035)!important;
          transform:none!important;
          box-shadow:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab::before,
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab::after{
          content:none!important;
          display:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab.is-active{
          color:#00675f!important;
          background:linear-gradient(90deg,rgba(0,103,95,.105),rgba(0,103,95,.035))!important;
          box-shadow:none!important;
          border:0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab.is-active::before{
          content:none!important;
          display:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-icon{
          width:16px!important;
          height:16px!important;
          min-width:16px!important;
          color:#334155!important;
          background:transparent!important;
          border-radius:0!important;
          box-shadow:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-icon::after{
          content:none!important;
          display:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-icon svg{
          width:15px!important;
          height:15px!important;
          stroke-width:1.8!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab.is-active .loop-settings-tab-icon{
          color:#00675f!important;
          background:transparent!important;
          box-shadow:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-copy strong{
          font-size:12px!important;
          font-weight:800!important;
          letter-spacing:-.012em!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-settings-tab-arrow{
          display:none!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card{
          margin:0!important;
          min-height:60px!important;
          border-radius:16px!important;
          padding:13px 14px!important;
          gap:11px!important;
          background:linear-gradient(135deg,#fff7ed,#fff2dc)!important;
          box-shadow:inset 0 0 0 1px rgba(251,191,36,.20)!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card span{
          width:22px!important;
          height:22px!important;
          border-radius:8px!important;
          background:#fff8eb!important;
          font-size:11px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card strong{
          font-size:12px!important;
          font-weight:860!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card em{
          font-size:10px!important;
          margin-top:2px!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card b{
          display:none!important;
        }
        @media(max-width:1180px){
          .loop-settings-minimal:not(.is-native) .loop-settings-layout{grid-template-columns:286px minmax(0,1fr)!important;}
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2{width:258px!important;min-width:258px!important;max-width:258px!important;}
        }


        /* LOOP D2.9.4K — page header aligned with save button */
        .loop-settings-topbar-v2{
          display:flex!important;
          align-items:flex-start!important;
          justify-content:space-between!important;
          gap:22px!important;
          min-height:auto!important;
          margin:0 0 12px!important;
          padding:0 0 2px!important;
        }
        .loop-settings-page-heading{min-width:0;padding-top:2px;}
        .loop-settings-page-heading h1{margin:0;color:#0f172a;font-size:clamp(28px,2.25vw,36px);line-height:.95;font-weight:950;letter-spacing:-.065em;}
        .loop-settings-page-heading p{margin:8px 0 0;color:#475569;font-size:14px;font-weight:750;line-height:1.35;max-width:760px;}
        .loop-settings-save-area{display:flex;align-items:center;gap:14px;justify-content:flex-end;flex:0 0 auto;padding-top:2px;}
        .loop-settings-save-area .loop-settings-topbar-note{white-space:nowrap;font-size:12.5px;font-weight:800;color:#64748b;}
        .loop-settings-topbar-v2 .loop-save-btn{height:46px!important;min-width:166px!important;border-radius:15px!important;font-size:14.5px!important;box-shadow:0 16px 34px rgba(0,103,95,.18)!important;}
        .loop-settings-content{margin-top:0!important;}
        @media(max-width:980px){.loop-settings-topbar-v2{display:grid!important;gap:12px!important}.loop-settings-save-area{justify-content:space-between;width:100%;}.loop-settings-save-area .loop-settings-topbar-note{white-space:normal;}}
        @media(max-width:560px){.loop-settings-save-area{display:grid;grid-template-columns:1fr}.loop-settings-topbar-v2 .loop-save-btn{width:100%!important}}


        /* =========================================================
           LOOP RESPONSIVE BUSINESS OS — unified layout pass
           Goal: one mobile/tablet/desktop system for BusinessEditPage + sections.
           ========================================================= */
        .loop-settings-minimal{
          --loop-admin-bg:#fbf7ef;
          --loop-admin-card:#ffffff;
          --loop-admin-line:#e6edf2;
          --loop-admin-text:#0f172a;
          --loop-admin-muted:#64748b;
          --loop-admin-brand:#00675f;
          --loop-admin-shadow:0 18px 50px rgba(15,23,42,.065);
          isolation:isolate;
          overflow-x:hidden!important;
        }

        .loop-settings-minimal .loop-settings-layout,
        .loop-settings-minimal .loop-settings-main,
        .loop-settings-minimal .loop-settings-content{
          min-width:0!important;
        }

        .loop-settings-minimal:not(.is-native) .loop-settings-layout{
          width:min(100%,1540px)!important;
          margin:0 auto!important;
          grid-template-columns:minmax(236px,292px) minmax(0,1fr)!important;
          gap:clamp(16px,2vw,28px)!important;
        }

        .loop-settings-minimal:not(.is-native) .loop-settings-main{
          width:100%!important;
          max-width:none!important;
        }

        .loop-settings-topbar-v2{
          position:relative!important;
          z-index:80!important;
          margin-bottom:clamp(12px,1.4vw,18px)!important;
        }

        .loop-settings-page-heading h1{
          color:#0b1220!important;
          font-size:clamp(28px,2.4vw,40px)!important;
          letter-spacing:-.07em!important;
        }

        .loop-settings-page-heading p{
          max-width:760px!important;
          color:#526174!important;
        }

        .loop-settings-save-area .loop-save-btn,
        .loop-action-buttons .loop-save-btn{
          background:linear-gradient(135deg,#00574f,#008373)!important;
          box-shadow:0 16px 34px rgba(0,103,95,.18)!important;
        }

        .loop-settings-content{
          width:100%!important;
          max-width:none!important;
          overflow:visible!important;
          position:relative!important;
          z-index:1!important;
        }

        .loop-settings-content > *{
          width:100%!important;
          max-width:none!important;
          min-width:0!important;
        }

        /* Normalize section canvases so every module fits the same responsive grid. */
        .loop-settings-content .gen-page,
        .loop-settings-content .gen-v3-page,
        .loop-settings-content .svc-page,
        .loop-settings-content .svc-minimal-page,
        .loop-settings-content .availability-shell,
        .loop-settings-content .workers-pro-shell,
        .loop-settings-content .workers-general-premium-shell,
        .loop-settings-content .msg-page,
        .loop-settings-content .msg-shell,
        .loop-settings-content .notify-settings-page,
        .loop-settings-content .loyalty-shell,
        .loop-settings-content .membership-section,
        .loop-settings-content .billing-shell,
        .loop-settings-content .bos-page,
        .loop-settings-content .gallery-pro-shell{
          width:100%!important;
          max-width:none!important;
          margin:0!important;
          padding:0!important;
          background:transparent!important;
          min-width:0!important;
        }

        .loop-settings-content .svc-minimal-shell,
        .loop-settings-content .gen-v3-page,
        .loop-settings-content .availability-shell,
        .loop-settings-content .workers-pro-shell,
        .loop-settings-content .msg-shell,
        .loop-settings-content .loyalty-shell,
        .loop-settings-content .billing-shell,
        .loop-settings-content .bos-page,
        .loop-settings-content .gallery-pro-shell{
          display:grid!important;
          gap:clamp(14px,1.6vw,22px)!important;
        }

        /* Shared card polish across settings modules. */
        .loop-settings-content .gen-v3-card,
        .loop-settings-content .gen-v3-hero-card,
        .loop-settings-content .gen-v3-public-card,
        .loop-settings-content .gen-v3-logo-edit-card,
        .loop-settings-content .svc-catalog-card,
        .loop-settings-content .svc-materials-compact-card,
        .loop-settings-content .availability-card,
        .loop-settings-content .availability-week-card,
        .loop-settings-content .availability-config-card,
        .loop-settings-content .availability-block-card,
        .loop-settings-content .workers-modern-card,
        .loop-settings-content .workers-profile-card-premium,
        .loop-settings-content .workers-profile-fields-card,
        .loop-settings-content .notify-card,
        .loop-settings-content .msg-main-card,
        .loop-settings-content .msg-side-card,
        .loop-settings-content .loyalty-program-card,
        .loop-settings-content .loyalty-metric-card,
        .loop-settings-content .ms-plan-card,
        .loop-settings-content .ms-metric-card,
        .loop-settings-content .billing-card,
        .loop-settings-content .billing-hero-card,
        .loop-settings-content .bos-card,
        .loop-settings-content .gallery-card,
        .loop-settings-content .gallery-hero-card{
          border-color:rgba(226,232,240,.92)!important;
          box-shadow:0 14px 38px rgba(15,23,42,.055)!important;
          overflow:visible!important;
        }

        /* Grids should collapse gracefully. */
        .loop-settings-content .gen-v3-modal-grid,
        .loop-settings-content .gen-v3-mode-grid,
        .loop-settings-content .svc-option1-field-grid,
        .loop-settings-content .svc-pricing-mode-grid,
        .loop-settings-content .svc-concurrency-grid,
        .loop-settings-content .svc-icon-grid,
        .loop-settings-content .availability-config-grid,
        .loop-settings-content .availability-rules-grid,
        .loop-settings-content .workers-profile-fields-grid,
        .loop-settings-content .workers-profile-fields-grid-clean,
        .loop-settings-content .workers-permission-groups-grid,
        .loop-settings-content .workers-compensation-grid,
        .loop-settings-content .msg-content-grid,
        .loop-settings-content .msg-channel-cards,
        .loop-settings-content .loyalty-program-grid,
        .loop-settings-content .loyalty-type-grid,
        .loop-settings-content .ms-form-grid,
        .loop-settings-content .ms-benefit-type-grid,
        .loop-settings-content .billing-grid,
        .loop-settings-content .billing-fields-grid,
        .loop-settings-content .billing-limits-grid,
        .loop-settings-content .bos-grid,
        .loop-settings-content .bos-module-grid,
        .loop-settings-content .gallery-grid{
          min-width:0!important;
        }

        /* Keep portaled modals above the editor and avoid clipped dropdowns. */
        .svc-modal,
        .gen-v3-modal,
        .workers-add-modal-overlay,
        .workers-editor-modal-overlay,
        .loyalty-sheet,
        .ms-modal-layer{
          z-index:var(--loop-z-modal-backdrop,4000)!important;
        }
        .svc-sheet,
        .gen-v3-sheet,
        .workers-add-modal-shell,
        .workers-editor-modal-shell,
        .loyalty-sheet,
        .ms-modal{
          max-width:min(960px,calc(100vw - 28px))!important;
          max-height:min(92dvh,920px)!important;
          overflow:hidden!important;
        }
        .svc-sheet-body,
        .gen-v3-sheet-body,
        .workers-add-modal-body,
        .workers-editor-body,
        .loyalty-sheet-body,
        .ms-modal-body{
          min-height:0!important;
          overflow:auto!important;
          -webkit-overflow-scrolling:touch!important;
        }

        /* Tablet layout: sidebar becomes a premium sticky horizontal rail. */
        @media(max-width:1180px){
          .loop-settings-minimal:not(.is-native){
            padding:18px 14px calc(54px + env(safe-area-inset-bottom,0px))!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-layout{
            grid-template-columns:1fr!important;
            gap:16px!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2{
            position:sticky!important;
            top:calc(var(--loop-mobile-header-height,64px) + env(safe-area-inset-top,0px) + 8px)!important;
            z-index:300!important;
            width:100%!important;
            min-width:0!important;
            max-width:none!important;
            display:grid!important;
            grid-template-columns:minmax(180px,240px) minmax(0,1fr) auto!important;
            gap:10px!important;
            align-items:center!important;
            padding:10px!important;
            border:1px solid rgba(226,232,240,.92)!important;
            border-radius:22px!important;
            background:rgba(255,255,255,.94)!important;
            box-shadow:0 18px 42px rgba(15,23,42,.10)!important;
            backdrop-filter:blur(18px)!important;
            -webkit-backdrop-filter:blur(18px)!important;
            overflow:hidden!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-sidebar-brand{display:none!important;}
          .loop-settings-minimal:not(.is-native) .loop-nav-actions{display:block!important;min-width:0!important;}
          .loop-settings-minimal:not(.is-native) .loop-nav-business-card{
            padding:8px!important;
            border-radius:16px!important;
            box-shadow:none!important;
            border:1px solid rgba(226,232,240,.82)!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-nav-readiness{display:none!important;}
          .loop-settings-minimal:not(.is-native) .loop-settings-grouped-tabs{
            display:flex!important;
            gap:8px!important;
            overflow-x:auto!important;
            scrollbar-width:none!important;
            padding:0!important;
            background:transparent!important;
            box-shadow:none!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-grouped-tabs::-webkit-scrollbar{display:none!important;}
          .loop-settings-minimal:not(.is-native) .loop-settings-group{display:flex!important;gap:8px!important;align-items:center!important;}
          .loop-settings-minimal:not(.is-native) .loop-settings-group-label{display:none!important;}
          .loop-settings-minimal:not(.is-native) .loop-settings-tabs{display:flex!important;gap:8px!important;}
          .loop-settings-minimal:not(.is-native) .loop-settings-tab{
            height:40px!important;
            min-height:40px!important;
            min-width:max-content!important;
            border-radius:999px!important;
            padding:0 13px!important;
            background:#fff!important;
            border:1px solid rgba(226,232,240,.90)!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-tab-icon{width:18px!important;height:18px!important;min-width:18px!important;}
          .loop-settings-minimal:not(.is-native) .loop-settings-tab-copy strong{font-size:12px!important;font-weight:850!important;}
          .loop-settings-minimal:not(.is-native) .loop-premium-card{
            min-width:42px!important;
            width:42px!important;
            height:42px!important;
            border-radius:999px!important;
            padding:0!important;
            display:grid!important;
            place-items:center!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-premium-card div,
          .loop-settings-minimal:not(.is-native) .loop-premium-card b{display:none!important;}
          .loop-settings-topbar-v2{align-items:center!important;}
        }

        /* Mobile layout: feed style, one column, bottom save bar, bottom sheets. */
        @media(max-width:760px){
          .loop-settings-minimal,
          .loop-settings-minimal:not(.is-native){
            --loop-safe-bottom:max(env(safe-area-inset-bottom,0px),22px);
            padding:10px 10px calc(118px + var(--loop-safe-bottom))!important;
            background:linear-gradient(180deg,#fffaf3 0%,#f8f1e8 100%)!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-layout,
          .loop-settings-minimal .loop-settings-layout{
            gap:12px!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal.is-native .loop-settings-nav{
            top:calc(env(safe-area-inset-top,0px) + 8px)!important;
            grid-template-columns:1fr!important;
            border-radius:20px!important;
            padding:8px!important;
            gap:8px!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-nav-actions,
          .loop-settings-minimal.is-native .loop-nav-actions{display:none!important;}
          .loop-settings-minimal:not(.is-native) .loop-premium-card{display:none!important;}
          .loop-settings-page-heading h1{font-size:30px!important;line-height:.95!important;}
          .loop-settings-page-heading p{font-size:13px!important;line-height:1.35!important;margin-top:7px!important;}
          .loop-settings-topbar-v2{
            display:block!important;
            margin-bottom:8px!important;
          }
          .loop-settings-save-area{
            position:fixed!important;
            left:10px!important;
            right:10px!important;
            bottom:calc(var(--loop-safe-bottom) + 8px)!important;
            z-index:var(--loop-z-sticky,500)!important;
            display:grid!important;
            grid-template-columns:1fr!important;
            gap:0!important;
            padding:8px!important;
            border:1px solid rgba(226,232,240,.96)!important;
            border-radius:22px!important;
            background:rgba(255,255,255,.96)!important;
            box-shadow:0 18px 48px rgba(15,23,42,.20)!important;
            backdrop-filter:blur(18px)!important;
            -webkit-backdrop-filter:blur(18px)!important;
          }
          .loop-settings-save-area .loop-settings-topbar-note{display:none!important;}
          .loop-settings-save-area .loop-save-btn{
            width:100%!important;
            height:50px!important;
            min-height:50px!important;
            border-radius:16px!important;
            font-size:14px!important;
          }
          .loop-action-rail{display:none!important;}
          .loop-settings-updated{display:none!important;}
          .loop-settings-content{padding-bottom:8px!important;}

          .loop-settings-content .gen-v3-modal-grid,
          .loop-settings-content .gen-v3-mode-grid,
          .loop-settings-content .svc-option1-field-grid,
          .loop-settings-content .svc-pricing-mode-grid,
          .loop-settings-content .svc-concurrency-grid,
          .loop-settings-content .availability-config-grid,
          .loop-settings-content .availability-rules-grid,
          .loop-settings-content .workers-profile-fields-grid,
          .loop-settings-content .workers-profile-fields-grid-clean,
          .loop-settings-content .workers-permission-groups-grid,
          .loop-settings-content .workers-compensation-grid,
          .loop-settings-content .msg-content-grid,
          .loop-settings-content .msg-channel-cards,
          .loop-settings-content .loyalty-program-grid,
          .loop-settings-content .loyalty-type-grid,
          .loop-settings-content .ms-form-grid,
          .loop-settings-content .ms-benefit-type-grid,
          .loop-settings-content .billing-grid,
          .loop-settings-content .billing-fields-grid,
          .loop-settings-content .billing-limits-grid,
          .loop-settings-content .bos-grid,
          .loop-settings-content .bos-module-grid,
          .loop-settings-content .gallery-grid{
            grid-template-columns:1fr!important;
          }

          .loop-settings-content .svc-minimal-toolbar,
          .loop-settings-content .workers-services-toolbar,
          .loop-settings-content .msg-activity-toolbar,
          .loop-settings-content .loyalty-toolbar,
          .loop-settings-content .ms-toolbar,
          .loop-settings-content .gallery-actions{
            display:grid!important;
            grid-template-columns:1fr!important;
            width:100%!important;
            gap:10px!important;
          }
          .loop-settings-content button,
          .loop-settings-content input,
          .loop-settings-content select,
          .loop-settings-content textarea{
            max-width:100%!important;
          }
          .loop-settings-content .svc-catalog-card,
          .loop-settings-content .workers-modern-card,
          .loop-settings-content .notify-setting-row,
          .loop-settings-content .billing-toggle-row,
          .loop-settings-content .bos-card,
          .loop-settings-content .gallery-item{
            border-radius:18px!important;
          }

          .svc-modal,
          .gen-v3-modal,
          .workers-add-modal-overlay,
          .workers-editor-modal-overlay,
          .loyalty-sheet,
          .ms-modal-layer{
            align-items:flex-end!important;
            padding:0!important;
          }
          .svc-sheet,
          .gen-v3-sheet,
          .workers-add-modal-shell,
          .workers-editor-modal-shell,
          .loyalty-sheet,
          .ms-modal{
            width:100vw!important;
            max-width:100vw!important;
            height:auto!important;
            max-height:calc(94dvh - env(safe-area-inset-bottom,0px))!important;
            border-radius:26px 26px 0 0!important;
            margin:0!important;
            overflow:hidden!important;
          }
          .svc-sheet-head,
          .gen-v3-sheet-head,
          .workers-add-modal-head,
          .workers-editor-head,
          .loyalty-sheet-head,
          .ms-modal-head{
            position:sticky!important;
            top:0!important;
            z-index:2!important;
            background:rgba(255,255,255,.98)!important;
            backdrop-filter:blur(12px)!important;
            -webkit-backdrop-filter:blur(12px)!important;
          }
          .svc-sheet-foot,
          .gen-v3-sheet-footer,
          .workers-editor-footer,
          .loyalty-sheet-footer,
          .ms-modal-footer{
            position:sticky!important;
            bottom:0!important;
            z-index:3!important;
            background:rgba(255,255,255,.98)!important;
            padding-bottom:calc(12px + env(safe-area-inset-bottom,0px))!important;
          }
        }

        @media(max-width:420px){
          .loop-settings-minimal:not(.is-native) .loop-settings-tab,
          .loop-settings-minimal.is-native .loop-settings-tab{
            height:38px!important;
            min-height:38px!important;
            padding:0 11px!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-tab-copy strong,
          .loop-settings-minimal.is-native .loop-settings-tab-copy strong{
            font-size:11.5px!important;
          }
          .loop-settings-page-heading h1{font-size:28px!important;}
        }



        /* =========================================================
           LOOP MOBILE SHELL HOTFIX — compact panel + single save CTA
           - On phones the left panel becomes a thin horizontal tabs rail.
           - Legacy action rail is force-hidden to avoid duplicate Guardar cambios.
           - Only the bottom fixed save button remains visible.
           ========================================================= */
        @media(max-width:760px){
          .loop-settings-minimal,
          .loop-settings-minimal:not(.is-native),
          .loop-settings-minimal.is-native{
            padding-top:6px!important;
            padding-left:8px!important;
            padding-right:8px!important;
            padding-bottom:calc(96px + max(env(safe-area-inset-bottom,0px),18px))!important;
            overflow-x:hidden!important;
          }

          .loop-settings-minimal .loop-settings-layout,
          .loop-settings-minimal:not(.is-native) .loop-settings-layout,
          .loop-settings-minimal.is-native .loop-settings-layout{
            display:grid!important;
            grid-template-columns:1fr!important;
            gap:10px!important;
            width:100%!important;
            min-width:0!important;
          }

          /* Kill the old floating/sidebar action rail on mobile.
             This is the source of the duplicated Guardar cambios button. */
          .loop-settings-minimal .loop-settings-layout > .loop-action-rail,
          .loop-settings-minimal:not(.is-native) .loop-settings-layout > .loop-action-rail,
          .loop-settings-minimal.is-native .loop-settings-layout > .loop-action-rail,
          .loop-settings-minimal .loop-action-rail{
            display:none!important;
            visibility:hidden!important;
            pointer-events:none!important;
          }

          /* Compact mobile navigation rail */
          .loop-settings-minimal .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal.is-native .loop-settings-nav.loop-settings-nav-v2{
            position:sticky!important;
            top:calc(env(safe-area-inset-top,0px) + 4px)!important;
            z-index:var(--loop-z-sticky,500)!important;
            width:100%!important;
            max-width:100%!important;
            min-width:0!important;
            display:block!important;
            padding:6px!important;
            margin:0!important;
            border-radius:18px!important;
            border:1px solid rgba(226,232,240,.88)!important;
            background:rgba(255,255,255,.94)!important;
            box-shadow:0 12px 26px rgba(15,23,42,.09)!important;
            backdrop-filter:blur(16px)!important;
            -webkit-backdrop-filter:blur(16px)!important;
            overflow:hidden!important;
          }

          .loop-settings-minimal .loop-sidebar-brand,
          .loop-settings-minimal .loop-nav-actions,
          .loop-settings-minimal .loop-settings-nav-title,
          .loop-settings-minimal .loop-settings-privacy-card,
          .loop-settings-minimal .loop-premium-card{
            display:none!important;
          }

          .loop-settings-minimal .loop-settings-grouped-tabs,
          .loop-settings-minimal:not(.is-native) .loop-settings-grouped-tabs,
          .loop-settings-minimal.is-native .loop-settings-grouped-tabs{
            display:flex!important;
            align-items:center!important;
            gap:7px!important;
            width:100%!important;
            max-width:100%!important;
            min-width:0!important;
            overflow-x:auto!important;
            overflow-y:hidden!important;
            padding:0!important;
            margin:0!important;
            scrollbar-width:none!important;
            -webkit-overflow-scrolling:touch!important;
          }
          .loop-settings-minimal .loop-settings-grouped-tabs::-webkit-scrollbar{display:none!important;}

          .loop-settings-minimal .loop-settings-group,
          .loop-settings-minimal:not(.is-native) .loop-settings-group,
          .loop-settings-minimal.is-native .loop-settings-group{
            display:flex!important;
            align-items:center!important;
            gap:7px!important;
            flex:0 0 auto!important;
            min-width:0!important;
          }

          .loop-settings-minimal .loop-settings-group-label{display:none!important;}
          .loop-settings-minimal .loop-settings-tabs,
          .loop-settings-minimal:not(.is-native) .loop-settings-tabs,
          .loop-settings-minimal.is-native .loop-settings-tabs{
            display:flex!important;
            align-items:center!important;
            gap:7px!important;
            min-width:0!important;
            padding:0!important;
            margin:0!important;
          }

          .loop-settings-minimal .loop-settings-tab,
          .loop-settings-minimal:not(.is-native) .loop-settings-tab,
          .loop-settings-minimal.is-native .loop-settings-tab{
            flex:0 0 auto!important;
            width:auto!important;
            min-width:max-content!important;
            height:38px!important;
            min-height:38px!important;
            max-height:38px!important;
            padding:0 11px!important;
            border-radius:999px!important;
            border:1px solid rgba(226,232,240,.92)!important;
            background:#ffffff!important;
            box-shadow:none!important;
            gap:7px!important;
          }

          .loop-settings-minimal .loop-settings-tab.is-active,
          .loop-settings-minimal:not(.is-native) .loop-settings-tab.is-active,
          .loop-settings-minimal.is-native .loop-settings-tab.is-active{
            background:rgba(0,103,95,.10)!important;
            border-color:rgba(0,103,95,.18)!important;
            color:#00675f!important;
          }

          .loop-settings-minimal .loop-settings-tab-icon{
            width:18px!important;
            height:18px!important;
            min-width:18px!important;
            border-radius:999px!important;
            background:transparent!important;
            box-shadow:none!important;
          }
          .loop-settings-minimal .loop-settings-tab-copy strong{
            font-size:12px!important;
            font-weight:900!important;
            letter-spacing:-.01em!important;
          }
          .loop-settings-minimal .loop-settings-tab-arrow{display:none!important;}

          .loop-settings-main{
            gap:10px!important;
          }
          .loop-settings-topbar-v2{
            margin:0 0 6px!important;
            padding:0!important;
          }
          .loop-settings-page-heading h1{
            font-size:28px!important;
            line-height:.95!important;
            margin:0!important;
          }
          .loop-settings-page-heading p{
            font-size:12.5px!important;
            line-height:1.32!important;
            margin-top:6px!important;
          }

          /* Keep only one save button: the bottom fixed CTA from topbar. */
          .loop-settings-save-area{
            position:fixed!important;
            left:8px!important;
            right:8px!important;
            bottom:calc(max(env(safe-area-inset-bottom,0px),18px) + 6px)!important;
            z-index:var(--loop-z-sticky,500)!important;
            display:block!important;
            padding:7px!important;
            border-radius:21px!important;
            border:1px solid rgba(226,232,240,.96)!important;
            background:rgba(255,255,255,.96)!important;
            box-shadow:0 18px 44px rgba(15,23,42,.22)!important;
            backdrop-filter:blur(18px)!important;
            -webkit-backdrop-filter:blur(18px)!important;
          }
          .loop-settings-save-area .loop-settings-topbar-note{display:none!important;}
          .loop-settings-save-area .loop-save-btn{
            width:100%!important;
            min-width:0!important;
            height:48px!important;
            min-height:48px!important;
            border-radius:15px!important;
            font-size:14px!important;
            margin:0!important;
          }
        }



        /* =========================================================
           LOOP MOBILE SECTION PICKER — replaces sidebar on phones
           ========================================================= */
        .loop-mobile-section-select-wrap{display:none;}
        .loop-mobile-nav-layer{display:none;}

        @media(max-width:760px){
          .loop-mobile-section-select-wrap{
            display:block!important;
            position:sticky!important;
            top:calc(env(safe-area-inset-top,0px) + 4px)!important;
            z-index:var(--loop-z-sticky,500)!important;
            width:100%!important;
            margin:0!important;
          }
          .loop-mobile-section-trigger{
            width:100%!important;
            min-height:54px!important;
            border:1px solid rgba(226,232,240,.92)!important;
            border-radius:18px!important;
            background:rgba(255,255,255,.96)!important;
            box-shadow:0 12px 28px rgba(15,23,42,.09)!important;
            display:grid!important;
            grid-template-columns:38px minmax(0,1fr) 28px!important;
            align-items:center!important;
            gap:10px!important;
            padding:8px 10px!important;
            color:#0f172a!important;
            text-align:left!important;
            cursor:pointer!important;
            backdrop-filter:blur(16px)!important;
            -webkit-backdrop-filter:blur(16px)!important;
          }
          .loop-mobile-trigger-icon{
            width:38px!important;
            height:38px!important;
            border-radius:14px!important;
            display:grid!important;
            place-items:center!important;
            background:rgba(0,103,95,.09)!important;
            color:#00675f!important;
          }
          .loop-mobile-trigger-copy{min-width:0!important;}
          .loop-mobile-trigger-copy strong{
            display:block!important;
            font-size:15px!important;
            line-height:1.05!important;
            font-weight:950!important;
            letter-spacing:-.025em!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }
          .loop-mobile-trigger-copy small{
            display:block!important;
            margin-top:3px!important;
            color:#64748b!important;
            font-size:11.5px!important;
            font-weight:750!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }
          .loop-mobile-trigger-chevron{
            width:28px!important;
            height:28px!important;
            border-radius:999px!important;
            display:grid!important;
            place-items:center!important;
            background:#f8fafc!important;
            color:#64748b!important;
            font-size:18px!important;
            line-height:1!important;
            font-weight:950!important;
          }

          /* Replace the large panel completely. */
          .loop-settings-minimal .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal.is-native .loop-settings-nav.loop-settings-nav-v2{
            display:none!important;
            visibility:hidden!important;
            height:0!important;
            min-height:0!important;
            overflow:hidden!important;
            pointer-events:none!important;
          }

          .loop-mobile-nav-layer{
            display:block!important;
            position:fixed!important;
            inset:0!important;
            z-index:var(--loop-z-modal-backdrop,4000)!important;
          }
          .loop-mobile-nav-backdrop{
            position:absolute!important;
            inset:0!important;
            border:0!important;
            background:rgba(15,23,42,.38)!important;
            backdrop-filter:blur(5px)!important;
            -webkit-backdrop-filter:blur(5px)!important;
          }
          .loop-mobile-nav-sheet{
            position:absolute!important;
            left:0!important;
            right:0!important;
            bottom:0!important;
            max-height:min(78dvh,680px)!important;
            border-radius:28px 28px 0 0!important;
            border:1px solid rgba(226,232,240,.92)!important;
            border-bottom:0!important;
            background:#fffdf8!important;
            box-shadow:0 -24px 70px rgba(15,23,42,.24)!important;
            padding:10px 14px calc(34px + env(safe-area-inset-bottom,0px))!important;
            overflow:auto!important;
            -webkit-overflow-scrolling:touch!important;
            animation:loopMobileSheetIn .18s ease-out!important;
          }
          @keyframes loopMobileSheetIn{
            from{transform:translateY(22px);opacity:.78;}
            to{transform:translateY(0);opacity:1;}
          }
          .loop-mobile-nav-handle{
            width:44px!important;
            height:5px!important;
            border-radius:999px!important;
            background:#d0d5dd!important;
            margin:2px auto 12px!important;
          }
          .loop-mobile-nav-head{
            display:flex!important;
            align-items:flex-start!important;
            justify-content:space-between!important;
            gap:14px!important;
            padding:2px 2px 14px!important;
            border-bottom:1px solid rgba(226,232,240,.86)!important;
          }
          .loop-mobile-nav-head span{
            display:block!important;
            color:#00675f!important;
            font-size:11px!important;
            font-weight:950!important;
            letter-spacing:.13em!important;
            text-transform:uppercase!important;
          }
          .loop-mobile-nav-head strong{
            display:block!important;
            margin-top:5px!important;
            color:#0f172a!important;
            font-size:20px!important;
            line-height:1!important;
            font-weight:950!important;
            letter-spacing:-.045em!important;
          }
          .loop-mobile-nav-head button{
            width:38px!important;
            height:38px!important;
            border-radius:999px!important;
            border:1px solid rgba(226,232,240,.92)!important;
            background:#ffffff!important;
            color:#475569!important;
            font-size:24px!important;
            line-height:1!important;
            font-weight:750!important;
            cursor:pointer!important;
          }
          .loop-mobile-plan-summary{
            width:100%!important;
            margin:12px 0 12px!important;
            border:1px solid rgba(204,251,241,.92)!important;
            border-radius:22px!important;
            background:linear-gradient(135deg,#ffffff 0%,#f0fdfa 100%)!important;
            box-shadow:0 14px 34px rgba(0,103,95,.10)!important;
            display:grid!important;
            grid-template-columns:42px minmax(0,1fr) auto!important;
            align-items:center!important;
            gap:11px!important;
            padding:12px!important;
            color:#0f172a!important;
            text-align:left!important;
            cursor:pointer!important;
          }
          .loop-mobile-plan-summary.is-black{
            border-color:rgba(212,175,55,.42)!important;
            background:linear-gradient(135deg,#111827 0%,#050505 100%)!important;
            color:#f8fafc!important;
            box-shadow:0 18px 42px rgba(0,0,0,.22)!important;
          }
          .loop-mobile-plan-summary.is-premium{
            border-color:rgba(245,158,11,.30)!important;
            background:linear-gradient(135deg,#fffdf8 0%,#fff7ed 100%)!important;
          }
          .loop-mobile-plan-summary.is-basico{
            border-color:rgba(94,234,212,.38)!important;
            background:linear-gradient(135deg,#ffffff 0%,#ecfeff 100%)!important;
          }
          .loop-mobile-plan-summary.is-active{
            outline:2px solid rgba(0,103,95,.18)!important;
            outline-offset:2px!important;
          }
          .loop-mobile-plan-summary-icon{
            width:42px!important;
            height:42px!important;
            border-radius:16px!important;
            display:grid!important;
            place-items:center!important;
            background:rgba(0,103,95,.10)!important;
            color:#00675f!important;
            box-shadow:inset 0 0 0 1px rgba(0,103,95,.10)!important;
          }
          .loop-mobile-plan-summary.is-black .loop-mobile-plan-summary-icon{
            background:rgba(212,175,55,.14)!important;
            color:#f7d98a!important;
            box-shadow:inset 0 0 0 1px rgba(212,175,55,.26)!important;
          }
          .loop-mobile-plan-summary.is-premium .loop-mobile-plan-summary-icon{
            background:#fff7ed!important;
            color:#b45309!important;
            box-shadow:inset 0 0 0 1px rgba(245,158,11,.20)!important;
          }
          .loop-mobile-plan-summary-copy{min-width:0!important;display:block!important;}
          .loop-mobile-plan-summary-copy small{
            display:block!important;
            color:#00675f!important;
            font-size:10px!important;
            line-height:1!important;
            font-weight:950!important;
            text-transform:uppercase!important;
            letter-spacing:.12em!important;
            margin-bottom:5px!important;
          }
          .loop-mobile-plan-summary.is-black .loop-mobile-plan-summary-copy small{color:#f7d98a!important;}
          .loop-mobile-plan-summary-copy strong{
            display:block!important;
            color:inherit!important;
            font-size:15px!important;
            line-height:1.05!important;
            font-weight:950!important;
            letter-spacing:-.035em!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }
          .loop-mobile-plan-summary-copy em{
            display:block!important;
            margin-top:4px!important;
            color:#64748b!important;
            font-style:normal!important;
            font-size:11.5px!important;
            line-height:1.18!important;
            font-weight:750!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }
          .loop-mobile-plan-summary.is-black .loop-mobile-plan-summary-copy em{color:rgba(248,250,252,.78)!important;}
          .loop-mobile-plan-summary b{
            display:inline-flex!important;
            align-items:center!important;
            justify-content:center!important;
            min-height:28px!important;
            padding:0 10px!important;
            border-radius:999px!important;
            background:#00675f!important;
            color:#ffffff!important;
            font-size:11px!important;
            font-weight:950!important;
            white-space:nowrap!important;
          }
          .loop-mobile-plan-summary.is-black b{background:#f7d98a!important;color:#111827!important;}
          .loop-mobile-nav-layer ~ .loop-settings-main .loop-settings-save-area{
            display:none!important;
          }
          .loop-mobile-nav-list{
            display:grid!important;
            gap:16px!important;
            padding:16px 0 calc(18px + env(safe-area-inset-bottom,0px))!important;
          }
          .loop-mobile-nav-group{display:grid!important;gap:7px!important;}
          .loop-mobile-nav-group-label{
            color:#98a2b3!important;
            font-size:10.5px!important;
            font-weight:950!important;
            letter-spacing:.13em!important;
            text-transform:uppercase!important;
            padding:0 4px!important;
          }
          .loop-mobile-nav-item{
            width:100%!important;
            min-height:48px!important;
            border-radius:16px!important;
            border:1px solid rgba(226,232,240,.88)!important;
            background:#ffffff!important;
            color:#344054!important;
            display:grid!important;
            grid-template-columns:34px minmax(0,1fr) auto!important;
            align-items:center!important;
            gap:10px!important;
            padding:7px 10px!important;
            text-align:left!important;
            font-size:14px!important;
            font-weight:900!important;
            cursor:pointer!important;
            box-shadow:0 8px 18px rgba(15,23,42,.035)!important;
          }
          .loop-mobile-nav-item.is-active{
            border-color:rgba(0,103,95,.22)!important;
            background:rgba(0,103,95,.075)!important;
            color:#004f47!important;
          }
          .loop-mobile-nav-item-icon{
            width:34px!important;
            height:34px!important;
            border-radius:13px!important;
            display:grid!important;
            place-items:center!important;
            background:#f8fafc!important;
            color:#667085!important;
          }
          .loop-mobile-nav-item.is-active .loop-mobile-nav-item-icon{
            background:#ffffff!important;
            color:#00675f!important;
            box-shadow:0 8px 18px rgba(0,103,95,.10)!important;
          }
          .loop-mobile-nav-item-icon.premium{
            color:#b45309!important;
            background:#fff7ed!important;
            font-size:14px!important;
          }
          .loop-mobile-nav-item span:nth-child(2){
            min-width:0!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }
          .loop-mobile-nav-item em{
            font-style:normal!important;
            border-radius:999px!important;
            background:#ffffff!important;
            color:#00675f!important;
            padding:5px 8px!important;
            font-size:10.5px!important;
            font-weight:950!important;
          }
        }
        @media(max-width:390px){
          .loop-settings-minimal .loop-settings-tab,
          .loop-settings-minimal:not(.is-native) .loop-settings-tab,
          .loop-settings-minimal.is-native .loop-settings-tab{
            height:36px!important;
            min-height:36px!important;
            padding:0 10px!important;
          }
          .loop-settings-minimal .loop-settings-tab-copy strong{font-size:11.5px!important;}
          .loop-settings-page-heading h1{font-size:26px!important;}
        }


        /* LOOP BUSINESS EDIT STABILITY HOTFIX — fixed desktop panel top + clean loading handoff */
        .loop-settings-minimal:not(.is-native) .loop-settings-layout{
          align-items:start!important;
          align-content:start!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-main{
          align-self:start!important;
          margin-top:0!important;
          padding-top:0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-topbar-v2{
          margin-top:0!important;
        }
        .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2{
          align-self:start!important;
          margin-top:0!important;
          transform:none!important;
          top:84px!important;
        }
        @media(min-width:1181px){
          .loop-settings-minimal:not(.is-native){
            padding-top:18px!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-layout{
            padding-top:0!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2{
            position:sticky!important;
            top:84px!important;
          }
        }
        @media(max-width:760px){
          .loop-settings-minimal .loop-mobile-section-trigger{
            margin-top:0!important;
          }
        }

        /* LOOP DESKTOP SIDEBAR TRUE TOP LOCK — final override
           Keeps the settings panel pinned in the same top position on every tab.
           This avoids General/Membership/Fidelidad inheriting different vertical offsets. */
        @media(min-width:761px){
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-fixed-left:clamp(48px,5.5vw,86px);
            --loop-sidebar-fixed-top:calc(66px + 22px);
            --loop-sidebar-fixed-width:292px;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-layout{
            display:grid!important;
            grid-template-columns:318px minmax(0,1fr)!important;
            gap:24px!important;
            align-items:start!important;
            align-content:start!important;
            padding-top:0!important;
            margin-top:0!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2{
            position:fixed!important;
            top:var(--loop-sidebar-fixed-top)!important;
            left:var(--loop-sidebar-fixed-left)!important;
            width:var(--loop-sidebar-fixed-width)!important;
            min-width:var(--loop-sidebar-fixed-width)!important;
            max-width:var(--loop-sidebar-fixed-width)!important;
            margin:0!important;
            transform:none!important;
            align-self:start!important;
            z-index:1200!important;
            max-height:calc(100dvh - var(--loop-sidebar-fixed-top) - 18px)!important;
            overflow:auto!important;
            overscroll-behavior:contain!important;
            scrollbar-width:none!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2::-webkit-scrollbar{display:none!important;}
          .loop-settings-minimal:not(.is-native) .loop-settings-main{
            grid-column:2!important;
            align-self:start!important;
            margin-top:0!important;
            padding-top:0!important;
            min-width:0!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-topbar-v2{
            margin-top:0!important;
            padding-top:0!important;
          }
        }
        @media(min-width:761px) and (max-width:1120px){
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-fixed-left:18px;
            --loop-sidebar-fixed-width:270px;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-layout{
            grid-template-columns:294px minmax(0,1fr)!important;
            gap:18px!important;
          }
        }


        

        /* LOOP RESPONSIVE SHELL LOCK — one navigation pattern per breakpoint
           Desktop: left sidebar. Tablet/mobile: compact selector + bottom sheet.
           Fixes: weird half-sidebar, duplicated Guardar perfil, floating save overlap. */
        @media (min-width: 1101px){
          .loop-settings-minimal:not(.is-native) .loop-mobile-section-select-wrap{
            display:none!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2{
            display:grid!important;
            visibility:visible!important;
            height:auto!important;
            min-height:0!important;
            pointer-events:auto!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-save-area{
            position:static!important;
            left:auto!important;
            right:auto!important;
            bottom:auto!important;
            z-index:auto!important;
            display:flex!important;
            padding:0!important;
            border:0!important;
            background:transparent!important;
            box-shadow:none!important;
            backdrop-filter:none!important;
            -webkit-backdrop-filter:none!important;
          }
          .loop-settings-minimal:not(.is-native) .loop-settings-topbar-v2{
            display:grid!important;
            grid-template-columns:minmax(0,1fr) auto!important;
            gap:18px!important;
            align-items:start!important;
          }
        }

        @media (max-width: 1100px){
          .loop-settings-minimal{
            padding-left:10px!important;
            padding-right:10px!important;
            padding-bottom:calc(120px + max(env(safe-area-inset-bottom,0px),18px))!important;
            overflow-x:hidden!important;
          }

          .loop-settings-minimal .loop-settings-layout,
          .loop-settings-minimal:not(.is-native) .loop-settings-layout,
          .loop-settings-minimal.is-native .loop-settings-layout{
            display:grid!important;
            grid-template-columns:minmax(0,1fr)!important;
            gap:10px!important;
            width:100%!important;
            max-width:100%!important;
            margin:0!important;
            padding:0!important;
          }

          .loop-settings-minimal .loop-settings-main,
          .loop-settings-minimal:not(.is-native) .loop-settings-main,
          .loop-settings-minimal.is-native .loop-settings-main{
            grid-column:1!important;
            width:100%!important;
            max-width:100%!important;
            min-width:0!important;
            justify-self:stretch!important;
            margin:0!important;
            padding:0!important;
            gap:10px!important;
          }

          /* Hide the desktop sidebar completely before it turns into the ugly half-panel. */
          .loop-settings-minimal .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal.is-native .loop-settings-nav.loop-settings-nav-v2{
            display:none!important;
            visibility:hidden!important;
            height:0!important;
            max-height:0!important;
            min-height:0!important;
            width:0!important;
            max-width:0!important;
            overflow:hidden!important;
            pointer-events:none!important;
            padding:0!important;
            margin:0!important;
            border:0!important;
            box-shadow:none!important;
          }

          /* The compact section selector becomes the only nav for tablet/mobile. */
          .loop-mobile-section-select-wrap{
            display:block!important;
            position:sticky!important;
            top:calc(env(safe-area-inset-top,0px) + 4px)!important;
            z-index:var(--loop-z-sticky,500)!important;
            width:100%!important;
            margin:0!important;
          }

          .loop-mobile-section-trigger{
            width:100%!important;
            min-height:52px!important;
            border:1px solid rgba(226,232,240,.92)!important;
            border-radius:17px!important;
            background:rgba(255,255,255,.96)!important;
            box-shadow:0 10px 24px rgba(15,23,42,.08)!important;
            display:grid!important;
            grid-template-columns:38px minmax(0,1fr) 28px!important;
            align-items:center!important;
            gap:10px!important;
            padding:7px 10px!important;
            color:#0f172a!important;
            text-align:left!important;
            cursor:pointer!important;
            backdrop-filter:blur(16px)!important;
            -webkit-backdrop-filter:blur(16px)!important;
          }

          .loop-mobile-trigger-icon{
            width:38px!important;
            height:38px!important;
            border-radius:14px!important;
            display:grid!important;
            place-items:center!important;
            background:#e9f7f3!important;
            color:#00675f!important;
            flex:0 0 auto!important;
          }

          .loop-mobile-trigger-copy{
            min-width:0!important;
            display:grid!important;
            gap:2px!important;
          }

          .loop-mobile-trigger-copy strong{
            display:block!important;
            color:#0f172a!important;
            font-size:14px!important;
            font-weight:950!important;
            line-height:1.05!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }

          .loop-mobile-trigger-copy small{
            display:block!important;
            color:#64748b!important;
            font-size:11.5px!important;
            font-weight:850!important;
            line-height:1.1!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }

          .loop-mobile-trigger-chevron{
            width:28px!important;
            height:28px!important;
            border-radius:999px!important;
            background:#f8fafc!important;
            color:#64748b!important;
            display:grid!important;
            place-items:center!important;
            font-size:18px!important;
            line-height:1!important;
            font-weight:950!important;
          }

          .loop-mobile-nav-layer{
            display:block!important;
            position:fixed!important;
            inset:0!important;
            z-index:var(--loop-z-modal-backdrop,4000)!important;
          }

          .loop-mobile-nav-backdrop{
            position:absolute!important;
            inset:0!important;
            border:0!important;
            background:rgba(15,23,42,.38)!important;
            backdrop-filter:blur(5px)!important;
            -webkit-backdrop-filter:blur(5px)!important;
          }

          .loop-mobile-nav-sheet{
            position:absolute!important;
            left:0!important;
            right:0!important;
            bottom:0!important;
            max-height:min(78dvh,680px)!important;
            border-radius:28px 28px 0 0!important;
            border:1px solid rgba(226,232,240,.92)!important;
            border-bottom:0!important;
            background:#fffdf8!important;
            box-shadow:0 -24px 70px rgba(15,23,42,.24)!important;
            padding:10px 14px calc(16px + env(safe-area-inset-bottom,0px))!important;
            overflow:auto!important;
            -webkit-overflow-scrolling:touch!important;
          }

          /* Only one save CTA. Never let the topbar button float over content. */
          .loop-settings-topbar-v2{
            display:block!important;
            margin:0!important;
            padding:0!important;
          }

          .loop-settings-save-area{
            position:fixed!important;
            left:8px!important;
            right:8px!important;
            bottom:calc(max(env(safe-area-inset-bottom,0px),18px) + 6px)!important;
            z-index:var(--loop-z-sticky,500)!important;
            display:block!important;
            padding:7px!important;
            border-radius:21px!important;
            border:1px solid rgba(226,232,240,.96)!important;
            background:rgba(255,255,255,.96)!important;
            box-shadow:0 18px 44px rgba(15,23,42,.20)!important;
            backdrop-filter:blur(18px)!important;
            -webkit-backdrop-filter:blur(18px)!important;
            width:auto!important;
            max-width:none!important;
          }

          .loop-settings-save-area .loop-settings-topbar-note{
            display:none!important;
          }

          .loop-settings-save-area .loop-save-btn{
            width:100%!important;
            min-width:0!important;
            height:48px!important;
            min-height:48px!important;
            border-radius:15px!important;
            font-size:14px!important;
            margin:0!important;
          }

          .loop-settings-page-heading h1{
            font-size:clamp(26px,5vw,34px)!important;
            line-height:.96!important;
            margin:0!important;
          }

          .loop-settings-page-heading p{
            max-width:680px!important;
            font-size:13px!important;
            line-height:1.32!important;
            margin-top:6px!important;
          }
        }

        @media (max-width: 520px){
          .loop-settings-minimal{
            padding-left:0!important;
            padding-right:0!important;
          }
          .loop-mobile-section-select-wrap{
            padding:0 8px!important;
          }
          .loop-settings-main{
            gap:8px!important;
          }
        }

        `}</style>

      
      <style>{`
        /* =========================================================
           LOOP BUSINESS EDIT — TRUE STICKY SIDEBAR FINAL PATCH
           Diagnosis:
           DOM showed sidebar computed as position: fixed; top: 88px.
           Fixed removes it from the layout flow and can overlap content on zoom.
           This patch forces the rail back into the grid as sticky, Facebook-style:
           stable under the global header, but still reserving its column.
           ========================================================= */

        @media (min-width: 1101px){
          .loop-settings-minimal{
            --loop-header-h: 66px;
            --loop-sidebar-w: 292px;
            --loop-sidebar-gap: clamp(48px, 4vw, 76px);
            padding-top: 22px !important;
            padding-left: clamp(24px, 3.2vw, 56px) !important;
            padding-right: clamp(24px, 3.2vw, 56px) !important;
            overflow-x: clip !important;
          }

          .loop-settings-layout{
            width: min(100%, 1560px) !important;
            max-width: 1560px !important;
            margin: 0 auto !important;
            display: grid !important;
            grid-template-columns: var(--loop-sidebar-w) minmax(0, 1fr) !important;
            gap: var(--loop-sidebar-gap) !important;
            align-items: start !important;
            justify-content: center !important;
            position: relative !important;
          }

          .loop-settings-nav.loop-settings-nav-v2{
            position: sticky !important;
            top: calc(var(--loop-header-h) + 22px) !important;
            left: auto !important;
            right: auto !important;
            bottom: auto !important;
            inset: auto !important;
            transform: none !important;
            translate: none !important;
            width: var(--loop-sidebar-w) !important;
            min-width: var(--loop-sidebar-w) !important;
            max-width: var(--loop-sidebar-w) !important;
            height: auto !important;
            min-height: 0 !important;
            max-height: calc(100dvh - var(--loop-header-h) - 44px) !important;
            margin: 0 !important;
            align-self: start !important;
            justify-self: start !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            z-index: 30 !important;
            will-change: auto !important;
            contain: none !important;
          }

          .loop-settings-main{
            width: 100% !important;
            max-width: 1180px !important;
            min-width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            justify-self: start !important;
            position: relative !important;
            z-index: 1 !important;
            overflow: visible !important;
          }

          .loop-settings-topbar-v2{
            position: sticky !important;
            top: calc(var(--loop-header-h) + 6px) !important;
            z-index: 25 !important;
            background:
              linear-gradient(
                180deg,
                rgba(255,250,244,.98) 0%,
                rgba(255,250,244,.92) 74%,
                rgba(255,250,244,0) 100%
              ) !important;
            backdrop-filter: blur(10px) !important;
            -webkit-backdrop-filter: blur(10px) !important;
            padding-bottom: 14px !important;
            margin-bottom: 8px !important;
          }

          .loop-settings-save-area{
            position: static !important;
            left: auto !important;
            right: auto !important;
            top: auto !important;
            bottom: auto !important;
            inset: auto !important;
            transform: none !important;
            display: flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
            gap: 14px !important;
            min-width: max-content !important;
            width: auto !important;
            padding: 0 !important;
            margin: 0 !important;
            border: 0 !important;
            background: transparent !important;
            box-shadow: none !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
          }
        }

        @media (min-width: 1101px) and (max-width: 1400px){
          .loop-settings-minimal{
            --loop-sidebar-w: 276px;
            --loop-sidebar-gap: 44px;
            padding-left: 22px !important;
            padding-right: 22px !important;
          }

          .loop-settings-layout{
            max-width: calc(100vw - 44px) !important;
          }

          .loop-settings-main{
            max-width: calc(100vw - 276px - 44px - 64px) !important;
          }
        }

        @media (min-width: 1101px) and (max-height: 760px){
          .loop-settings-minimal{
            padding-top: 14px !important;
          }

          .loop-settings-nav.loop-settings-nav-v2{
            top: calc(var(--loop-header-h) + 14px) !important;
            max-height: calc(100dvh - var(--loop-header-h) - 28px) !important;
          }

          .loop-settings-topbar-v2{
            top: calc(var(--loop-header-h) + 4px) !important;
          }
        }

        @media (max-width: 1100px){
          .loop-settings-nav.loop-settings-nav-v2{
            display: none !important;
            position: static !important;
          }

          .loop-settings-layout{
            grid-template-columns: minmax(0,1fr) !important;
            gap: 10px !important;
            max-width: 100% !important;
          }

          .loop-settings-main{
            max-width: 100% !important;
            width: 100% !important;
          }
        }
      `}</style>


      
      <style>{`

        /* =========================================================
           LOOP BUSINESS EDIT — CONTENT CLOSER TO SIDEBAR + HEADER
           Goal: match the desired screenshot:
           - main content starts higher
           - main content moves left, closer to the sidebar
           - reduce wasted right/left whitespace on desktop
           - keep mobile/tablet behavior untouched
           ========================================================= */
        @media (min-width: 1101px) {
          .loop-settings-minimal {
            --loop-global-header-h: 66px;
            --loop-settings-rail-w: 286px;
            padding-top: 10px !important;
            padding-left: clamp(14px, 1.8vw, 28px) !important;
            padding-right: clamp(14px, 1.8vw, 28px) !important;
          }

          .loop-settings-layout {
            width: min(100%, 1680px) !important;
            max-width: 1680px !important;
            margin: 0 auto !important;
            grid-template-columns: var(--loop-settings-rail-w) minmax(0, 1fr) !important;
            gap: clamp(24px, 2.6vw, 44px) !important;
            align-items: start !important;
          }

          .loop-settings-nav.loop-settings-nav-v2 {
            top: calc(var(--loop-global-header-h) + 14px) !important;
            margin-top: 0 !important;
          }

          .loop-settings-main {
            max-width: 1240px !important;
            width: 100% !important;
            justify-self: start !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .loop-settings-topbar-v2 {
            top: calc(var(--loop-global-header-h) + 4px) !important;
            padding-bottom: 8px !important;
            margin-bottom: 4px !important;
          }

          .loop-settings-page-heading h1 {
            font-size: clamp(34px, 2.55vw, 46px) !important;
            line-height: .94 !important;
          }

          .loop-settings-page-heading p {
            margin-top: 6px !important;
          }
        }

        @media (min-width: 1101px) and (max-width: 1500px) {
          .loop-settings-minimal {
            --loop-settings-rail-w: 270px;
            padding-left: 14px !important;
            padding-right: 14px !important;
          }

          .loop-settings-layout {
            gap: 28px !important;
          }

          .loop-settings-main {
            max-width: calc(100vw - var(--loop-settings-rail-w) - 70px) !important;
          }
        }

      `}</style>

      
      <style>{`
        /* =========================================================
           LOOP BUSINESS EDIT — DESKTOP ALIGNMENT FINAL OVERRIDE
           Based on console measurement:
           header=66, page y=66, layout y=84, main x=360, genPage y=188.
           Goal: content closer to sidebar and header, like the target screenshot.
           ========================================================= */

        @media (min-width: 1101px) {
          body {
            padding-top: 66px !important;
          }

          .business-settings-shell.loop-settings-minimal,
          .loop-settings-minimal.business-settings-shell,
          .loop-settings-minimal {
            padding-top: 4px !important;
            padding-left: 12px !important;
            padding-right: 12px !important;
            padding-bottom: 46px !important;
            overflow-x: hidden !important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-layout,
          .loop-settings-minimal.business-settings-shell .loop-settings-layout,
          .loop-settings-minimal .loop-settings-layout,
          .loop-settings-layout {
            width: min(100%, 1760px) !important;
            max-width: 1760px !important;
            margin: 0 auto !important;
            display: grid !important;
            grid-template-columns: 274px minmax(0, 1fr) !important;
            gap: 18px !important;
            align-items: start !important;
            position: relative !important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal.business-settings-shell .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-nav.loop-settings-nav-v2 {
            width: 270px !important;
            max-width: 270px !important;
            min-width: 270px !important;
            left: 24px !important;
            top: 78px !important;
            padding: 14px !important;
            border-radius: 22px !important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-main,
          .loop-settings-minimal.business-settings-shell .loop-settings-main,
          .loop-settings-minimal .loop-settings-main,
          .loop-settings-main {
            width: 100% !important;
            max-width: none !important;
            min-width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            justify-self: stretch !important;
            gap: 8px !important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-topbar-v2,
          .loop-settings-minimal.business-settings-shell .loop-settings-topbar-v2,
          .loop-settings-minimal .loop-settings-topbar-v2,
          .loop-settings-topbar-v2 {
            position: static !important;
            top: auto !important;
            margin: 0 !important;
            padding: 0 0 6px 0 !important;
            min-height: 0 !important;
            height: auto !important;
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) auto !important;
            align-items: start !important;
            gap: 14px !important;
            background: transparent !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
          }

          .loop-settings-page-heading h1 {
            font-size: clamp(32px, 2.5vw, 43px) !important;
            line-height: .92 !important;
            margin: 0 !important;
            letter-spacing: -0.075em !important;
          }

          .loop-settings-page-heading p {
            margin-top: 6px !important;
            max-width: 760px !important;
            font-size: 13.2px !important;
            line-height: 1.32 !important;
          }

          .loop-settings-save-area {
            padding-top: 0 !important;
            margin-top: 0 !important;
            align-self: start !important;
          }

          .loop-settings-save-area .loop-save-btn {
            height: 42px !important;
            min-height: 42px !important;
            border-radius: 13px !important;
          }
        }

        @media (min-width: 1101px) and (max-width: 1320px) {
          .business-settings-shell.loop-settings-minimal .loop-settings-layout,
          .loop-settings-minimal.business-settings-shell .loop-settings-layout,
          .loop-settings-minimal .loop-settings-layout,
          .loop-settings-layout {
            grid-template-columns: 260px minmax(0, 1fr) !important;
            gap: 14px !important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal.business-settings-shell .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-nav.loop-settings-nav-v2 {
            width: 256px !important;
            min-width: 256px !important;
            max-width: 256px !important;
            left: 18px !important;
          }
        }
      

        /* =========================================================
           LOOP BUSINESS EDIT — MOBILE SAVE + TOP GAP FINAL FIX
           Fixes:
           - only one save button on tablet/mobile
           - removes the floating top save bar that overlaps the selector
           - tightens the empty space between global header and section selector
           ========================================================= */
        .loop-mobile-save-dock{
          display:none;
        }

        @media (max-width: 1100px){
          .business-settings-shell.loop-settings-minimal,
          .loop-settings-minimal.business-settings-shell,
          .loop-settings-minimal{
            padding-top:6px!important;
            padding-left:6px!important;
            padding-right:6px!important;
            padding-bottom:calc(92px + max(env(safe-area-inset-bottom,0px),18px))!important;
            overflow-x:hidden!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-layout,
          .loop-settings-minimal.business-settings-shell .loop-settings-layout,
          .loop-settings-minimal .loop-settings-layout,
          .loop-settings-layout{
            display:grid!important;
            grid-template-columns:minmax(0,1fr)!important;
            gap:6px!important;
            width:100%!important;
            max-width:100%!important;
            margin:0!important;
            padding:0!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-settings-minimal.business-settings-shell .loop-mobile-section-select-wrap,
          .loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-mobile-section-select-wrap{
            display:block!important;
            position:sticky!important;
            top:4px!important;
            z-index:var(--loop-z-sticky,500)!important;
            width:100%!important;
            margin:0!important;
            padding:0!important;
          }

          .loop-mobile-section-trigger{
            min-height:50px!important;
            border-radius:17px!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-main,
          .loop-settings-minimal.business-settings-shell .loop-settings-main,
          .loop-settings-minimal .loop-settings-main,
          .loop-settings-main{
            width:100%!important;
            max-width:100%!important;
            min-width:0!important;
            margin:0!important;
            padding:0!important;
            gap:8px!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-topbar-v2,
          .loop-settings-minimal.business-settings-shell .loop-settings-topbar-v2,
          .loop-settings-minimal .loop-settings-topbar-v2,
          .loop-settings-topbar-v2{
            position:static!important;
            top:auto!important;
            margin:0!important;
            padding:0 0 4px 0!important;
            background:transparent!important;
            box-shadow:none!important;
            backdrop-filter:none!important;
            -webkit-backdrop-filter:none!important;
            display:block!important;
            min-height:0!important;
          }

          /* Hide the original topbar save area on tablet/mobile.
             It was the one appearing as a huge bar above the section selector. */
          .business-settings-shell.loop-settings-minimal .loop-settings-save-area,
          .loop-settings-minimal.business-settings-shell .loop-settings-save-area,
          .loop-settings-minimal .loop-settings-save-area,
          .loop-settings-save-area{
            display:none!important;
            visibility:hidden!important;
            pointer-events:none!important;
            position:static!important;
            inset:auto!important;
            width:0!important;
            height:0!important;
            min-height:0!important;
            padding:0!important;
            margin:0!important;
            border:0!important;
            box-shadow:none!important;
            background:transparent!important;
          }

          /* Hide legacy action rail on tablet/mobile so it cannot create a second save button. */
          .business-settings-shell.loop-settings-minimal .loop-action-rail,
          .loop-settings-minimal.business-settings-shell .loop-action-rail,
          .loop-settings-minimal .loop-action-rail,
          .loop-action-rail{
            display:none!important;
            visibility:hidden!important;
            pointer-events:none!important;
            width:0!important;
            height:0!important;
            overflow:hidden!important;
          }

          .loop-mobile-save-dock{
            display:block!important;
            position:fixed!important;
            left:8px!important;
            right:8px!important;
            bottom:calc(max(env(safe-area-inset-bottom,0px),18px) + 6px)!important;
            z-index:var(--loop-z-modal-backdrop,4000)!important;
            padding:7px!important;
            border-radius:21px!important;
            border:1px solid rgba(226,232,240,.96)!important;
            background:rgba(255,255,255,.96)!important;
            box-shadow:0 18px 44px rgba(15,23,42,.22)!important;
            backdrop-filter:blur(18px)!important;
            -webkit-backdrop-filter:blur(18px)!important;
          }

          .loop-mobile-save-btn{
            width:100%!important;
            min-width:0!important;
            height:48px!important;
            min-height:48px!important;
            border-radius:15px!important;
            font-size:14px!important;
            margin:0!important;
            display:flex!important;
            align-items:center!important;
            justify-content:center!important;
          }

          .loop-settings-page-heading h1{
            font-size:clamp(27px,6vw,34px)!important;
            line-height:.94!important;
            margin:0!important;
          }

          .loop-settings-page-heading p{
            font-size:12.5px!important;
            line-height:1.3!important;
            margin-top:6px!important;
            max-width:100%!important;
          }

          .loop-settings-content{
            padding-bottom:4px!important;
          }
        }

        @media (max-width: 520px){
          .business-settings-shell.loop-settings-minimal,
          .loop-settings-minimal.business-settings-shell,
          .loop-settings-minimal{
            padding-top:4px!important;
            padding-left:4px!important;
            padding-right:4px!important;
          }

          .loop-mobile-section-select-wrap{
            top:2px!important;
          }

          .loop-settings-main{
            gap:6px!important;
          }
        }

        

        /* =========================================================
           LOOP BUSINESS EDIT — MOBILE ROOT OFFSET FINAL FIX
           Console diagnosis after the previous patch:
           - .mainmenu-header-only-root is already height:0
           - body still has padding-top:58px
           - .loop-mobile-section-select-wrap has sticky top:58px
           That creates a double offset: 58 + 58 = 116.
           Fix: in BusinessEditPage mobile/tablet, body no longer reserves the
           header height; the selector itself stays sticky at top:58px.
           ========================================================= */
        @media (max-width: 1100px){
          body{
            padding-top:0!important;
          }

          #root{
            transform:none!important;
            margin-top:0!important;
            padding-top:0!important;
          }

          .mainmenu-header-only-root{
            height:0!important;
            min-height:0!important;
            max-height:0!important;
            margin:0!important;
            padding:0!important;
            overflow:visible!important;
            pointer-events:none!important;
          }

          .business-settings-shell.loop-settings-minimal,
          .loop-settings-minimal.business-settings-shell,
          .loop-settings-minimal{
            margin-top:0!important;
            padding-top:0!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-layout,
          .loop-settings-minimal.business-settings-shell .loop-settings-layout,
          .loop-settings-minimal .loop-settings-layout,
          .loop-settings-layout{
            margin-top:0!important;
            padding-top:0!important;
            gap:6px!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-settings-minimal.business-settings-shell .loop-mobile-section-select-wrap,
          .loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-mobile-section-select-wrap{
            position:sticky!important;
            top:58px!important;
            margin-top:0!important;
            padding-top:0!important;
          }
        }

        

        /* =========================================================
           LOOP BUSINESS EDIT — MOBILE HEADER INTERACTION + SELECTOR POLISH
           Fixes after console/mobile test:
           1) Header buttons were not clickable because .mainmenu-header-only-root
              had pointer-events:none while the fixed header lives inside/near it.
           2) Section selector was too tight to the header. Add a small breathing gap.
           3) The BusinessEditPage mobile topbar text was showing behind the selector.
              On mobile the selector + section content own the context, so hide it.
           ========================================================= */
        @media (max-width: 1100px){
          body{
            padding-top:0!important;
          }

          #root{
            transform:none!important;
            margin-top:0!important;
            padding-top:0!important;
          }

          .mainmenu-header-only-root{
            height:0!important;
            min-height:0!important;
            max-height:0!important;
            margin:0!important;
            padding:0!important;
            overflow:visible!important;
            pointer-events:auto!important;
          }

          #app-main-header,
          .loop-ref-header,
          .loop-mobile-header-v2,
          .loop-header-emerald-hardlock{
            pointer-events:auto!important;
            z-index:var(--loop-z-header,1000)!important;
          }

          .business-settings-shell.loop-settings-minimal,
          .loop-settings-minimal.business-settings-shell,
          .loop-settings-minimal{
            margin-top:0!important;
            padding-top:0!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-layout,
          .loop-settings-minimal.business-settings-shell .loop-settings-layout,
          .loop-settings-minimal .loop-settings-layout,
          .loop-settings-layout{
            margin-top:0!important;
            padding-top:0!important;
            gap:8px!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-settings-minimal.business-settings-shell .loop-mobile-section-select-wrap,
          .loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-mobile-section-select-wrap{
            position:sticky!important;
            top:66px!important;
            z-index:var(--loop-z-sticky,500)!important;
            margin:0!important;
            padding:0 4px!important;
            width:100%!important;
          }

          .loop-mobile-section-trigger{
            min-height:52px!important;
            height:52px!important;
            border-radius:17px!important;
            box-shadow:0 10px 22px rgba(15,23,42,.075)!important;
          }

          /* Hide BusinessEditPage mobile topbar so no text appears behind the selector.
             GeneralSection/Availability/etc. still render their own content titles. */
          .business-settings-shell.loop-settings-minimal .loop-settings-topbar-v2,
          .loop-settings-minimal.business-settings-shell .loop-settings-topbar-v2,
          .loop-settings-minimal .loop-settings-topbar-v2,
          .loop-settings-topbar-v2{
            display:none!important;
            visibility:hidden!important;
            height:0!important;
            min-height:0!important;
            max-height:0!important;
            overflow:hidden!important;
            pointer-events:none!important;
            padding:0!important;
            margin:0!important;
            border:0!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-main,
          .loop-settings-minimal.business-settings-shell .loop-settings-main,
          .loop-settings-minimal .loop-settings-main,
          .loop-settings-main{
            margin:0!important;
            padding:0!important;
            gap:8px!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content,
          .loop-settings-minimal.business-settings-shell .loop-settings-content,
          .loop-settings-minimal .loop-settings-content,
          .loop-settings-content{
            margin-top:8px!important;
            padding-top:0!important;
          }
        }

        @media (max-width: 520px){
          .business-settings-shell.loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-settings-minimal.business-settings-shell .loop-mobile-section-select-wrap,
          .loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-mobile-section-select-wrap{
            top:64px!important;
            padding-left:4px!important;
            padding-right:4px!important;
          }

          .loop-settings-content{
            margin-top:6px!important;
          }
        }

        

        /* =========================================================
           LOOP BUSINESS EDIT — MOBILE SELECTOR BREATHING ROOM + CONTENT SAFE GAP
           - selector sits a little lower below fixed header
           - content starts below selector with a professional gap
           - header buttons remain clickable
           ========================================================= */
        @media (max-width: 1100px){
          body{
            padding-top:0!important;
          }

          .mainmenu-header-only-root{
            height:0!important;
            min-height:0!important;
            max-height:0!important;
            margin:0!important;
            padding:0!important;
            overflow:visible!important;
            pointer-events:auto!important;
          }

          #app-main-header,
          .loop-ref-header,
          .loop-mobile-header-v2,
          .loop-header-emerald-hardlock{
            pointer-events:auto!important;
            z-index:var(--loop-z-header,1000)!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-settings-minimal.business-settings-shell .loop-mobile-section-select-wrap,
          .loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-mobile-section-select-wrap{
            top:72px!important;
            padding-left:6px!important;
            padding-right:6px!important;
            z-index:var(--loop-z-sticky,500)!important;
          }

          .loop-mobile-section-trigger{
            height:52px!important;
            min-height:52px!important;
            border-radius:17px!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-topbar-v2,
          .loop-settings-minimal.business-settings-shell .loop-settings-topbar-v2,
          .loop-settings-minimal .loop-settings-topbar-v2,
          .loop-settings-topbar-v2{
            display:none!important;
            visibility:hidden!important;
            height:0!important;
            min-height:0!important;
            max-height:0!important;
            overflow:hidden!important;
            pointer-events:none!important;
            padding:0!important;
            margin:0!important;
            border:0!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content,
          .loop-settings-minimal.business-settings-shell .loop-settings-content,
          .loop-settings-minimal .loop-settings-content,
          .loop-settings-content{
            margin-top:18px!important;
            padding-top:0!important;
          }
        }

        @media (max-width: 520px){
          .business-settings-shell.loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-settings-minimal.business-settings-shell .loop-mobile-section-select-wrap,
          .loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-mobile-section-select-wrap{
            top:70px!important;
            padding-left:6px!important;
            padding-right:6px!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content,
          .loop-settings-minimal.business-settings-shell .loop-settings-content,
          .loop-settings-minimal .loop-settings-content,
          .loop-settings-content{
            margin-top:18px!important;
          }
        }

        

        /* =========================================================
           LOOP BUSINESS EDIT — MOBILE SELECTOR OVERLAY GAP FIX
           Console diagnosis:
           selector: 68 → 120
           hero/logo: starts at 72/89
           The selector is sticky and visually overlays the content.
           Fix: keep selector under header, then reserve real space inside
           content before the active section begins.
           ========================================================= */
        @media (max-width: 1100px){
          .business-settings-shell.loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-settings-minimal.business-settings-shell .loop-mobile-section-select-wrap,
          .loop-settings-minimal .loop-mobile-section-select-wrap,
          .loop-mobile-section-select-wrap{
            position:sticky!important;
            top:68px!important;
            z-index:var(--loop-z-sticky,500)!important;
            margin:0!important;
            padding:0 6px!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-main,
          .loop-settings-minimal.business-settings-shell .loop-settings-main,
          .loop-settings-minimal .loop-settings-main,
          .loop-settings-main{
            margin-top:0!important;
            padding-top:0!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content,
          .loop-settings-minimal.business-settings-shell .loop-settings-content,
          .loop-settings-minimal .loop-settings-content,
          .loop-settings-content{
            margin-top:0!important;
            padding-top:66px!important;
            position:relative!important;
            z-index:1!important;
          }

          .business-settings-shell.loop-settings-minimal .gen-v3-page,
          .business-settings-shell.loop-settings-minimal .gen-page{
            padding-top:0!important;
            margin-top:0!important;
          }
        }

        @media (max-width: 520px){
          .business-settings-shell.loop-settings-minimal .loop-settings-content,
          .loop-settings-minimal.business-settings-shell .loop-settings-content,
          .loop-settings-minimal .loop-settings-content,
          .loop-settings-content{
            padding-top:68px!important;
          }
        }

        

        /* =========================================================
           LOOP BUSINESS EDIT — SIDEBAR SCALE HARMONY FINAL
           Objetivo:
           - Panel lateral un poco más ancho y más presente en desktop.
           - Tipografía/íconos un poco más grandes.
           - Mantener fit responsive sin romper tablet/mobile.
           ========================================================= */

        @media (min-width: 1101px){
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-w-final: clamp(304px, 18.4vw, 336px);
            --loop-sidebar-gap-final: clamp(26px, 2.7vw, 46px);
            padding-left: clamp(18px, 1.9vw, 34px) !important;
            padding-right: clamp(18px, 1.9vw, 34px) !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-layout{
            grid-template-columns: var(--loop-sidebar-w-final) minmax(0, 1fr) !important;
            gap: var(--loop-sidebar-gap-final) !important;
            align-items: start !important;
            max-width: min(1720px, calc(100vw - 36px)) !important;
            margin: 0 auto !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-nav,
          .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2{
            width: var(--loop-sidebar-w-final) !important;
            min-width: var(--loop-sidebar-w-final) !important;
            max-width: var(--loop-sidebar-w-final) !important;
            padding: 18px !important;
            border-radius: 28px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-main{
            max-width: min(1390px, calc(100vw - var(--loop-sidebar-w-final) - var(--loop-sidebar-gap-final) - 64px)) !important;
            justify-self: stretch !important;
            width: 100% !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-nav-business-card{
            padding: 0 0 20px !important;
            gap: 16px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-nav-business-row{
            grid-template-columns: 64px minmax(0,1fr) !important;
            gap: 14px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-nav-business-logo{
            width: 62px !important;
            height: 62px !important;
            border-radius: 18px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-nav-business-copy strong{
            font-size: 14px !important;
            line-height: 1.08 !important;
            letter-spacing: -0.035em !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-nav-business-copy span{
            margin-top: 4px !important;
            font-size: 11.5px !important;
            line-height: 1.18 !important;
            font-weight: 820 !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-nav-progress-copy strong{
            font-size: 13px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-nav-progress-copy span{
            font-size: 11.5px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-nav-section-label{
            font-size: 11px !important;
            letter-spacing: .13em !important;
            margin: 18px 0 8px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-nav-link{
            min-height: 44px !important;
            border-radius: 15px !important;
            padding: 0 13px !important;
            gap: 12px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-nav-link .loop-nav-icon{
            width: 30px !important;
            height: 30px !important;
            border-radius: 11px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-nav-link .loop-nav-icon svg{
            width: 17px !important;
            height: 17px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-nav-link strong,
          .loop-settings-minimal:not(.is-native) .loop-settings-nav-link span:not(.loop-nav-icon){
            font-size: 13.5px !important;
            line-height: 1.1 !important;
            font-weight: 930 !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-nav-premium-card{
            min-height: 74px !important;
            border-radius: 18px !important;
            padding: 14px 16px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-nav-premium-card strong{
            font-size: 13px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-nav-premium-card span{
            font-size: 11px !important;
          }
        }

        @media (min-width: 1101px) and (max-width: 1320px){
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-w-final: 300px;
            --loop-sidebar-gap-final: 28px;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-nav,
          .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2{
            padding: 16px !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-main{
            max-width: calc(100vw - 300px - 28px - 52px) !important;
          }
        }

        @media (min-width: 1500px){
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-w-final: 328px;
            --loop-sidebar-gap-final: 44px;
          }
        }

        @media (min-width: 1780px){
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-w-final: 340px;
            --loop-sidebar-gap-final: 48px;
          }
        }

        @media (max-width: 1100px){
          .loop-settings-minimal:not(.is-native) .loop-settings-nav,
          .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2{
            width: auto !important;
            min-width: 0 !important;
            max-width: none !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-main{
            max-width: 100% !important;
          }
        }


        /* =========================================================
           LOOP BUSINESS EDIT — SIDEBAR GAP BALANCE FINAL
           Mantiene el panel más grande, pero elimina el espacio muerto
           entre el sidebar y el contenido principal.
           ========================================================= */

        @media (min-width: 1101px){
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-w-final: clamp(292px, 16.4vw, 318px) !important;
            --loop-sidebar-gap-final: clamp(18px, 1.25vw, 26px) !important;
            padding-left: clamp(12px, 1.45vw, 24px) !important;
            padding-right: clamp(16px, 1.8vw, 30px) !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-layout{
            grid-template-columns: var(--loop-sidebar-w-final) minmax(0, 1fr) !important;
            gap: var(--loop-sidebar-gap-final) !important;
            column-gap: var(--loop-sidebar-gap-final) !important;
            justify-content: start !important;
            align-items: start !important;
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-nav,
          .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2{
            width: var(--loop-sidebar-w-final) !important;
            min-width: var(--loop-sidebar-w-final) !important;
            max-width: var(--loop-sidebar-w-final) !important;
            justify-self: start !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-main{
            width: 100% !important;
            min-width: 0 !important;
            max-width: none !important;
            justify-self: stretch !important;
            margin-left: 0 !important;
          }
        }

        @media (min-width: 1320px){
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-w-final: 306px !important;
            --loop-sidebar-gap-final: 22px !important;
          }
        }

        @media (min-width: 1600px){
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-w-final: 314px !important;
            --loop-sidebar-gap-final: 24px !important;
          }
        }

        @media (min-width: 1820px){
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-w-final: 322px !important;
            --loop-sidebar-gap-final: 26px !important;
          }
        }

        @media (min-width: 1101px) and (max-width: 1320px){
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-w-final: 292px !important;
            --loop-sidebar-gap-final: 18px !important;
          }
        }

        @media (max-width: 1100px){
          .loop-settings-minimal:not(.is-native) .loop-settings-layout{
            max-width: none !important;
            margin: 0 !important;
          }

          .loop-settings-minimal:not(.is-native) .loop-settings-main{
            max-width: 100% !important;
            margin-left: 0 !important;
          }
        }

        /* LOOP PLAN CARD ART V2 — real transparent PNG, no white corners, no forced crop */
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card.loop-plan-art-card,
        .loop-premium-card.loop-plan-art-card{
          position:relative!important;
          display:block!important;
          width:100%!important;
          min-height:0!important;
          height:auto!important;
          padding:0!important;
          margin:0!important;
          border:0!important;
          border-radius:0!important;
          background:transparent!important;
          background-image:none!important;
          box-shadow:none!important;
          overflow:visible!important;
          cursor:pointer!important;
          line-height:0!important;
          isolation:isolate!important;
          appearance:none!important;
          -webkit-appearance:none!important;
        }

        .loop-premium-card.loop-plan-art-card .loop-plan-card-image{
          display:block!important;
          width:100%!important;
          max-width:100%!important;
          height:auto!important;
          aspect-ratio:auto!important;
          object-fit:contain!important;
          object-position:center!important;
          border:0!important;
          border-radius:0!important;
          background:transparent!important;
          box-shadow:none!important;
          filter:drop-shadow(0 14px 28px rgba(15,23,42,.07))!important;
          image-rendering:auto!important;
          transform:translateZ(0)!important;
          backface-visibility:hidden!important;
          transition:transform .18s ease, filter .18s ease, opacity .18s ease!important;
          user-select:none!important;
          pointer-events:none!important;
        }

        .loop-premium-card.loop-plan-art-card:hover .loop-plan-card-image{
          transform:translateY(-1px) translateZ(0)!important;
          filter:drop-shadow(0 18px 34px rgba(15,23,42,.10))!important;
        }

        .loop-premium-card.loop-plan-art-card .loop-plan-card-fallback,
        .loop-premium-card.loop-plan-art-card .loop-plan-card-a11y{
          position:absolute!important;
          width:1px!important;
          height:1px!important;
          overflow:hidden!important;
          clip:rect(0 0 0 0)!important;
          white-space:nowrap!important;
          border:0!important;
          padding:0!important;
          margin:-1px!important;
        }

        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card.loop-plan-art-card span,
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card.loop-plan-art-card div,
        .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2 .loop-premium-card.loop-plan-art-card b{
          background:transparent!important;
          box-shadow:none!important;
        }

        @media (max-width: 1100px){
          .loop-settings-minimal:not(.is-native) .loop-premium-card.loop-plan-art-card{
            display:none!important;
          }
        }

`}</style>



      <style>{`
        /* =========================================================
           LOOP BUSINESS EDIT — NATIVE HEADER CONTRACT V27
           V25/V26 made the global header math correct. BusinessEditPage still
           had old mobile rules that set body padding to 0 and a sticky section
           selector top:58px. In native, body already reserves the header, so the
           selector should start at the top of this page content, not below a
           second artificial header.
           ========================================================= */
        @media (max-width: 1100px){
          html.is-mobile-app,
          body.is-mobile-app{
            --loop-native-editor-gap: 12px !important;
          }

          body.is-mobile-app{
            padding-top: var(--loop-mobile-header-total, 94px) !important;
            margin-top: 0 !important;
          }

          body.is-mobile-app #root{
            padding-top: 0 !important;
            margin-top: 0 !important;
            transform: none !important;
          }

          body.is-mobile-app .mainmenu-header-only-root{
            height: 0 !important;
            min-height: 0 !important;
            max-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            pointer-events: auto !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native{
            margin-top: 0 !important;
            padding-top: var(--loop-native-editor-gap) !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
            min-height: calc(100dvh - var(--loop-mobile-header-total, 94px)) !important;
            overflow: auto !important;
            -webkit-overflow-scrolling: touch !important;
            box-sizing: border-box !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-layout{
            margin-top: 0 !important;
            padding-top: 0 !important;
            gap: 10px !important;
            min-height: 0 !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-mobile-section-select-wrap{
            position: sticky !important;
            top: 0 !important;
            margin-top: 0 !important;
            margin-bottom: 12px !important;
            padding-top: 0 !important;
            z-index: 420 !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-topbar-v2{
            display: none !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-main{
            gap: 10px !important;
          }
        }
      `}</style>



      <style>{`
        /* =========================================================
           LOOP BUSINESS EDIT — NATIVE GAP TIGHTEN V28
           V27 confirmed the editor begins below the header. The remaining
           issue is internal spacing: shell margin + selector + main gap.
           This keeps the selector visible but removes the large empty feel.
           ========================================================= */
        @media (max-width: 1100px){
          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native{
            margin-top: 0 !important;
            padding-top: 6px !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-layout{
            gap: 6px !important;
            padding-top: 0 !important;
            margin-top: 0 !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-mobile-section-select-wrap{
            top: 0 !important;
            margin-top: 0 !important;
            margin-bottom: 6px !important;
            padding-top: 0 !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-main{
            gap: 8px !important;
            margin-top: 0 !important;
            padding-top: 0 !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-main > .loop-settings-topbar-v2{
            display: none !important;
            height: 0 !important;
            min-height: 0 !important;
            max-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }
        }
      `}</style>



      <style>{`
        /* =========================================================
           LOOP BUSINESS EDIT — REMOVE CONTENT TOP GAP V29
           Console confirmed the large empty space is exactly here:
           .loop-settings-content padding-top = 68px.
           Remove that only in native app so it matches Chrome.
           ========================================================= */
        @media (max-width: 1100px){
          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-main{
            margin-top: 0 !important;
            padding-top: 0 !important;
            gap: 6px !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-content{
            padding-top: 0 !important;
            margin-top: 0 !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .gen-v3-page{
            margin-top: 0 !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .gen-v3-hero-card{
            margin-top: 0 !important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-mobile-section-select-wrap{
            margin-bottom: 6px !important;
          }
        }
      `}</style>




      <style>{`
        /* =========================================================
           LOOP BUSINESS EDIT — MOBILE SCROLL END LOCK V30
           Fixes the long empty beige scroll area below the last card.
           The page still leaves enough room for the fixed save dock,
           but no longer adds multiple stacked bottom paddings.
           ========================================================= */
        @media (max-width: 1100px){
          .business-settings-shell.loop-settings-minimal,
          .business-settings-shell.loop-settings-minimal.is-native,
          .loop-settings-minimal.business-settings-shell,
          .loop-settings-minimal{
            --loop-mobile-dock-bottom:max(env(safe-area-inset-bottom,0px),18px);
            --loop-mobile-dock-h:68px;
            --loop-mobile-end-gap:12px;
            min-height:0!important;
            height:auto!important;
            padding-bottom:0!important;
            overflow-x:hidden!important;
          }

          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native{
            min-height:0!important;
            height:auto!important;
            padding-bottom:0!important;
            overflow:visible!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-layout,
          .business-settings-shell.loop-settings-minimal.is-native .loop-settings-layout,
          .loop-settings-minimal.business-settings-shell .loop-settings-layout,
          .loop-settings-minimal .loop-settings-layout,
          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-layout{
            min-height:0!important;
            height:auto!important;
            padding-bottom:0!important;
            margin-bottom:0!important;
            overflow:visible!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-main,
          .business-settings-shell.loop-settings-minimal.is-native .loop-settings-main,
          .loop-settings-minimal.business-settings-shell .loop-settings-main,
          .loop-settings-minimal .loop-settings-main,
          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-main{
            min-height:0!important;
            height:auto!important;
            padding-bottom:0!important;
            margin-bottom:0!important;
            overflow:visible!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content,
          .business-settings-shell.loop-settings-minimal.is-native .loop-settings-content,
          .loop-settings-minimal.business-settings-shell .loop-settings-content,
          .loop-settings-minimal .loop-settings-content,
          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-content{
            min-height:0!important;
            height:auto!important;
            margin-bottom:0!important;
            overflow:visible!important;
            padding-bottom:calc(var(--loop-mobile-dock-h) + var(--loop-mobile-dock-bottom) + var(--loop-mobile-end-gap))!important;
          }

          .business-settings-shell.loop-settings-minimal .gen-v3-page,
          .business-settings-shell.loop-settings-minimal.is-native .gen-v3-page,
          .business-settings-shell.loop-settings-minimal .gen-page,
          .business-settings-shell.loop-settings-minimal.is-native .gen-page,
          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .gen-v3-page,
          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .gen-page{
            min-height:0!important;
            height:auto!important;
            margin-bottom:0!important;
            padding-bottom:0!important;
          }

          .business-settings-shell.loop-settings-minimal .gen-v3-page > :last-child,
          .business-settings-shell.loop-settings-minimal.is-native .gen-v3-page > :last-child,
          .business-settings-shell.loop-settings-minimal .gen-page > :last-child,
          .business-settings-shell.loop-settings-minimal.is-native .gen-page > :last-child{
            margin-bottom:0!important;
          }

          .loop-mobile-save-dock{
            bottom:calc(var(--loop-mobile-dock-bottom) + 6px)!important;
          }
        }
      `}</style>


      <style>{`
        /* =========================================================
           LOOP BUSINESS EDIT — PROFILE PREVIEW BUTTON POLISH V31
           Moves the GeneralSection “Ver mi perfil” action out of the
           large empty public card on mobile and turns it into a small,
           minimal pill beside the Visible status/edit controls.
           ========================================================= */
        @media (max-width: 760px){
          .business-settings-shell.loop-settings-minimal .loop-settings-content .gen-v3-hero-card,
          .loop-settings-minimal .loop-settings-content .gen-v3-hero-card{
            position:relative!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content .gen-v3-title-row,
          .loop-settings-minimal .loop-settings-content .gen-v3-title-row{
            position:relative!important;
            padding-right:104px!important;
            align-items:center!important;
            row-gap:6px!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content .gen-v3-title-row h2,
          .loop-settings-minimal .loop-settings-content .gen-v3-title-row h2{
            min-width:0!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content .gen-v3-public-card,
          .loop-settings-minimal .loop-settings-content .gen-v3-public-card{
            grid-column:1 / -1!important;
            grid-row:2!important;
            align-self:start!important;
            justify-self:end!important;
            width:auto!important;
            min-width:0!important;
            max-width:none!important;
            min-height:0!important;
            height:auto!important;
            padding:0!important;
            margin:1px 43px 0 0!important;
            border:0!important;
            border-radius:0!important;
            background:transparent!important;
            box-shadow:none!important;
            backdrop-filter:none!important;
            -webkit-backdrop-filter:none!important;
            display:block!important;
            z-index:9!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content .gen-v3-public-card > div,
          .business-settings-shell.loop-settings-minimal .loop-settings-content .gen-v3-public-card > p,
          .loop-settings-minimal .loop-settings-content .gen-v3-public-card > div,
          .loop-settings-minimal .loop-settings-content .gen-v3-public-card > p{
            display:none!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content .gen-v3-public-card > button,
          .loop-settings-minimal .loop-settings-content .gen-v3-public-card > button{
            width:auto!important;
            min-width:0!important;
            height:28px!important;
            min-height:28px!important;
            max-height:28px!important;
            padding:0 10px!important;
            border-radius:999px!important;
            border:1px solid rgba(0,103,95,.16)!important;
            background:rgba(0,103,95,.075)!important;
            color:#00675f!important;
            box-shadow:none!important;
            display:inline-flex!important;
            align-items:center!important;
            justify-content:center!important;
            gap:0!important;
            font-size:0!important;
            line-height:1!important;
            letter-spacing:-.01em!important;
            white-space:nowrap!important;
            transform:none!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content .gen-v3-public-card > button::after,
          .loop-settings-minimal .loop-settings-content .gen-v3-public-card > button::after{
            content:"Ver perfil";
            display:block;
            font-size:11px!important;
            line-height:1!important;
            font-weight:950!important;
            color:inherit!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content .gen-v3-public-card > button:hover,
          .loop-settings-minimal .loop-settings-content .gen-v3-public-card > button:hover{
            background:rgba(0,103,95,.11)!important;
            border-color:rgba(0,103,95,.24)!important;
          }
        }

        @media (min-width: 761px) and (max-width: 1100px){
          .business-settings-shell.loop-settings-minimal .loop-settings-content .gen-v3-public-card > button,
          .loop-settings-minimal .loop-settings-content .gen-v3-public-card > button{
            font-size:0!important;
          }
          .business-settings-shell.loop-settings-minimal .loop-settings-content .gen-v3-public-card > button::after,
          .loop-settings-minimal .loop-settings-content .gen-v3-public-card > button::after{
            content:"Ver perfil";
            font-size:12px!important;
            font-weight:950!important;
          }
        }
      `}</style>


      <style>{`
        /* =========================================================
           LOOP DEVICE FOOTER FIT V45
           Scope: BusinessEditPage mobile save dock.
           Goal:
           - Android: Guardar perfil stays above system navigation buttons.
           - iOS: only real safe-area inset, no artificial Android gutter.
           - No html/body/#root locks; this page remains normal document scroll.
           ========================================================= */
        @media (max-width: 1100px) {
          .business-settings-shell.loop-settings-minimal,
          .business-settings-shell.loop-settings-minimal.is-native,
          .loop-settings-minimal.business-settings-shell,
          .loop-settings-minimal {
            --loop-device-bottom-safe: env(safe-area-inset-bottom, 0px);
            --loop-mobile-dock-h: 68px;
            --loop-mobile-dock-bottom: var(--loop-device-bottom-safe);
            --loop-mobile-end-gap: 16px;

            padding-bottom: calc(
              var(--loop-mobile-dock-h) +
              var(--loop-device-bottom-safe) +
              var(--loop-mobile-end-gap)
            ) !important;
            scroll-padding-bottom: calc(
              var(--loop-mobile-dock-h) +
              var(--loop-device-bottom-safe) +
              18px
            ) !important;
          }

          body.is-mobile-app.is-android-app .business-settings-shell.loop-settings-minimal,
          body.is-mobile-app.is-android-app .business-settings-shell.loop-settings-minimal.is-native,
          html.is-android-app body.is-mobile-app .business-settings-shell.loop-settings-minimal,
          html.is-android-app body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native {
            --loop-device-bottom-safe: max(
              env(safe-area-inset-bottom, 0px),
              var(--safe-area-inset-bottom, 0px),
              var(--loop-safe-bottom, 0px)
            );
            --loop-mobile-dock-bottom: var(--loop-device-bottom-safe);
          }

          body.is-mobile-app.is-ios-app .business-settings-shell.loop-settings-minimal,
          body.is-mobile-app.is-ios-app .business-settings-shell.loop-settings-minimal.is-native,
          html.is-ios-app body.is-mobile-app .business-settings-shell.loop-settings-minimal,
          html.is-ios-app body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native {
            --loop-device-bottom-safe: env(safe-area-inset-bottom, 0px);
            --loop-mobile-dock-bottom: var(--loop-device-bottom-safe);
          }

          .business-settings-shell.loop-settings-minimal .loop-settings-content,
          .business-settings-shell.loop-settings-minimal.is-native .loop-settings-content,
          .loop-settings-minimal.business-settings-shell .loop-settings-content,
          .loop-settings-minimal .loop-settings-content,
          body.is-mobile-app .business-settings-shell.loop-settings-minimal.is-native .loop-settings-content {
            padding-bottom: calc(
              var(--loop-mobile-dock-h) +
              var(--loop-device-bottom-safe) +
              18px
            ) !important;
          }

          .business-settings-shell.loop-settings-minimal .loop-mobile-save-dock,
          .business-settings-shell.loop-settings-minimal.is-native .loop-mobile-save-dock,
          .loop-settings-minimal.business-settings-shell .loop-mobile-save-dock,
          .loop-mobile-save-dock {
            bottom: calc(var(--loop-device-bottom-safe) + 8px) !important;
          }

          .loop-mobile-save-dock .loop-mobile-save-btn,
          .business-settings-shell.loop-settings-minimal .loop-mobile-save-btn {
            margin: 0 !important;
          }

          .svc-sheet-foot,
          .gen-v3-sheet-footer,
          .workers-editor-footer,
          .loyalty-sheet-footer,
          .ms-modal-footer {
            padding-bottom: calc(12px + var(--loop-device-bottom-safe)) !important;
          }
        }
      `}</style>


      <style>{`
        /* =========================================================
           LOOP BUSINESS EDIT V57 — tablet/native section picker sheet
           Problem:
           - The tablet breakpoint (761px–1100px) already hides the desktop
             sidebar and shows the mobile selector.
           - But most of the bottom-sheet internal styles only lived under
             max-width:760px, so tablet/native rendered raw inline text/icons.
           Contract:
           - 0–1100px = one selector + one polished bottom sheet.
           - Desktop sidebar never leaks into tablet/native.
           ========================================================= */
        @media (max-width: 1100px){
          .business-settings-shell.loop-settings-minimal .loop-mobile-nav-layer,
          .loop-settings-minimal.business-settings-shell .loop-mobile-nav-layer,
          .loop-settings-minimal .loop-mobile-nav-layer,
          .loop-mobile-nav-layer{
            display:block!important;
            position:fixed!important;
            inset:0!important;
            z-index:var(--loop-z-modal-backdrop,4000)!important;
            isolation:isolate!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-mobile-nav-backdrop,
          .loop-settings-minimal.business-settings-shell .loop-mobile-nav-backdrop,
          .loop-settings-minimal .loop-mobile-nav-backdrop,
          .loop-mobile-nav-backdrop{
            position:absolute!important;
            inset:0!important;
            width:100%!important;
            height:100%!important;
            border:0!important;
            padding:0!important;
            margin:0!important;
            background:rgba(15,23,42,.38)!important;
            backdrop-filter:blur(5px)!important;
            -webkit-backdrop-filter:blur(5px)!important;
            z-index:0!important;
          }

          .business-settings-shell.loop-settings-minimal .loop-mobile-nav-sheet,
          .loop-settings-minimal.business-settings-shell .loop-mobile-nav-sheet,
          .loop-settings-minimal .loop-mobile-nav-sheet,
          .loop-mobile-nav-sheet{
            position:absolute!important;
            left:0!important;
            right:0!important;
            bottom:0!important;
            z-index:1!important;
            width:100%!important;
            max-width:100%!important;
            max-height:min(78dvh,680px)!important;
            border-radius:28px 28px 0 0!important;
            border:1px solid rgba(226,232,240,.94)!important;
            border-bottom:0!important;
            background:#fffdf8!important;
            box-shadow:0 -24px 70px rgba(15,23,42,.24)!important;
            padding:10px 14px calc(18px + var(--loop-device-bottom-safe, env(safe-area-inset-bottom,0px)))!important;
            overflow:auto!important;
            -webkit-overflow-scrolling:touch!important;
            display:block!important;
            color:#0f172a!important;
          }

          .loop-mobile-nav-handle{
            display:block!important;
            width:44px!important;
            height:5px!important;
            border-radius:999px!important;
            background:#d0d5dd!important;
            margin:2px auto 12px!important;
            flex:0 0 auto!important;
          }

          .loop-mobile-nav-head{
            display:flex!important;
            align-items:flex-start!important;
            justify-content:space-between!important;
            gap:14px!important;
            width:100%!important;
            min-width:0!important;
            padding:2px 2px 14px!important;
            margin:0!important;
            border-bottom:1px solid rgba(226,232,240,.86)!important;
          }
          .loop-mobile-nav-head > div{
            display:grid!important;
            gap:4px!important;
            min-width:0!important;
          }
          .loop-mobile-nav-head span{
            display:block!important;
            color:#00675f!important;
            font-size:11px!important;
            line-height:1!important;
            font-weight:950!important;
            letter-spacing:.13em!important;
            text-transform:uppercase!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }
          .loop-mobile-nav-head strong{
            display:block!important;
            color:#0f172a!important;
            font-size:20px!important;
            line-height:1.05!important;
            font-weight:950!important;
            letter-spacing:-.045em!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }
          .loop-mobile-nav-head button{
            width:38px!important;
            height:38px!important;
            min-width:38px!important;
            border-radius:999px!important;
            border:1px solid rgba(226,232,240,.92)!important;
            background:#ffffff!important;
            color:#475569!important;
            font-size:24px!important;
            line-height:1!important;
            font-weight:750!important;
            display:grid!important;
            place-items:center!important;
            padding:0!important;
            margin:0!important;
            cursor:pointer!important;
          }

          .loop-mobile-plan-summary{
            width:100%!important;
            margin:12px 0 12px!important;
            border:1px solid rgba(204,251,241,.92)!important;
            border-radius:22px!important;
            background:linear-gradient(135deg,#ffffff 0%,#f0fdfa 100%)!important;
            box-shadow:0 14px 34px rgba(0,103,95,.10)!important;
            display:grid!important;
            grid-template-columns:42px minmax(0,1fr) auto!important;
            align-items:center!important;
            gap:11px!important;
            padding:12px!important;
            color:#0f172a!important;
            text-align:left!important;
            cursor:pointer!important;
          }
          .loop-mobile-plan-summary.is-black{
            border-color:rgba(212,175,55,.42)!important;
            background:linear-gradient(135deg,#111827 0%,#050505 100%)!important;
            color:#f8fafc!important;
          }
          .loop-mobile-plan-summary.is-premium{
            border-color:rgba(245,158,11,.30)!important;
            background:linear-gradient(135deg,#fffdf8 0%,#fff7ed 100%)!important;
          }
          .loop-mobile-plan-summary.is-basico{
            border-color:rgba(94,234,212,.38)!important;
            background:linear-gradient(135deg,#ffffff 0%,#ecfeff 100%)!important;
          }
          .loop-mobile-plan-summary.is-active{
            outline:2px solid rgba(0,103,95,.18)!important;
            outline-offset:2px!important;
          }
          .loop-mobile-plan-summary-icon{
            width:42px!important;
            height:42px!important;
            border-radius:16px!important;
            display:grid!important;
            place-items:center!important;
            background:rgba(0,103,95,.10)!important;
            color:#00675f!important;
          }
          .loop-mobile-plan-summary-copy{
            display:grid!important;
            gap:3px!important;
            min-width:0!important;
          }
          .loop-mobile-plan-summary-copy small{
            display:block!important;
            color:#00675f!important;
            font-size:10px!important;
            line-height:1!important;
            font-weight:950!important;
            text-transform:uppercase!important;
            letter-spacing:.12em!important;
            margin:0!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }
          .loop-mobile-plan-summary-copy strong{
            display:block!important;
            color:inherit!important;
            font-size:15px!important;
            line-height:1.05!important;
            font-weight:950!important;
            letter-spacing:-.035em!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }
          .loop-mobile-plan-summary-copy em{
            display:block!important;
            color:#64748b!important;
            font-style:normal!important;
            font-size:11.5px!important;
            line-height:1.18!important;
            font-weight:750!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }
          .loop-mobile-plan-summary b{
            display:inline-flex!important;
            align-items:center!important;
            justify-content:center!important;
            min-height:28px!important;
            padding:0 10px!important;
            border-radius:999px!important;
            background:#00675f!important;
            color:#ffffff!important;
            font-size:11px!important;
            font-weight:950!important;
            white-space:nowrap!important;
          }

          .loop-mobile-nav-list{
            display:grid!important;
            grid-template-columns:1fr!important;
            gap:16px!important;
            width:100%!important;
            min-width:0!important;
            padding:16px 0 calc(18px + var(--loop-device-bottom-safe, env(safe-area-inset-bottom,0px)))!important;
            margin:0!important;
          }
          .loop-mobile-nav-group{
            display:grid!important;
            grid-template-columns:1fr!important;
            gap:7px!important;
            width:100%!important;
            min-width:0!important;
          }
          .loop-mobile-nav-group-label{
            display:block!important;
            color:#98a2b3!important;
            font-size:10.5px!important;
            line-height:1!important;
            font-weight:950!important;
            letter-spacing:.13em!important;
            text-transform:uppercase!important;
            padding:0 4px!important;
            margin:0!important;
          }
          .loop-mobile-nav-item{
            width:100%!important;
            min-height:48px!important;
            border-radius:16px!important;
            border:1px solid rgba(226,232,240,.88)!important;
            background:#ffffff!important;
            color:#344054!important;
            display:grid!important;
            grid-template-columns:34px minmax(0,1fr) auto!important;
            align-items:center!important;
            gap:10px!important;
            padding:7px 10px!important;
            text-align:left!important;
            font-size:14px!important;
            font-weight:900!important;
            cursor:pointer!important;
            box-shadow:0 8px 18px rgba(15,23,42,.035)!important;
            margin:0!important;
          }
          .loop-mobile-nav-item.is-active{
            border-color:rgba(0,103,95,.22)!important;
            background:rgba(0,103,95,.075)!important;
            color:#004f47!important;
          }
          .loop-mobile-nav-item-icon{
            width:34px!important;
            height:34px!important;
            min-width:34px!important;
            border-radius:13px!important;
            display:grid!important;
            place-items:center!important;
            background:#f8fafc!important;
            color:#667085!important;
          }
          .loop-mobile-nav-item-icon svg{
            width:17px!important;
            height:17px!important;
          }
          .loop-mobile-nav-item.is-active .loop-mobile-nav-item-icon{
            background:#ffffff!important;
            color:#00675f!important;
            box-shadow:0 8px 18px rgba(0,103,95,.10)!important;
          }
          .loop-mobile-nav-item > span:nth-child(2){
            display:block!important;
            min-width:0!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }
          .loop-mobile-nav-item em{
            display:inline-flex!important;
            align-items:center!important;
            justify-content:center!important;
            font-style:normal!important;
            border-radius:999px!important;
            background:#ffffff!important;
            color:#00675f!important;
            padding:5px 8px!important;
            font-size:10.5px!important;
            font-weight:950!important;
            white-space:nowrap!important;
          }
        }

        @media (min-width: 761px) and (max-width: 1100px){
          .loop-mobile-nav-sheet{
            left:50%!important;
            right:auto!important;
            width:min(620px, calc(100vw - 28px))!important;
            transform:translateX(-50%)!important;
            border-radius:28px!important;
            border-bottom:1px solid rgba(226,232,240,.94)!important;
            bottom:calc(12px + var(--loop-device-bottom-safe, env(safe-area-inset-bottom,0px)))!important;
            padding:12px 16px 16px!important;
          }
        }
      `}</style>

      <style>{`
        /* =========================================================
           LOOP BUSINESS EDIT V59 — native/tablet section picker hard fix
           - Dialog is portaled to document.body so it cannot be clipped by
             BusinessEditPage wrappers, transforms, scroll containers, or tablet
             width math.
           - 0–1100px and native touch tablets use the same full-width mobile
             bottom sheet contract.
           ========================================================= */
        body.loop-business-edit-mobile-nav-open{
          overflow:hidden!important;
          touch-action:none!important;
        }

        .loop-business-edit-mobile-nav-portal{
          position:fixed!important;
          inset:0!important;
          width:100vw!important;
          height:100dvh!important;
          z-index:var(--loop-z-modal-backdrop,4000)!important;
          display:block!important;
          isolation:isolate!important;
          pointer-events:auto!important;
          box-sizing:border-box!important;
        }

        .loop-business-edit-mobile-nav-portal .loop-mobile-nav-backdrop{
          position:absolute!important;
          inset:0!important;
          width:100vw!important;
          height:100dvh!important;
          border:0!important;
          padding:0!important;
          margin:0!important;
          background:rgba(15,23,42,.42)!important;
          backdrop-filter:blur(6px)!important;
          -webkit-backdrop-filter:blur(6px)!important;
          z-index:0!important;
        }

        .loop-business-edit-mobile-nav-portal .loop-mobile-nav-sheet{
          position:absolute!important;
          left:0!important;
          right:0!important;
          bottom:0!important;
          top:auto!important;
          transform:none!important;
          width:100vw!important;
          max-width:100vw!important;
          min-width:0!important;
          max-height:calc(100dvh - max(18px, env(safe-area-inset-top, 0px)))!important;
          border-radius:30px 30px 0 0!important;
          border:1px solid rgba(226,232,240,.94)!important;
          border-bottom:0!important;
          background:#fffdf8!important;
          box-shadow:0 -28px 86px rgba(15,23,42,.28)!important;
          padding:12px clamp(14px, 3.2vw, 28px) calc(22px + max(env(safe-area-inset-bottom,0px), 14px))!important;
          overflow-y:auto!important;
          overflow-x:hidden!important;
          -webkit-overflow-scrolling:touch!important;
          display:block!important;
          box-sizing:border-box!important;
          color:#0f172a!important;
        }

        .loop-business-edit-mobile-nav-portal .loop-mobile-nav-list{
          display:grid!important;
          grid-template-columns:1fr!important;
          gap:16px!important;
          width:100%!important;
          min-width:0!important;
          max-width:760px!important;
          margin:0 auto!important;
          padding:16px 0 calc(18px + max(env(safe-area-inset-bottom,0px), 14px))!important;
          box-sizing:border-box!important;
        }

        .loop-business-edit-mobile-nav-portal .loop-mobile-nav-head,
        .loop-business-edit-mobile-nav-portal .loop-mobile-plan-summary{
          width:100%!important;
          max-width:760px!important;
          margin-left:auto!important;
          margin-right:auto!important;
          box-sizing:border-box!important;
        }

        .loop-business-edit-mobile-nav-portal .loop-mobile-nav-group{
          display:grid!important;
          grid-template-columns:1fr!important;
          width:100%!important;
          min-width:0!important;
          gap:7px!important;
        }

        .loop-business-edit-mobile-nav-portal .loop-mobile-nav-item{
          width:100%!important;
          min-width:0!important;
          min-height:52px!important;
          display:grid!important;
          grid-template-columns:38px minmax(0,1fr) auto!important;
          align-items:center!important;
          gap:11px!important;
          padding:8px 12px!important;
          border-radius:18px!important;
          box-sizing:border-box!important;
        }

        .loop-business-edit-mobile-nav-portal .loop-mobile-nav-item > span:nth-child(2){
          display:block!important;
          min-width:0!important;
          overflow:visible!important;
          text-overflow:clip!important;
          white-space:normal!important;
          line-height:1.12!important;
        }

        .loop-business-edit-mobile-nav-portal .loop-mobile-nav-item em{
          flex:0 0 auto!important;
          white-space:nowrap!important;
        }

        @media (pointer: coarse) and (min-width: 761px) and (max-width: 1368px){
          .loop-business-edit-mobile-nav-portal .loop-mobile-nav-sheet{
            left:0!important;
            right:0!important;
            bottom:0!important;
            width:100vw!important;
            max-width:100vw!important;
            transform:none!important;
            max-height:min(86dvh, 820px)!important;
            border-radius:32px 32px 0 0!important;
            padding-left:clamp(20px, 5vw, 54px)!important;
            padding-right:clamp(20px, 5vw, 54px)!important;
          }
        }

        @media (max-width: 430px){
          .loop-business-edit-mobile-nav-portal .loop-mobile-nav-sheet{
            border-radius:28px 28px 0 0!important;
            padding-left:14px!important;
            padding-right:14px!important;
          }
          .loop-business-edit-mobile-nav-portal .loop-mobile-nav-list{
            max-width:none!important;
          }
        }
      `}</style>


      <style>{`
        /* =========================================================
           LOOP BUSINESS SETTINGS SIDEBAR RESPONSIVE CONTRACT V1
           Final deterministic transition band:
           - <=1100px: preserve the existing mobile/tablet contract.
           - 1101px–1180px: no hybrid sidebar; use one compact selector.
           - >=1181px: full desktop sidebar.
           This block intentionally comes AFTER all historical sidebar CSS.
           ========================================================= */

        @media (min-width:1101px) and (max-width:1180px){
          .business-settings-shell.loop-settings-minimal:not(.is-native),
          .loop-settings-minimal.business-settings-shell:not(.is-native),
          .loop-settings-minimal:not(.is-native){
            --loop-sidebar-w-final:0px!important;
            --loop-sidebar-gap-final:0px!important;
            padding-left:clamp(14px,2vw,24px)!important;
            padding-right:clamp(14px,2vw,24px)!important;
            overflow-x:hidden!important;
          }

          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-settings-layout,
          .loop-settings-minimal.business-settings-shell:not(.is-native) .loop-settings-layout,
          .loop-settings-minimal:not(.is-native) .loop-settings-layout{
            display:grid!important;
            grid-template-columns:minmax(0,1fr)!important;
            grid-template-rows:auto auto!important;
            gap:10px!important;
            column-gap:0!important;
            width:100%!important;
            max-width:100%!important;
            margin:0!important;
            padding:0!important;
            align-items:start!important;
            justify-content:stretch!important;
          }

          /* Kill the desktop rail completely inside the transition band.
             This prevents the old 1101px desktop rules and 1120/1180 tablet
             rules from producing a half-sidebar. */
          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-settings-nav,
          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-settings-nav-v2,
          .loop-settings-minimal.business-settings-shell:not(.is-native) .loop-settings-nav,
          .loop-settings-minimal.business-settings-shell:not(.is-native) .loop-settings-nav-v2,
          .loop-settings-minimal:not(.is-native) .loop-settings-nav,
          .loop-settings-minimal:not(.is-native) .loop-settings-nav-v2{
            display:none!important;
            visibility:hidden!important;
            position:static!important;
            inset:auto!important;
            width:0!important;
            min-width:0!important;
            max-width:0!important;
            height:0!important;
            min-height:0!important;
            max-height:0!important;
            margin:0!important;
            padding:0!important;
            overflow:hidden!important;
            pointer-events:none!important;
            transform:none!important;
          }

          /* One navigation pattern only: compact selector in normal document
             flow, sticky under the global header. */
          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-mobile-section-select-wrap,
          .loop-settings-minimal.business-settings-shell:not(.is-native) .loop-mobile-section-select-wrap,
          .loop-settings-minimal:not(.is-native) .loop-mobile-section-select-wrap{
            display:block!important;
            grid-column:1!important;
            grid-row:1!important;
            position:sticky!important;
            top:calc(var(--loop-global-header-h,66px) + 8px)!important;
            z-index:1300!important;
            width:100%!important;
            max-width:100%!important;
            min-width:0!important;
            margin:0 0 10px!important;
            padding:0!important;
          }

          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-mobile-section-trigger,
          .loop-settings-minimal.business-settings-shell:not(.is-native) .loop-mobile-section-trigger,
          .loop-settings-minimal:not(.is-native) .loop-mobile-section-trigger{
            width:100%!important;
            min-height:54px!important;
            display:grid!important;
            grid-template-columns:38px minmax(0,1fr) 28px!important;
            align-items:center!important;
            gap:10px!important;
            padding:8px 11px!important;
            border:1px solid rgba(226,232,240,.94)!important;
            border-radius:17px!important;
            background:rgba(255,255,255,.97)!important;
            box-shadow:0 10px 24px rgba(15,23,42,.075)!important;
            color:#0f172a!important;
            text-align:left!important;
            cursor:pointer!important;
            backdrop-filter:blur(16px)!important;
            -webkit-backdrop-filter:blur(16px)!important;
          }

          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-mobile-trigger-icon,
          .loop-settings-minimal:not(.is-native) .loop-mobile-trigger-icon{
            width:38px!important;
            height:38px!important;
            min-width:38px!important;
            display:grid!important;
            place-items:center!important;
            border-radius:13px!important;
            background:rgba(0,103,95,.085)!important;
            color:#00675f!important;
          }

          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-mobile-trigger-copy,
          .loop-settings-minimal:not(.is-native) .loop-mobile-trigger-copy{
            min-width:0!important;
          }

          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-mobile-trigger-copy strong,
          .loop-settings-minimal:not(.is-native) .loop-mobile-trigger-copy strong{
            display:block!important;
            min-width:0!important;
            color:#0f172a!important;
            font-size:14px!important;
            line-height:1.08!important;
            font-weight:950!important;
            letter-spacing:-.025em!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }

          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-mobile-trigger-copy small,
          .loop-settings-minimal:not(.is-native) .loop-mobile-trigger-copy small{
            display:block!important;
            min-width:0!important;
            margin-top:3px!important;
            color:#64748b!important;
            font-size:10.5px!important;
            line-height:1.1!important;
            font-weight:780!important;
            white-space:nowrap!important;
            overflow:hidden!important;
            text-overflow:ellipsis!important;
          }

          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-mobile-trigger-chevron,
          .loop-settings-minimal:not(.is-native) .loop-mobile-trigger-chevron{
            display:grid!important;
            place-items:center!important;
            width:28px!important;
            height:28px!important;
            color:#64748b!important;
            font-size:16px!important;
          }

          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-settings-main,
          .loop-settings-minimal.business-settings-shell:not(.is-native) .loop-settings-main,
          .loop-settings-minimal:not(.is-native) .loop-settings-main{
            grid-column:1!important;
            grid-row:2!important;
            width:100%!important;
            min-width:0!important;
            max-width:none!important;
            justify-self:stretch!important;
            margin:0!important;
            padding:0!important;
          }

          /* The narrow-desktop selector owns sticky navigation. Keep the
             page heading/save row in normal flow so the two sticky surfaces
             never collide. */
          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-settings-topbar-v2,
          .loop-settings-minimal:not(.is-native) .loop-settings-topbar-v2{
            position:relative!important;
            top:auto!important;
            z-index:1!important;
            margin-top:0!important;
            margin-bottom:6px!important;
            padding-top:0!important;
            padding-bottom:8px!important;
            background:transparent!important;
            backdrop-filter:none!important;
            -webkit-backdrop-filter:none!important;
          }

          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-settings-content,
          .loop-settings-minimal:not(.is-native) .loop-settings-content{
            margin-top:0!important;
            padding-top:0!important;
          }

          /* On mouse/desktop-width narrow screens the same selector opens a
             centered tablet sheet instead of a raw full-width phone sheet. */
          .loop-business-edit-mobile-nav-portal .loop-mobile-nav-sheet{
            left:50%!important;
            right:auto!important;
            bottom:14px!important;
            width:min(620px,calc(100vw - 28px))!important;
            max-width:min(620px,calc(100vw - 28px))!important;
            transform:translateX(-50%)!important;
            max-height:min(82dvh,760px)!important;
            border-radius:28px!important;
            border-bottom:1px solid rgba(226,232,240,.94)!important;
            padding:12px 16px 18px!important;
          }
        }

        @media (min-width:1181px){
          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-mobile-section-select-wrap,
          .loop-settings-minimal.business-settings-shell:not(.is-native) .loop-mobile-section-select-wrap,
          .loop-settings-minimal:not(.is-native) .loop-mobile-section-select-wrap{
            display:none!important;
          }

          .business-settings-shell.loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal.business-settings-shell:not(.is-native) .loop-settings-nav.loop-settings-nav-v2,
          .loop-settings-minimal:not(.is-native) .loop-settings-nav.loop-settings-nav-v2{
            display:grid!important;
            visibility:visible!important;
            height:auto!important;
            min-height:0!important;
            pointer-events:auto!important;
          }
        }
      `}</style>

      <div className="loop-settings-layout">
        <div className="loop-mobile-section-select-wrap">
          <button
            type="button"
            className="loop-mobile-section-trigger"
            onClick={() => setMobileNavOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={mobileNavOpen ? "true" : "false"}
          >
            <span className="loop-mobile-trigger-icon" aria-hidden="true"><SettingsIcon name={activeNavItem?.icon || "gear"} size={17} /></span>
            <span className="loop-mobile-trigger-copy">
              <strong>{getActiveSectionTitle()}</strong>
              <small>{currentBusinessName} · {t(loopPlanCard.title)}</small>
            </span>
            <span className="loop-mobile-trigger-chevron" aria-hidden="true">⌄</span>
          </button>
        </div>

        <aside className="loop-settings-nav loop-settings-nav-v2" aria-label={t("Configuración del negocio")}>
          <div className="loop-nav-actions" aria-label={t("Resumen del negocio")}>
            <button type="button" className="loop-nav-business-card" onClick={() => selectSettingsTab("general")}>
              <div className="loop-nav-business-row">
                <div className="loop-nav-business-logo">
                  {publicLogo ? <img src={publicLogo} alt={currentBusinessName} /> : <span>{String(currentBusinessName || "L").slice(0, 2).toUpperCase()}</span>}
                </div>
                <div className="loop-nav-business-copy">
                  <strong>{currentBusinessName}</strong>
                  <span>{publicType || t("Negocio")}</span>
                </div>
                <span className="loop-nav-business-arrow" aria-hidden="true">›</span>
              </div>
              <div className="loop-nav-readiness">
                <div className="loop-nav-readiness-row">
                  <span>{readinessPct}{t("% perfil completo")}</span>
                </div>
                <div className="loop-nav-readiness-track"><i style={{ width: `${readinessPct}%` }} /></div>
              </div>
            </button>
          </div>

          <div className="loop-settings-privacy-card" aria-label={t("Seguridad del negocio")}>
            <span className="loop-settings-privacy-icon"><SettingsIcon name="shield" size={22} /></span>
            <div>
              <strong>{t("Seguro y privado")}</strong>
              <p>{t("Solo tú y tu equipo pueden ver esta información.")}</p>
            </div>
          </div>

          <div className="loop-settings-grouped-tabs" aria-label={t("Secciones de configuración")}>
            {settingsGroups.map((group) => (
              <div className="loop-settings-group" key={group.label}>
                <span className="loop-settings-group-label">{t(group.label)}</span>
                <div className="loop-settings-tabs">
                  {group.items.filter((item) => !item.mobileOnly).map((item) => {
                    const active = activeTab === item.id && item.primary === true;
                    return (
                      <button
                        key={`${t(group.label)}-${item.navKey}`}
                        type="button"
                        className={`loop-settings-tab ${active ? "is-active" : ""}`}
                        onClick={() => selectSettingsTab(item.id)}
                      >
                        <span className={`loop-settings-tab-icon icon-${item.icon}`} aria-hidden="true"><SettingsIcon name={item.icon} size={16} /></span>
                        <span className="loop-settings-tab-copy">
                          <strong>{t(item.label)}</strong>
                        </span>
                        <span className="loop-settings-tab-arrow" aria-hidden="true">›</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <button type="button" className="loop-plan-compact" onClick={() => selectSettingsTab("billing")}><span>{t(loopPlanCard.title)}</span><span>{t("Ver plan →")}</span></button>
        </aside>

        {mobileNavOpen && typeof document !== "undefined" ? createPortal(
          <div className="loop-mobile-nav-layer loop-business-edit-mobile-nav-portal" role="dialog" aria-modal="true" aria-label={t("Secciones de configuración")}>
            <button type="button" className="loop-mobile-nav-backdrop" onClick={() => setMobileNavOpen(false)} aria-label={t("Cerrar navegación")} />
            <div className="loop-mobile-nav-sheet">
              <div className="loop-mobile-nav-handle" aria-hidden="true" />
              <div className="loop-mobile-nav-head">
                <div>
                  <span>{t("Configurar negocio")}</span>
                  <strong>{currentBusinessName}</strong>
                </div>
                <button type="button" onClick={() => setMobileNavOpen(false)} aria-label={t("Cerrar")}>×</button>
              </div>
              <button
                type="button"
                className={`loop-mobile-plan-summary is-${loopPlanCard.key} ${activeTab === "billing" ? "is-active" : ""}`}
                onClick={() => selectSettingsTab("billing")}
                aria-label={`${t(loopPlanCard.title)}. ${t(loopPlanCard.subtitle)}. ${t(loopPlanCard.cta)}`}
              >
                <span className="loop-mobile-plan-summary-icon" aria-hidden="true"><SettingsIcon name={loopPlanCard.icon || "diamond"} size={18} /></span>
                <span className="loop-mobile-plan-summary-copy">
                  <small>{t("Plan actual")}</small>
                  <strong>{t(loopPlanCard.title)}</strong>
                  <em>{t(loopPlanCard.subtitle)}</em>
                </span>
                <b>{activeTab === "billing" ? t("Actual") : t("Ver")}</b>
              </button>
              <div className="loop-mobile-nav-list">
                {settingsGroups.map((group) => (
                  <div className="loop-mobile-nav-group" key={`mobile-${t(group.label)}`}>
                    <span className="loop-mobile-nav-group-label">{t(group.label)}</span>
                    {group.items.map((item) => {
                      const active = activeTab === item.id && item.primary === true;
                      return (
                        <button
                          key={`mobile-${t(group.label)}-${item.navKey}`}
                          type="button"
                          className={`loop-mobile-nav-item ${active ? "is-active" : ""}`}
                          onClick={() => selectSettingsTab(item.id)}
                        >
                          <span className="loop-mobile-nav-item-icon" aria-hidden="true"><SettingsIcon name={item.icon} size={17} /></span>
                          <span>{t(item.label)}</span>
                          {active ? <em>{t("Actual")}</em> : null}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body
        ) : null}

        <main className="loop-settings-main">
          <div className="loop-settings-topbar loop-settings-topbar-v2">
            <div className="loop-settings-page-heading">
              <h1>{getActiveSectionTitle()}</h1>
              <p>{getActiveSectionSubtitle()}</p>
            </div>
            <div className="loop-settings-save-area">
              <span className="loop-settings-topbar-note">{hasSectionSave ? "" : activeTab === "gallery" || activeTab === "businessos" || activeTab === "onlinepayments" ? "Cada acción se guarda al completarse." : "Los cambios del perfil se aplican al guardar."}</span>
              {hasSectionSave && <button type="button" className="loop-save-btn" onClick={handleContextualSave} disabled={saving || sectionSaving}>{saving || sectionSaving ? t("Guardando...") : getContextualSaveLabel()}</button>}
            </div>
          </div>
          {editorNavigation.dialog}
          <section className="loop-settings-content">
            {businessDoc?.onboardingVersion === 'business_draft_v3' && <BusinessReviewChecklist businessId={businessId} business={businessDoc} onNavigate={selectSettingsTab}/>}
            {false && activeTab === "general" && (
              <div style={{padding: 20, marginBottom: 16}}>
                <label htmlFor="booking-refund-policy">{t("Devolución del anticipo al cancelar")}</label>
                <select id="booking-refund-policy" ref={refundPolicyRef} key={`${businessId}-${JSON.stringify(businessDoc?.bookingRefundPolicy)}`}
                  defaultValue={businessDoc?.bookingRefundPolicy?.version === 1 ? (businessDoc.bookingRefundPolicy.enabled ? String(businessDoc.bookingRefundPolicy.noticeHours) : "disabled") : ""}>
                  <option value="">{t("Sin configurar")}</option>
                  <option value="24">{t("Hasta 24 horas antes (sugerido para belleza)")}</option>
                  <option value="48">{t("Hasta 48 horas antes")}</option>
                  <option value="72">{t("Hasta 3 días antes")}</option>
                  <option value="disabled">{t("Sin devolución por cancelación del cliente")}</option>
                </select>
                <p>{t("Aplica a nuevas reservas. Las anteriores conservan sus condiciones. La devolución automática aún no está habilitada; los casos que cumplan el plazo requieren revisión.")}</p>
              </div>
            )}
            {activeTab === "general" && (
              <GeneralSection onOpenGallery={() => setActiveTab("gallery")} onSaveGeneral={handleSaveBusiness}
                businessId={businessId}
                businessDoc={businessDoc}
                businessTypes={businessTypes}
                nameRef={nameRef}
                addressRef={addressRef}
                mapsRef={mapsRef}
                phoneRef={phoneRef}
                whatsappRef={whatsappRef}
                instagramRef={instagramRef}
                websiteRef={websiteRef}
                hoursRef={hoursRef}
                eventCategoryRef={eventCategoryRef}
                serviceModeRef={serviceModeRef}
                serviceAreasRef={serviceAreasRef}
                quienesRef={quienesRef}
                onDiscardLogo={() => {setLogoFile(null);setLogoPreview(businessDoc?.logoUrl || null);}}
                logoPreview={logoPreview}
                templatePreview={templatePreview}
                openCropper={openCropper}
                uploadPct={uploadPct}
                autosaveStatus={"idle"}
                visible={profileVisible}
                onVisibilityChange={handleVisibilityChange}

              />
            )}



            {activeTab === "gallery" && (
              <GallerySection
                businessId={businessId}
                businessDoc={businessDoc || {}}
                projects={projects}
                uploading={projectUploading}
                uploadPct={uploadPct}
                onUploadProjects={handleUploadProjects}
                onRemoveProject={handleRemoveGalleryProject}
                onVisibilityChange={handleGalleryVisibilityChange}
              />
            )}

            {(activeTab === "services" || paymentReturnTab === "services") && (
              <div hidden={activeTab !== "services"}><ServicesSection businessDoc={businessDoc}
                active={activeTab === "services"}
                onConfigurePayments={() => {setPaymentReturnTab("services");setActiveTab("onlinepayments");}}
                businessId={businessId}
                services={services}
                onUploadServiceImage={handleUploadServiceImage}
                onDeleteService={handleDeleteService}
                onAddService={(name, duration, price, extras) => handleAddService(name, duration, price, extras)}
                onServicesChange={(next) => setServices(next)}
                disableAdd={!!singleService}
                singleService={singleService}
                singleServiceDuration={singleServiceDuration}
                onToggleSingleService={(v) => handleToggleSingleService(v)}
                onChangeSingleServiceDuration={(mins) => setSingleServiceDuration(Number(mins || 60))}
                isEventsBusiness={isEventsBusiness}
              /></div>
            )}

            {activeTab === "availability" && (
              <AvailabilitySection businessId={businessId} businessDoc={businessDoc} db={db} externalSaveRef={availabilitySaveRef} />
            )}

            {activeTab === "notifications" && (
              <NotificationSection businessId={businessId} businessDoc={businessDoc} db={db} externalSaveRef={notificationSaveRef} hideLocalActions />
            )}



            {activeTab === "loyalty" && (
              <LoyaltySection
                businessId={businessId}
                businessDoc={businessDoc}
                services={services}
                db={db}
              />
            )}



            {(activeTab === "memberships" || paymentReturnTab === "memberships") && (
              <div hidden={activeTab !== "memberships"}><MembershipSection
                active={activeTab === "memberships"}
                onConfigurePayments={() => {setPaymentReturnTab("memberships");setActiveTab("onlinepayments");}}
                businessId={businessId}
                businessDoc={businessDoc}
                services={services}
                db={db}
              /></div>
            )}

            {activeTab === "onlinepayments" && <>{paymentReturnTab && <button type="button" className="loop-save-btn" onClick={()=>{setActiveTab(paymentReturnTab);setPaymentReturnTab(null);}}>{t("← Volver") + " "}{paymentReturnTab === "services" ? "al servicio" : "a la membresía"}</button>}<OnlinePaymentsSection key={businessId} businessId={businessId} businessDoc={businessDoc} /></>}
            {activeTab === "businessos" && (
              <>{paymentReturnTab && <button type="button" className="loop-save-btn" onClick={()=>setActiveTab(paymentReturnTab)}>{t("← Volver") + " "}{paymentReturnTab === "services" ? "al servicio" : "a la membresía"}</button>}<BusinessOsSection
                openPayments={Boolean(paymentReturnTab)}
                isLoopAdmin={loopBusinessIsPlatformOwner(auth, userProp)}
                canManageBusinessOs={loopBusinessIsPlatformOwner(auth, userProp) || Boolean(loopBusinessUserId(auth, userProp) && businessDoc?.ownerUid === loopBusinessUserId(auth, userProp))}
                businessId={businessId}
                businessDoc={businessDoc}
                db={db}
                onBusinessPatch={(patch) => setBusinessDoc((prev) => ({ ...(prev || {}), ...(patch || {}) }))}
              /></>
            )}

            {activeTab === "billing" && (
              <BillingSection
                businessId={businessId}
                businessDoc={businessDoc}
                auth={auth}
                externalSaveRef={billingSaveRef}
                hideLocalActions
                onBusinessPatch={(patch) => setBusinessDoc((prev) => ({ ...(prev || {}), ...(patch || {}) }))}
              />
            )}

            {activeTab === "workers" && (
              <WorkersSection
                businessId={businessId}
                businessDoc={businessDoc}
                showPageHeader={false}
                employees={employees}
                services={services}
                onDeleteEmployee={handleDeleteEmployee}
                openCropper={openCropper}
                onUploadEmployeeProjects={handleUploadEmployeeProjects}
                onSaveEmployee={handleSaveEmployee}
                onAccessSaved={(worker) => setEmployees(previous => previous.map(item => item.id === worker.id ? {...item, ...worker} : item))}
                onAddEmployee={(payload) => handleAddEmployee(payload)}
                disableAdd={singleWorker}
                onToggleNoWorkers={(v) => handleToggleNoWorkers(v)}
                onToggleSingleWorker={(v) => handleToggleSingleWorker(v)}
              />
            )}

            {activeTab === "projects" && (
              <div className="loop-crm-only-shell">
                <CustomersPage businessId={businessId} embedded />
              </div>
            )}
          </section>

          <div className="loop-settings-updated">{t("Última actualización:")}{businessDoc?.updatedAt ? businessDoc.updatedAt.seconds ? new Date(businessDoc.updatedAt.seconds * 1000).toLocaleString() : String(businessDoc.updatedAt) : "—"}
          </div>
        </main>

        <aside className="loop-action-rail" aria-label={t("Acciones rápidas del negocio")}>
          <div className="loop-action-card loop-action-profile">
            <div className="loop-action-profile-top">
              <div className="loop-action-logo">
                {publicLogo ? <img src={publicLogo} alt={currentBusinessName} /> : <span>{String(currentBusinessName || "L").slice(0, 2).toUpperCase()}</span>}
              </div>
              <div className="loop-action-business">
                <strong>{currentBusinessName}</strong>
                <span>{servicesCount}{" " + t("servicios ·") + " "}{employeesCount}{" " + t("personas")}</span>
              </div>
            </div>

            <span className={`loop-status-pill ${profileVisible ? "" : "paused"}`}>{profileVisible ? "Perfil público" : "Perfil pausado"}</span>

            <div className="loop-action-progress">
              <div className="loop-action-progress-row">
                <strong>{readinessPct}{t("% completo")}</strong>
                <span>{completedReadiness}/{readinessItems.length}</span>
              </div>
              <div className="loop-action-track" aria-hidden="true"><i style={{ width: `${readinessPct}%` }} /></div>
              <div className="loop-action-missing">
                {missingReadiness.length ? (
                  <small>{t("Falta:") + " "}{missingReadiness.map((item) => item.label).join(", ")}</small>
                ) : (
                  <small>{t("Tu perfil básico está completo.")}</small>
                )}
              </div>
            </div>

            <div className="loop-action-buttons">
              <button type="button" className="loop-preview-btn" onClick={openPublicPreview}>{t("Vista previa")}</button>
              {hasSectionSave && <button type="button" className="loop-save-btn" onClick={handleContextualSave} disabled={saving || sectionSaving}>{saving || sectionSaving ? t("Guardando...") : getContextualSaveLabel()}</button>}
            </div>
          </div>
        </aside>
      </div>

      {!mobileNavOpen && hasSectionSave ? (
        <div className="loop-mobile-save-dock" aria-label={t("Guardar cambios de la sección")}>
          <button
            type="button"
            className="loop-save-btn loop-mobile-save-btn"
            onClick={handleContextualSave}
            disabled={saving || sectionSaving}
          >
            {saving || sectionSaving ? t("Guardando...") : getContextualSaveLabel()}
          </button>
        </div>
      ) : null}

      {saveToast ? (
        <div className={`loop-save-toast ${saveToast.tone === "error" ? "error" : "success"}`} role="status" aria-live="polite">
          <div className="loop-save-toast-icon">{saveToast.tone === "error" ? "!" : "✓"}</div>
          <div>
            <strong>{saveToast.tone === "error" ? "No se pudo guardar" : "Cambios guardados"}</strong>
            <span>{saveToast.message}</span>
          </div>
          <button type="button" aria-label={t("Cerrar")} onClick={() => setSaveToast(null)}>×</button>
        </div>
      ) : null}

      {cropperFile && cropperMode ? (
        typeof document !== "undefined" && document.body
          ? createPortal(
              <div
                className="loop-cropper-backdrop"
                data-loop-cropper="true"
                style={{ zIndex: cropperZIndex }}
                onClick={() => handleCropperCancel()}
              >
                <div
                  className="loop-cropper-panel"
                  style={{ position: "relative", zIndex: 1 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3>{t("Ajustar imagen")}</h3>
                  <ImageCropper
                    file={cropperFile}
                    aspect={cropperAspect}
                    outputWidth={cropperOutputWidth}
                    backgroundColor={cropperBackgroundColor}
                    fitMode={cropperFitMode}
                    onCancel={handleCropperCancel}
                    onComplete={handleCropperComplete}
                  />
                </div>
              </div>,
              document.body
            )
          : (
              <div
                className="loop-cropper-backdrop"
                data-loop-cropper="true"
                style={{ zIndex: cropperZIndex }}
                onClick={() => handleCropperCancel()}
              >
                <div
                  className="loop-cropper-panel"
                  style={{ position: "relative", zIndex: 1 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3>{t("Ajustar imagen")}</h3>
                  <ImageCropper
                    file={cropperFile}
                    aspect={cropperAspect}
                    outputWidth={cropperOutputWidth}
                    backgroundColor={cropperBackgroundColor}
                    fitMode={cropperFitMode}
                    onCancel={handleCropperCancel}
                    onComplete={handleCropperComplete}
                  />
                </div>
              </div>
            )
      ) : null}
    </div>
  );
}
